// STORY-813 PR 5c — compat helper retired; only new vocabulary
// in the DB post-0156. See `isCancellable` check below.

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  locale: string;
  country: string | null;
  tenantId: string | null;
  regionCode: string | null;
}

export interface MeProfile {
  id: string;
  email: string;
  username: string;
  locale: string;
  country: string | null;
  kyc_status: string;
  kyc_tier: string;
  role: string;
  status: string;
  // STORY-348 — per-user "strict market orders" preference. When
  // true, market orders that don't fully fill cancel the remainder
  // instead of auto-converting to a resting limit at the outcome
  // anchor. Default false.
  strict_market_orders: boolean;
  // STORY-817 — power-user "show advanced order types" toggle. When
  // true the trade panel surfaces FOK/GTD/GTC/POSTONLY execution-flag
  // selectors. Default false (smart-routing FAK is the regular UX).
  advanced_order_types_enabled: boolean;
  // EPIC-041 part 3a — when true, fills auto-emit a feed post
  // (kind=auto_trade). Defaults to true; PIs are service-layer-locked
  // to true and can't flip it off.
  auto_share_to_feed: boolean;
  // STORY-107 — TOTP 2FA state. The settings page reads these to
  // decide whether to render the enable or disable flow.
  two_fa_enabled: boolean;
  two_fa_enabled_at: string | null;
  // STORY-1121 phase 2 — cross-device theme. NULL = follow system.
  theme_pref?: "system" | "light" | "dark" | null;
  // EPIC-021 STORY-375 — public profile fields.
  display_name?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  // EPIC-029 STORY-1701 — per-user display-currency preference.
  // NULL = derive from regions.default_currency at render time;
  // explicit value overrides region default. Allowed values mirror
  // wallets.currency (USD/USDC/BRL/EUR/GBP/MXN/ARS/COP).
  display_currency?: string | null;
  // EPIC-029 BR-primary display flip (2026-05-08) — which currency
  // leads on every screen. Default per region (BRL for region=BR,
  // USDC otherwise) was set by migration 0143; user-overridable via
  // /settings/display.
  display_currency_primary?: "USDC" | "BRL";
  // EPIC-030 STORY-1805 phase 2 — toggle the inline USD→local
  // conversion annotation on market detail pages. Default true.
  show_inline_conversion?: boolean;
  // EPIC-013 STORY-1307 follow-up — when this user last self-requested
  // a virtual money reset. NULL = never. The 30-day cooldown is
  // enforced server-side; surfacing it here lets the client suppress
  // the reset CTA pre-click instead of relying on the 403.
  last_virtual_reset_at?: string | null;
  created_at: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export interface SignupInput {
  email: string;
  username: string;
  password: string;
  country?: string;
  locale?: string;
  tenantSlug?: string;
  // STORY-1305: required when env INVITE_REQUIRED=true (closed beta).
  // Optional otherwise (still recorded for audit if supplied).
  inviteCode?: string;
}

export interface LoginInput {
  emailOrUsername: string;
  password: string;
}

// STORY-107 — when 2FA is enabled, /v1/auth/login returns this
// shape instead of AuthTokens. Caller posts the challengeToken +
// the user's TOTP code to /v1/auth/login/2fa.
export interface LoginRequires2fa {
  requires2fa: true;
  challengeToken: string;
}

export interface I18nText {
  en: string;
  pt?: string;
  es?: string;
  /**
   * EPIC-030 STORY-1801 — additional locales surfaced by the admin
   * translation editor. Field-set is intentionally open via the
   * index signature; admins add locales as the supported-locale
   * config grows.
   */
  de?: string;
  "pt-BR"?: string;
  "de-DE"?: string;
  "en-CA"?: string;
  "fr-CA"?: string;
  [locale: string]: string | undefined;
}

export interface MarketOutcome {
  id: string;
  sortOrder: number;
  name: I18nText;
  currentPriceCents: number;
  volumeCents: string;
  openInterestShares: string;
  // STORY E (multi-outcome row UI) — per-outcome metadata fields. All
  // four are ADDITIVE and may be null/empty for rows that haven't been
  // populated yet (snapshots / last-trade hooks land in a separate
  // story). Renderers MUST treat null as "no data" — show "—" rather
  // than collapsing to 0.
  metadata: Record<string, unknown>;
  lastTradeAt: string | null;
  lastTradePriceCents: number | null;
  // Latest snapshot price minus oldest snapshot price within the
  // trailing 24h, in cents (per-share). Positive = price up, negative
  // = price down, null = no snapshot in the window. Computed on read
  // by the API.
  priceDelta24hCents: number | null;
  // Migration 0166 — admin-controlled icon override. NULL = fall back
  // to the auto-detect chain in apps/web/lib/outcome-icons.ts (the
  // country-flag map / concept glyph / colored bubble). Non-null is
  // EITHER a public URL ("https://...", rendered as <img>) OR an
  // "emoji:<glyph>" sentinel (the OutcomeRowList strips the prefix and
  // emits the unicode text directly — lets the admin pick from a
  // curated library without uploading anything).
  iconUrl: string | null;
}

export interface MarketListItem {
  id: string;
  slug: string;
  title: I18nText;
  description: I18nText;
  imageUrl: string | null;
  kind: "binary" | "multi_outcome" | "scalar";
  status: string;
  opensAt: string;
  closesAt: string;
  resolvesAt: string;
  totalVolumeCents: string;
  tradeCount: number;
  uniqueTraders: number;
  categorySlug: string;
  yesPriceCents: number | null;
  // 24h hourly-bucketed avg YES price (cents). Null for markets
  // without a YES outcome (multi-outcome / scalar) or with no
  // trades in the window. Sorted oldest → newest. Used by
  // MarketCard's sparkline.
  priceHistoryCents: number[] | null;
  // ISO timestamp of the most recent trade on this market, or null
  // if the market has zero trades. Drives MarketCard's "LIVE"
  // badge — markets traded within the last 5 minutes show the
  // pulsing dot.
  lastTradeAt: string | null;
}

export interface MarketDetail extends MarketListItem {
  resolutionSource: string;
  resolutionSpec: Record<string, unknown>;
  resolvedAt: string | null;
  resolvedOutcomeId: string | null;
  resolutionNotes: string | null;
  totalLiquidityCents: string;
  createdAt: string;
  outcomes: MarketOutcome[];
  // EPIC-027 STORY-697 — public reward-config surface. `rewardsEnabled`
  // gates whether the orderbook draws the reward-zone band; the band
  // spans midpoint ± `maxSpreadCents`.
  rewardsEnabled: boolean;
  maxSpreadCents: number | null;
  minSizeShares: string | null;
}

export interface MarketsListResponse {
  markets: MarketListItem[];
  nextCursor: string | null;
}

// 2026-04-30 — public users search hit projection. Privacy-respecting:
// surfaces username (already public via /u/:username slug) + display
// name + avatar + verified flag; no email, no full profile gates.
// Click-through to /u/:username applies the visibility gate as
// usual.
export interface UserSearchHit {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: string;
  verifiedExpert: boolean;
}

// 2026-04-29 / 2026-04-30 — Stocktwits-style sentiment gauge.
// 2026-04-30 extended to multi-outcome: response now always carries
// `outcomes[]`. For binary markets `bullishCount` / `bearishCount`
// remain populated for back-compat with the v1 `<MarketSentimentGauge>`.
// Backend returns null for scalar markets only.
export interface MarketSentimentResponse {
  kind: "binary" | "multi_outcome";
  bullishCount: number;
  bearishCount: number;
  totalTraders: number;
  windowHours: number;
  outcomes: Array<{
    outcomeId: string;
    sortOrder: number;
    name: { en: string; pt?: string };
    buyerCount: number;
  }>;
}

export type UnifiedSentimentCategory =
  | "extremely_bullish"
  | "bullish"
  | "slightly_bullish"
  | "neutral"
  | "slightly_bearish"
  | "bearish"
  | "extremely_bearish";

export interface UnifiedSentimentResponse {
  score: number;
  category: UnifiedSentimentCategory;
  windowHours: number;
  breakdown: {
    trades: { score: number | null; weight: number; sampleSize: number };
    posts: { score: number | null; weight: number; sampleSize: number };
    news: { score: number | null; weight: number; sampleSize: number };
  };
}

// News Hub PR 3b — cluster summaries matched to a market by keyword
// overlap. `synthesis` populated when an admin published a Granbay-
// editorial summary for the cluster (PR 2 AiSummaryService → PR 3b
// publish bridge); null otherwise — caller falls back to topArticles.
export interface MarketRelatedNewsItem {
  clusterId: string;
  topic: string;
  articleCount: number;
  createdAt: string;
  synthesis: string | null;
  sentimentSignal: string | null;
  highlightedSources: Array<{
    sourceName: string;
    url: string;
    whyHighlighted: string;
  }>;
  topArticles: Array<{
    title: string;
    sourceName: string;
    sourceUrl: string;
    publishedAt: string;
  }>;
}

export interface MarketRelatedNewsResponse {
  items: MarketRelatedNewsItem[];
}

// Market Creation Hub — sports event sub-market types (migration 0157).
// The events backend ships at `GET /v1/market-events` (list) and
// `GET /v1/market-events/:slug` (detail). The page components import
// `MarketEventChildMarket` / `MarketEventDetail` / `MarketEventSummary`
// — names kept for compat with the EventHeader + EventSubMarketTabs
// component imports.

export interface MarketEventChildMarketPrice {
  outcomeId: string;
  name: I18nText;
  priceCents: number;
}

export interface MarketEventChildMarket {
  id: string;
  slug: string;
  title: I18nText;
  kind: "binary" | "multi_outcome" | "scalar";
  kindVariant: string | null;
  status: string;
  authoringStatus: string;
  currentPrices: MarketEventChildMarketPrice[];
  closesAt: string;
  totalVolumeCents: string;
  tradeCount: number;
}

export interface MarketEventSummary {
  id: string;
  slug: string;
  title: I18nText;
  description: I18nText;
  categorySlug: string;
  applicableRegions: string[];
  eventAt: string | null;
  status: "upcoming" | "live" | "completed" | "cancelled";
  imageUrl: string | null;
  metadata: Record<string, unknown>;
  childMarketCount: number;
}

export interface MarketEventDetail extends MarketEventSummary {
  childMarkets: MarketEventChildMarket[];
}

// News Hub admin types (PR 1-3b epic).

export interface NewsHubClusterRow {
  id: string;
  topic: string;
  regionCode: string | null;
  articleCount: number;
  status: "pending" | "reviewed" | "skipped" | "archived" | "drafted";
  // PR T (2026-05-06) — representative hero image picked from the
  // cluster's articles' image_url (first non-null, ordered by
  // published_at DESC). Null when none of the source articles had
  // an OG image at ingestion time. Mig 0132 backfilled this for
  // pre-existing clusters.
  imageUrl: string | null;
  createdAt: string;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
}

export interface NewsHubClusterDetail extends NewsHubClusterRow {
  articleIds: string[];
}

export interface NewsHubArticleRow {
  id: string;
  externalId: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  sourceName: string;
  sourceUrl: string;
  sourceProvider: string;
  sourceLocale: string;
  applicableRegions: string[];
  publishedAt: string;
  ingestedAt: string;
  country: string | null;
  language: string | null;
  categories: string[];
  keywords: string[];
  priority: number;
  isBreaking: boolean;
}

// Market Creation Hub item (2026-05-12) — union of a content_drafts
// row + a markets row, normalised into a single shape so the hub
// renders them uniformly. `kind` discriminator + `marketSlug` (on
// kind='market' rows) tells the UI which action to surface.
export interface CreationHubItem {
  kind: "draft" | "market";
  id: string;
  title: string;
  status: string;
  source: "cluster" | "competitor" | "manual" | "translation";
  region: string | null;
  topicSlug: string | null;
  topicName: string | null;
  imageUrl: string | null;
  createdAt: string;
  createdByUserId: string | null;
  publishedAt: string | null;
  marketSlug: string | null;
  draftKind: "binary" | "multi_outcome" | null;
  // v2 event-grouping (2026-05-12). Drafts off a news_hub_cluster
  // carry the cluster id + title; manual/competitor drafts and
  // markets carry null on both. Used by the hub UI to collapse
  // multiple drafts from the same cluster into one event card.
  clusterId: string | null;
  clusterTitle: string | null;
}

export interface ContentDraftRow {
  id: string;
  draftType: "market" | "post" | "translation";
  status: "pending" | "reviewed" | "published" | "discarded";
  content: Record<string, unknown>;
  sourceType: "cluster" | "competitor" | "manual" | "translation" | null;
  sourceId: string | null;
  aiModel: string | null;
  aiPromptVersion: string | null;
  aiCostUsd: number | null;
  createdAt: string;
  createdByUserId: string | null;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  publishedAt: string | null;
  // 2026-05-08 — surfaced on list() so the admin queue can render a
  // topic chip per row + group/filter by topic without a second
  // roundtrip. Undefined on getById/insert paths until those are
  // also extended.
  primaryTopicSlug?: string | null;
  primaryTopicName?: string | null;
  // STORY-T.3 (2026-05-12) — cluster og:image, LEFT JOIN'd at getById
  // time. DraftPreviewModal renders this as a hero above the per-type
  // renderer. Null on non-cluster sources or when the cluster hasn't
  // resolved an og:image yet.
  clusterImageUrl?: string | null;
}

/**
 * PR R-1 (2026-05-06) — discriminated union for `POST /v1/admin/drafts/:id/publish`.
 *
 * The backend handler is decorated `@HttpCode(OK)` blanket. The success
 * path returns `{ id, status, downstream, compliance }`. Four refusal
 * paths return `{ error, …context }` ALSO with HTTP 200 (violates the
 * PR #1495 discipline rule — converted to proper status codes by PR R-1.5,
 * shipped immediately after this fix). Until R-1.5 lands, frontend MUST
 * branch on `.error` first; the previous code blindly read `.downstream`
 * and crashed every refusal with "Cannot read properties of undefined
 * (reading 'slice')".
 */
export type AdminPublishDraftResult =
  | {
      // Success path.
      id: string;
      status: string;
      downstream: Record<string, unknown>;
      compliance?: {
        regionCode: string;
        passed: boolean;
        errorCount: number;
        warningCount: number;
        policyApplied: string;
      };
      error?: undefined;
    }
  | {
      // Refusal path. `error` is the discriminator; remaining fields
      // depend on which refusal fired (not_found / compliance_check_failed
      // / compliance_override_requires_reason / publish_downstream_failed).
      error: string;
      id?: string;
      draftId?: string;
      regionCode?: string;
      errors?: Array<{ field?: string; message?: string } | string>;
      warnings?: Array<{ field?: string; message?: string } | string>;
      hint?: string;
      detail?: string;
      draftType?: string;
      policyApplied?: string;
      downstream?: undefined;
      status?: undefined;
    };

export type RegionCode = "global" | "br" | "mx" | "ca" | "in" | "us";

export interface CompetitorMarketRow {
  id: string;
  source: "polymarket" | "kalshi" | "manifold";
  sourceExternalId: string;
  sourceUrl: string;
  title: string;
  description: string | null;
  volume24hUsd: number | null;
  currentPriceYes: number | null;
  status: string;
  resolutionAt: string | null;
  /** Region codes the row is relevant to. Always non-empty;
   * defaults to ['global']. Heuristic-populated at scrape time
   * from the title/description; AI re-classifies on draft. */
  applicableRegions: RegionCode[];
  scrapedAt: string;
  createdAt: string;
  updatedAt: string;
  // 2026-05-08 — populated by `adminListCompetitorMarkets` only (not
  // by getById). The list query LEFT JOINs content_drafts and
  // returns the highest-status draft per competitor:
  // 'published' > 'reviewed' > 'pending' > 'discarded'. The admin
  // UI grays out the Draft button on non-discarded statuses to
  // avoid duplicate API calls; a discarded row gets a "Re-draft"
  // affordance because the prior attempt was rejected.
  draftId?: string | null;
  draftStatus?: "pending" | "reviewed" | "published" | "discarded" | null;
}

// Market Creation Hub PR-D — competitor-import normalized shape.
export interface CompetitorImportOutcome {
  name: string;
  price: number | null;
}

export interface CompetitorImportMarket {
  competitor: "polymarket" | "kalshi";
  externalId: string;
  url: string;
  title: string;
  description: string | null;
  category: string | null;
  endDate: string | null;
  volume: number | null;
  outcomes: CompetitorImportOutcome[];
}

// 2026-04-29 — Stocktwits-style message volume gauge. Tier
// thresholds tuned for early-stage prediction-market scale; the
// SHAPE (low/medium/high) stays stable across re-tunes so the
// frontend doesn't bake numeric thresholds.
export interface MessageVolumeResponse {
  totalCount: number;
  rootCount: number;
  replyCount: number;
  windowHours: number;
  tier: "low" | "medium" | "high";
}

export interface OrderbookLevel {
  priceCents: number;
  sizeShares: string;
  orderCount: number;
  // Subset of sizeShares contributed by *implied* counterparties
  // (opposite-outcome BUYs displayed via complementary pricing).
  // Real depth = sizeShares - impliedDepthShares. UI marks levels
  // distinctly when impliedDepthShares > 0 — V1 matcher limitation:
  // BUY YES + SELL NO doesn't pair-mint, so implied depth is
  // visible-but-not-fillable from the same-side perspective.
  impliedDepthShares: string;
  // STORY-C (#1559) — depth at this price contributed by the
  // VIEWER's own orders, including cross-book attribution. Server
  // populates this via the optional-JWT orderbook endpoint when an
  // authenticated viewer is present; '0' for anonymous reads.
  // Optional for backwards compat with pre-STORY-C fixtures + the
  // anonymous response shape — UI treats absent as '0'.
  ownDepthShares?: string;
}

export interface OrderbookSide {
  bids: OrderbookLevel[];
  asks: OrderbookLevel[];
  bestBid: number | null;
  bestAsk: number | null;
  midPrice: number | null;
  spread: number | null;
}

export interface OrderbookResponse {
  marketId: string;
  marketSlug: string;
  outcomes: { yesId: string; noId: string };
  yes: OrderbookSide;
  no: OrderbookSide;
}

export interface RecentTrade {
  id: string;
  priceCents: number;
  sizeShares: string;
  outcomeId: string;
  outcomeSortOrder: number;
  createdAt: string;
}

export type PriceHistoryInterval = "1h" | "6h" | "1d" | "1w" | "1m" | "all";

export interface PriceHistoryPoint {
  ts: string;
  priceCents: number;
  volumeCents: string;
}

export interface PriceHistoryResponse {
  marketId: string;
  marketSlug: string;
  interval: PriceHistoryInterval;
  outcomeId: string;
  outcome: "yes" | "no";
  points: PriceHistoryPoint[];
}

// STORY-C — per-outcome history powering the multi-line chart on
// multi-outcome market detail pages. Top 5 outcomes by volume plus a
// synthetic "Other" rollup line when the market has 6+ outcomes. Same
// per-point shape as `PriceHistoryPoint` so the chart's point-mapping
// code is shared. The chart renders this from `GET
// /v1/markets/:slug/history?multi=true`.
export interface MultiOutcomeHistorySeries {
  outcomeId: string;
  name: I18nText;
  sortOrder: number;
  volumeCents: string;
  points: PriceHistoryPoint[];
}

export interface MultiOutcomeHistoryResponse {
  marketId: string;
  marketSlug: string;
  interval: PriceHistoryInterval;
  outcomes: MultiOutcomeHistorySeries[];
}

export interface PlaceOrderInput {
  marketId: string;
  outcomeId: string;
  side: "buy" | "sell";
  orderType: "limit" | "market";
  // EPIC-003 STORY-810 — Polymarket-style execution flag. Optional
  // during the Phase 2B compat window: when omitted, the backend
  // derives it from (orderType, autoConvert). STORY-812 phase 2
  // will flip TradePanel to send this directly and drop the legacy
  // (orderType, autoConvert) inputs.
  //   GTC = good-til-cancelled (rest until filled/cancelled)
  //   GTD = good-til-date (rest until expires_at)
  //   FOK = fill-or-kill (fill all immediately at ≤ price else cancel)
  //   FAK = fill-and-kill (fill what's available, cancel remainder —
  //         replaces the prior auto-convert-to-resting-limit
  //         transition that STORY-811 deleted)
  executionFlag?: "GTC" | "GTD" | "FOK" | "FAK";
  priceCents?: number;
  sizeShares: number;
  // STORY-347 — for market orders, when true (default) the backend
  // converts an unfilled order to a limit at the outcome's last-trade
  // price instead of cancelling. Set false for strict mode.
  // STORY-811 (2026-05-01): the auto-convert transition was deleted;
  // this flag now resolves to executionFlag=FAK (true) or FOK (false).
  // Kept on the input shape for compat — TradePanel still passes it.
  autoConvert?: boolean;
  // STORY-708 — slippage tolerance in cents from outcome anchor.
  // Mutually exclusive with maxSlippagePercent (server-side check).
  // Frontend (STORY-709) populates this from user settings (STORY-710).
  maxSlippageCents?: number;
  clientOrderId?: string;
  idempotencyKey?: string;
  // EPIC-041 part 3c — per-trade override for the auto-share-to-feed
  // pref. Default undefined → server falls back to
  // users.auto_share_to_feed. Pass `false` from the trade-confirm
  // step when the user un-checks "Share to feed" — that override is
  // per-trade only; the global pref stays unchanged.
  shareToFeed?: boolean;
  // EPIC-031 STORY-1102 phase 2 — Polymarket-style builder
  // attribution. 64-char hex (32 bytes) sourced from the granbay.bc
  // cookie / `?bc=<hex>` URL param via `useBuilderRefCode`. Backend
  // validates against `builders.code WHERE status='approved'`;
  // unknown codes 400 rather than silently dropping attribution.
  builderCodeHex?: string;
}

// EPIC-003 STORY-710 — slippage preferences shape mirrors backend
// SlippagePrefs interface. /settings/trading edits these.
export interface SlippagePrefs {
  defaultMaxSlippageCents: number;
  slippageAutoConvert: "auto-convert" | "reject";
}

// STORY-708 — structured rejection block returned alongside the order
// row when maxSlippageCents was set AND the matcher left shares
// unfilled because of the tightened price ceiling/floor.
export interface SlippageRejection {
  reason: "SLIPPAGE_EXCEEDED";
  anchorPriceCents: number;
  maxAllowedCents: number;
  effectivePriceCents: number;
  unfilledShares: string;
}

export interface OrderRecord {
  id: string;
  // 2026-04-27 — listMine JOINs markets + outcomes so /home can
  // render the Polymarket-style card (outcome badge + market
  // title + info row). The legacy snake_case fields stay for the
  // place-order response shape; new fields are camelCase to match
  // the listPositions convention.
  market_id?: string;
  marketId?: string;
  outcome_id?: string;
  outcomeId?: string;
  marketSlug?: string;
  marketTitle?: I18nText | null;
  marketKind?: "binary" | "multi_outcome" | null;
  marketImageUrl?: string | null;
  outcomeName?: I18nText | null;
  outcomeSortOrder?: number | null;
  side: "buy" | "sell";
  order_type: string;
  price_cents: number | null;
  size_shares: string;
  filled_shares: string;
  // STORY-812 phase 3c — average fill price cents for the post-
  // execution status display ("filled at avg N¢"). Backend writes
  // this on every fill in the matcher.
  avg_fill_price_cents?: number | null;
  status: string;
  // STORY-697 follow-up (2026-05-03) — per-order rewards earning
  // hint. Fields surface the market's reward config + latest
  // persisted midpoint so the open-orders list can render a "💰
  // earning" indicator without N+1 fetches. Only present on
  // listMine responses (not on place-order). See
  // computeOrderEarningStatus() below for the eligibility predicate.
  marketRewardsEnabled?: boolean | null;
  marketMaxSpreadCents?: number | null;
  marketMinSizeShares?: string | null;
  marketMidpointCents?: string | null;
  // STORY-708 — present on placeOrder response when the matcher left
  // shares unfilled because of the user's slippage ceiling.
  slippageRejection?: SlippageRejection;
  // STORY-B-server (#1557) — structured "why didn't this fill"
  // signal. Surfaced by OrdersService.placeOrder when the matcher
  // returns less than the full quantity. The trade panel maps each
  // value to a specific human message instead of a generic "didn't
  // fill" line.
  nonFillReason?:
    | "self_trade_only_liquidity"
    | "price_out_of_range"
    | "no_resting_orders"
    | "partial_fill_remainder_cancelled"
    | null;
  created_at: string;
}

/**
 * STORY-697 follow-up — derive the earning state of an open order
 * from the fields listMine surfaces. Returns:
 *   - "earning"   — order is on the book, in-zone, size-eligible
 *   - "ineligible"— rewards-enabled market but order fails one of
 *                   the gates (out of zone / under min size / wrong
 *                   status)
 *   - "off"       — market doesn't have rewards enabled
 *   - "unknown"   — missing data needed to compute (e.g. no
 *                   midpoint persisted yet for a fresh market)
 *
 * Mirrors the gates in apps/api/src/rewards/reward-scoring.service.ts
 * (proximity_factor + size threshold + active-on-book status).
 */
export type OrderEarningState = "earning" | "ineligible" | "off" | "unknown";

export function computeOrderEarningStatus(o: OrderRecord): OrderEarningState {
  if (o.marketRewardsEnabled === false || o.marketRewardsEnabled == null) {
    return o.marketRewardsEnabled === false ? "off" : "unknown";
  }
  if (o.status !== "live") return "ineligible";
  if (o.price_cents === null) return "ineligible"; // market orders never rest
  if (o.marketMidpointCents == null || o.marketMaxSpreadCents == null) {
    return "unknown";
  }
  const midpoint = Number(o.marketMidpointCents);
  if (!Number.isFinite(midpoint)) return "unknown";
  const distance = Math.abs(o.price_cents - midpoint);
  if (distance > o.marketMaxSpreadCents) return "ineligible";
  if (o.marketMinSizeShares != null) {
    const minSize = Number(o.marketMinSizeShares);
    const remaining = Number(o.size_shares) - Number(o.filled_shares ?? "0");
    if (
      Number.isFinite(minSize) &&
      Number.isFinite(remaining) &&
      remaining < minSize
    ) {
      return "ineligible";
    }
  }
  return "earning";
}

export interface WalletRow {
  kind: string;
  currency: string;
  balance_cents: string;
}

export interface TestFundsResponse {
  grantedCents: string;
  currency: string;
  message: string;
}

export interface PositionRecord {
  id: string;
  marketId: string;
  marketSlug: string;
  marketTitle: I18nText;
  marketStatus: string;
  marketImageUrl: string | null;
  resolvedOutcomeId: string | null;
  resolvedAt: string | null;
  outcomeId: string;
  outcomeName: I18nText;
  outcomeSortOrder: number;
  shares: string;
  avgCostCents: string;
  currentPriceCents: number;
  realizedPnlCents: string;
  totalFeesPaidCents: string;
  categorySlug: string;
  updatedAt: string;
}

// EPIC-005 STORY-309d phase 1 — admin queue row shape.
export interface DisputeAdminRow {
  id: string;
  market: {
    id: string;
    slug: string;
    title: I18nText | null;
    resolvedAt: string | null;
  };
  user: { id: string; username: string };
  status: "pending_review" | "withdrawn" | "upheld" | "rejected";
  reason: string;
  evidenceUrl: string | null;
  createdAt: string;
}

// STORY-309d phase 3 — multi-admin verdict workflow row shape.
// Returned by GET /v1/admin/disputes/:id/verdict-proposals; each
// row includes its vote roll so the admin sees who voted what at a
// glance.
export interface DisputeVerdictProposalRow {
  id: string;
  disputeId: string;
  proposerId: string;
  proposerUsername: string | null;
  proposedVerdict: "uphold" | "reject";
  proposerNotes: string;
  status: "pending" | "tiebreaker" | "agreed" | "rejected";
  approveCount: number;
  rejectCount: number;
  createdAt: string;
  finalizedAt: string | null;
  votes: Array<{
    voterId: string;
    voterUsername: string | null;
    vote: "approve" | "reject";
    reason: string | null;
    createdAt: string;
  }>;
}

// EPIC-029 STORY-1721 — withdrawal pipeline shapes.
export type WithdrawalStatus =
  | "requested"
  | "auto_approved"
  | "admin_review"
  | "admin_processing"
  | "processing"
  | "completed"
  | "rejected"
  | "failed";

// User-facing withdrawal row shape. Same fields as the admin
// surface today (the backend GET /me/withdrawals returns the full
// row); a future security pass may sanitize rule_eval_trace +
// reason_text on the user surface for hard rejections, but for
// closed-beta the user seeing their own engine verdict is useful
// debugging signal.
export interface MyWithdrawalRow {
  id: string;
  amountUsdcCents: string;
  destinationKind: "pix_cpf" | "pix_email" | "pix_phone" | "pix_random";
  destinationValue: string;
  status: WithdrawalStatus;
  ruleEvalTrace: Record<string, "pass" | "fail" | "skip">;
  reasonText: string | null;
  fxLocalCurrency: string | null;
  fxLockedRate: number | null;
  fxLockedAt: string | null;
  fxLockExpiresAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  externalTransferId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminWithdrawalRow {
  id: string;
  userId: string;
  amountUsdcCents: string;
  destinationKind: "pix_cpf" | "pix_email" | "pix_phone" | "pix_random";
  destinationValue: string;
  status: WithdrawalStatus;
  ruleEvalTrace: Record<string, "pass" | "fail" | "skip">;
  reasonText: string | null;
  fxLocalCurrency: string | null;
  fxLockedRate: number | null;
  fxLockedAt: string | null;
  fxLockExpiresAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  externalTransferId: string | null;
  createdAt: string;
  updatedAt: string;
}

// EPIC-COPY part 2 — copy-trader fan-out health.
export interface CopyHealthCounts {
  placed: number;
  skipped: number;
  failed: number;
  total: number;
  failureRatePct: number;
}

export interface CopyHealthPerPiRow {
  piUserId: string;
  username: string;
  placed: number;
  skipped: number;
  failed: number;
  total: number;
  failureRatePct: number;
  skipReasons: Array<{ reason: string | null; count: number }>;
}

export interface CopyHealthResponse {
  global: { last24h: CopyHealthCounts; last7d: CopyHealthCounts };
  perPi: CopyHealthPerPiRow[];
  generatedAt: string;
}

// EPIC-COPY STORY-510 — admin list of stop-loss-triggered subscriptions.
export interface StopLossTriggerRow {
  subscriptionId: string;
  subscriberId: string;
  subscriberUsername: string;
  piUserId: string;
  piUsername: string;
  allocationCents: string;
  stopLossPct: number | null;
  entryEquityCents: string | null;
  triggeredAt: string;
  subscribedAt: string;
}

// EPIC-038 — admin notification inbox.
export type AdminInboxPriority = "critical" | "high" | "medium" | "low";

export type AdminInboxSource =
  | "resolution"
  | "dispute"
  | "ticket"
  | "pi_application"
  | "copy_health"
  | "bot_report"
  | "account_deletion";

export interface AdminInboxItem {
  id: string;
  source: AdminInboxSource;
  priority: AdminInboxPriority;
  title: string;
  context: string;
  href: string;
  createdAt: string;
}

export interface AdminInboxResponse {
  total: number;
  counts: Record<AdminInboxPriority, number>;
  items: AdminInboxItem[];
  truncated: boolean;
  generatedAt: string;
}

export interface ListMarketsParams {
  status?: string;
  category?: string;
  q?: string;
  sort?: "closes_asc" | "trending";
  cursor?: string;
  limit?: number;
  // EPIC-022 STORY-627 — tag filter for the topic page. Backend GET
  // /v1/markets accepts CSV; the helper joins arrays.
  tags?: string[];
}

// 2026-05-13 — surface real strings from heterogeneous error-array
// shapes (string | ValidationError | ComplianceFinding). String(obj)
// = "[object Object]" was rendered to the founder in the Hub editor
// publish flow.
function extractErrorMessage(m: unknown): string {
  if (typeof m === "string") return m;
  if (m && typeof m === "object") {
    const obj = m as {
      constraints?: Record<string, string>;
      property?: string;
      message?: unknown;
      code?: unknown;
    };
    if (obj.constraints && typeof obj.constraints === "object") {
      const vals = Object.values(obj.constraints).filter(
        (v): v is string => typeof v === "string",
      );
      if (vals.length > 0) return vals.join(", ");
    }
    if (typeof obj.message === "string" && obj.message.length > 0) {
      return obj.message;
    }
    if (typeof obj.property === "string") {
      return `Invalid value for "${obj.property}".`;
    }
    if (typeof obj.code === "string") return obj.code;
  }
  return "Validation error.";
}

export class ApiError extends Error {
  // The raw server-side message (class-validator output, exception
  // payload, etc). Kept for diagnostics + admin tooling but NOT
  // surfaced to end-users — the `message` super-call below is
  // sanitized via humanizeApiMessage so trade-panel toasts and
  // similar surfaces never render schema-validator strings like
  // "property executionFlag should not exist" (founder bug
  // 2026-05-01).
  public rawMessage: string;
  // PR L (2026-05-06) — request-id from the response's
  // `x-request-id` header (populated by pino-http on every API
  // response). Surfaced into the 5xx user-facing message so
  // operators can correlate logs from a user-reported reference.
  // Null when the response didn't carry the header.
  public requestId: string | null;
  // STORY-R-1.6 (2026-05-12) — structured details from the server's
  // error.details field (AllExceptionsFilter merges any `details: {}`
  // shipped on a thrown HttpException body into the response). Lets
  // admin surfaces render per-field error/warning lists (e.g.
  // compliance.errors[]) instead of just the flattened first message.
  // Null when the server didn't ship details.
  public details: Record<string, unknown> | null;
  constructor(
    public status: number,
    public code: string,
    message: string,
    requestId: string | null = null,
    details: Record<string, unknown> | null = null,
  ) {
    super(humanizeApiMessage(status, message, requestId));
    this.rawMessage = message;
    this.requestId = requestId;
    this.details = details;
    this.name = "ApiError";
  }
}

/**
 * Replace machine-shaped schema-validator messages with a friendly
 * end-user fallback. Raw messages are preserved on `err.rawMessage`
 * for log surfaces. Heuristic only — when the message clearly looks
 * like field-level explanation a normal user could act on (e.g.
 * "must be at least 8 characters", custom service-layer 400s like
 * "Insufficient shares: ..."), we keep it as-is. The patterns we
 * sanitize are the ones that leak our internal field names or
 * type-system jargon.
 */
function humanizeApiMessage(
  status: number,
  message: string,
  requestId: string | null = null,
): string {
  // class-validator forbidNonWhitelisted: "property X should not exist"
  if (/^property\s+\w+\s+should not exist$/i.test(message)) {
    return "Something went wrong with that request. Please refresh and try again.";
  }
  // class-validator type-mismatch: "X must be a UUID", "must be an integer"
  // — these leak our internal field names. Only sanitize when they
  // smell purely structural (no user-actionable content). Keep
  // length/min/max-style messages because they're useful to users
  // (e.g. "Password must be at least 8 characters").
  if (
    /^[a-zA-Z_]+\s+must be (a|an) (UUID|integer|boolean|number|string)$/i.test(
      message,
    )
  ) {
    return "Something went wrong with that request. Please refresh and try again.";
  }
  // 5xx fallback: never surface raw 5xx server text. PR L (2026-05-06)
  // — include the request-id reference per the error-message
  // discipline rule from PR #1495 ("HOW to fix it" = give the user
  // something they can share with support). pino-http sets
  // `x-request-id` on every API response (apps/api/src/app.module.ts
  // genReqId), so the id is reliably available for correlation.
  if (status >= 500 && status < 600) {
    if (requestId) {
      return `Unexpected server error — please try again or share this reference: ${requestId}`;
    }
    return "Unexpected server error. Please try again.";
  }
  return message;
}

function getApiBase(): string {
  const url = process.env.NEXT_PUBLIC_API_URL;
  if (!url) {
    throw new Error(
      "NEXT_PUBLIC_API_URL is not set. Add it to .env.local for dev or to Vercel env for prod.",
    );
  }
  return url.replace(/\/$/, "");
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // PR L (2026-05-06) — request-id correlation. pino-http sets the
    // header on every API response (apps/api/src/app.module.ts
    // pinoHttp.genReqId). Pulled here and passed through ApiError so
    // the 5xx fallback string can include "share this reference: <id>"
    // per the error-message discipline rule (CLAUDE.md → "Error
    // message discipline" → 5xx pattern).
    const requestId = res.headers.get("x-request-id");
    let payload: {
      // AllExceptionsFilter shape: { error: { code, message, details?, traceId, timestamp } }
      error?:
        | {
            code?: string;
            message?: unknown;
            details?: { errors?: unknown[]; payload?: unknown };
          }
        | string;
      // Legacy / direct-Nest shapes some endpoints still emit:
      // { message: string | string[], statusCode, error: "Bad Request" }
      message?: unknown;
    } = {};
    try {
      payload = (await res.json()) as typeof payload;
    } catch {
      // non-JSON response (network error page, etc)
    }

    // Resolve in order of preference, biased toward the canonical
    // wrapped shape produced by AllExceptionsFilter. Older code paths
    // and class-validator's bare {message: string[]} body still flow
    // through the fallbacks. Critical: never let an object slip into
    // `message` — it ends up rendered as the literal "[object Object]"
    // (the bug behind tonight's SELL flow toast).
    let code = "error";
    let message = `HTTP ${res.status}`;
    let details: Record<string, unknown> | null = null;

    if (payload.error && typeof payload.error === "object") {
      const wrapped = payload.error;
      if (typeof wrapped.code === "string") code = wrapped.code;
      // STORY-R-1.6 (2026-05-12) — surface structured details on
      // ApiError so admin UIs can render per-field error lists.
      if (wrapped.details && typeof wrapped.details === "object") {
        details = wrapped.details as Record<string, unknown>;
      }
      // Prefer the field-level error array over the generic message.
      // The backend wraps class-validator failures as
      // { code: 'VALIDATION_ERROR', message: 'Request validation failed',
      //   details: { errors: ['slug must be ...', ...] } } — falling
      // through to `message` would surface only "Request validation
      // failed" and hide the field-level detail. Field-level wins.
      if (Array.isArray(wrapped.details?.errors)) {
        // 2026-05-13 — details.errors may contain:
        //   1) plain strings (legacy)
        //   2) class-validator ValidationError objects (.constraints)
        //   3) ComplianceFinding objects ({code, message, detail})
        // String(obj) = "[object Object]" — surface real messages.
        message = wrapped.details.errors.map(extractErrorMessage).join(", ");
      } else if (typeof wrapped.message === "string") {
        message = wrapped.message;
      }
    } else if (typeof payload.message === "string") {
      message = payload.message;
      if (typeof payload.error === "string") code = payload.error;
    } else if (Array.isArray(payload.message)) {
      // 2026-05-13 — array messages may contain strings,
      // ValidationError objects (.constraints), or ComplianceFinding
      // objects (.message). String(obj) = "[object Object]".
      message = payload.message.map(extractErrorMessage).join(", ");
      if (typeof payload.error === "string") code = payload.error;
    } else if (typeof payload.error === "string") {
      message = payload.error;
      code = payload.error;
    }

    throw new ApiError(res.status, code, message, requestId, details);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

// ── Authed fetch with refresh-token rotation ──────────────────
//
// On 401 from any authed call we attempt POST /v1/auth/refresh once,
// save the new pair, and retry the original call with the new
// access token. Refresh failures clear tokens and surface a
// session_expired error so the consumer can route to /login.
//
// `pendingRefresh` single-flights concurrent 401s — without it,
// parallel calls would each try to rotate, but only the first
// succeeds (refresh token rotation revokes the prior session) so
// the others would cascade-fail.

let pendingRefresh: Promise<AuthTokens> | null = null;

async function refreshSession(): Promise<AuthTokens> {
  if (pendingRefresh) return pendingRefresh;
  const tokens = loadTokens();
  if (!tokens) {
    throw new ApiError(401, "no_session", "Not signed in.");
  }
  pendingRefresh = (async () => {
    try {
      const next = await post<AuthTokens>("/v1/auth/refresh", {
        refreshToken: tokens.refreshToken,
      });
      saveTokens(next);
      return next;
    } catch {
      clearTokens();
      throw new ApiError(
        401,
        "session_expired",
        "Your session expired. Please log in again.",
      );
    } finally {
      pendingRefresh = null;
    }
  })();
  return pendingRefresh;
}

async function authedFetch<T>(
  build: (accessToken: string) => Promise<Response>,
): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new ApiError(401, "no_session", "Not signed in.");

  let res = await build(tokens.accessToken);
  if (res.status === 401) {
    const next = await refreshSession();
    res = await build(next.accessToken);
  }
  return handleResponse<T>(res);
}

async function authedGet<T>(path: string): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      headers: { authorization: `Bearer ${token}` },
      cache: "no-store",
    }),
  );
}

/**
 * Public adapter that matches the (url, init) shape components like
 * SuggestResolutionSourcesButton expect. Forwards through the same
 * authedFetch / refresh-token retry logic as the rest of the SDK so
 * callers don't reimplement token handling.
 *
 * Use sparingly — prefer dedicated `api.*` typed methods. This exists
 * for components that take an `authedFetch` prop (the resolution-
 * suggester button) so they can stay generic across marketId-set vs
 * marketId-unset code paths.
 */
export function adminAuthedFetch<T = unknown>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        authorization: `Bearer ${token}`,
      },
      cache: "no-store",
    }),
  );
}

/**
 * 2026-05-09 — request coalescing for endpoints called from many
 * components on the same render. /v1/me/wallets had 7 simultaneous
 * callers on /home (SiteHeader, MobileBottomNav, /home page,
 * VirtualResetBanner, TestFundsBanner, TradePanel,
 * MultiOutcomeTradePanel) → 6× 429 (Too Many Requests). Wrapping the
 * thunk in coalesce() means concurrent callers share one in-flight
 * promise; once it resolves/rejects, the cached entry clears, so the
 * next call is a fresh fetch.
 *
 * Use only for IDEMPOTENT GETs. Wrapping a mutation in this would
 * silently dedupe writes — bug.
 */
const inflight = new Map<string, Promise<unknown>>();
function coalesce<T>(key: string, thunk: () => Promise<T>): () => Promise<T> {
  return () => {
    const existing = inflight.get(key);
    if (existing) return existing as Promise<T>;
    const fresh = thunk().finally(() => {
      // Clear after settle so the next call is a fresh fetch.
      // Concurrent callers that joined this promise still get its
      // resolved value via the closure they captured.
      if (inflight.get(key) === fresh) inflight.delete(key);
    });
    inflight.set(key, fresh);
    return fresh;
  };
}

// PR-N2 (2026-05-13) — sticky session-lifetime cache + inflight
// dedupe for /v1/topics/<slug>/tags. The admin hub editor fans out
// one fetch per topic on mount, tripping the 30 req/sec throttler;
// the cache collapses repeat renders + concurrent callers to one
// network request per slug per session. See listTopicTags below for
// the call-site explanation.
const topicTagsCache = new Map<string, { tags: TopicTagRecord[] }>();
const topicTagsInflight = new Map<
  string,
  Promise<{ tags: TopicTagRecord[] }>
>();

async function authedPostJson<T>(path: string, body: unknown): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      cache: "no-store",
    }),
  );
}

async function authedDelete<T>(path: string): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${token}` },
      cache: "no-store",
    }),
  );
}

async function authedPatchJson<T>(path: string, body: unknown): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      method: "PATCH",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      cache: "no-store",
    }),
  );
}

// PR F (2026-05-06) — variant that accepts extra headers, used for
// secrets endpoints that carry the step-up token in X-Step-Up-Token.
async function authedRequestJson<T>(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  return authedFetch<T>((token) =>
    fetch(`${getApiBase()}${path}`, {
      method,
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
        ...(extraHeaders ?? {}),
      },
      body: JSON.stringify(body),
      cache: "no-store",
    }),
  );
}

/**
 * 2026-05-13 — multipart upload helper for the MediaController endpoints.
 *
 * Either `file` (browser File object) OR `sourceUrl` (string) must be
 * provided. File uploads use XHR so the caller can wire an
 * onProgress callback; sourceUrl uses the regular JSON authed fetch
 * path. Both share the same response shape.
 *
 * Returned shape mirrors MediaService.uploadImage's result:
 *   { url, key, width, height, bytes, contentType: 'image/webp' }
 */
interface UploadMediaImageInput {
  path: string;
  file?: File;
  sourceUrl?: string;
  scope?: "markets" | "market-templates" | "market-events";
  onProgress?: (pct: number) => void;
}

interface UploadMediaImageResult {
  url: string;
  key: string;
  width: number;
  height: number;
  bytes: number;
  contentType: "image/webp";
}

async function uploadMediaImageImpl(
  input: UploadMediaImageInput,
): Promise<UploadMediaImageResult> {
  if (!input.file && !input.sourceUrl) {
    throw new ApiError(
      400,
      "media_input_missing",
      "Upload failed: either a `file` or `sourceUrl` is required.",
    );
  }
  // JSON path — sourceUrl only. Routes through the standard authed
  // POST so refresh-token rotation + 4xx mapping behave like every
  // other admin call.
  if (!input.file && input.sourceUrl) {
    return authedPostJson<UploadMediaImageResult>(input.path, {
      sourceUrl: input.sourceUrl,
      ...(input.scope ? { scope: input.scope } : {}),
    });
  }

  // Multipart path — XHR so we can surface upload progress to the
  // dropzone progress bar. Manual refresh-token rotation: if the
  // first attempt 401s, refresh and retry once.
  const file = input.file!;
  const send = async (accessToken: string): Promise<UploadMediaImageResult> =>
    new Promise<UploadMediaImageResult>((resolve, reject) => {
      const form = new FormData();
      form.append("file", file, file.name);
      if (input.scope) form.append("scope", input.scope);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${getApiBase()}${input.path}`, true);
      xhr.setRequestHeader("authorization", `Bearer ${accessToken}`);
      xhr.upload.onprogress = (e) => {
        if (input.onProgress && e.lengthComputable) {
          input.onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as UploadMediaImageResult);
          } catch (err) {
            reject(
              new ApiError(
                xhr.status,
                "media_parse_failed",
                `Upload succeeded but response body was not valid JSON: ${(err as Error).message}`,
              ),
            );
          }
          return;
        }
        let parsed:
          | { error?: string; message?: string; statusCode?: number }
          | string
          | null = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = xhr.responseText;
        }
        const code =
          (typeof parsed === "object" && parsed && parsed.error) ||
          "media_upload_failed";
        const message =
          (typeof parsed === "object" && parsed && parsed.message) ||
          (typeof parsed === "string" && parsed) ||
          `Upload failed: HTTP ${xhr.status} ${xhr.statusText}.`;
        reject(new ApiError(xhr.status, code, message));
      };
      xhr.onerror = () =>
        reject(
          new ApiError(
            0,
            "network_error",
            "Upload failed: network error while contacting the API. Check connectivity and retry.",
          ),
        );
      xhr.send(form);
    });

  const tokens = loadTokens();
  if (!tokens) throw new ApiError(401, "no_session", "Not signed in.");

  try {
    return await send(tokens.accessToken);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      const next = await refreshSession();
      return send(next.accessToken);
    }
    throw err;
  }
}

// EPIC-021 STORY-381 — optional-auth GET. Sends the bearer token when
// the viewer is signed in (so the backend can compute isOwnProfile +
// isFollowing + follower-only stats) but degrades gracefully to an
// unauthed call for anonymous viewers.
async function maybeAuthedGet<T>(path: string): Promise<T> {
  const tokens = loadTokens();
  if (tokens) return authedGet<T>(path);
  const res = await fetch(`${getApiBase()}${path}`, { cache: "no-store" });
  return handleResponse<T>(res);
}

async function get<T>(
  path: string,
  options: { accessToken?: string; revalidate?: number } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.accessToken)
    headers.authorization = `Bearer ${options.accessToken}`;

  // Retry transient 5xx and network-level failures up to 3 attempts.
  // CF Tunnel from Vercel-region IPs is intermittently flaky (the
  // free-tier tunnel routing isn't always healthy from US-east edge to
  // our DOKS connector pods); for idempotent GETs, retry is safe and
  // takes ~750ms worst case before the data cache backs off to stale.
  const url = `${getApiBase()}${path}`;
  // Per-fetch Next.js data cache. On success, response is cached for
  // `revalidate` seconds; while revalidating in the background, stale
  // is served. If the upstream fails during revalidation, stale stays
  // served — the user sees slightly-old data, never an error page.
  // accessToken-bearing requests skip the cache (per-user data).
  const init: RequestInit & { next?: { revalidate: number } } = { headers };
  if (options.accessToken) {
    init.cache = "no-store";
  } else {
    init.next = { revalidate: options.revalidate ?? 30 };
  }

  let lastError: unknown = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }
    try {
      const res = await fetch(url, init);
      if (res.status >= 500 && res.status < 600 && attempt < 2) {
        lastError = new Error(`HTTP ${res.status}`);
        continue;
      }
      return await handleResponse<T>(res);
    } catch (err) {
      lastError = err;
      if (attempt < 2) continue;
      throw err;
    }
  }
  throw lastError ?? new Error("Unreachable retry path");
}

function buildMarketsQuery(params: ListMarketsParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.category) search.set("category", params.category);
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.tags && params.tags.length > 0) {
    search.set("tags", params.tags.join(","));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// Public methods (no auth required) and authed methods (token auto-managed).
// Authed methods read tokens from localStorage and transparently rotate via
// /v1/auth/refresh on 401 — consumers never pass the access token directly.
export const api = {
  signup: (input: SignupInput) => post<AuthTokens>("/v1/auth/signup", input),
  // STORY-107 — login may return a 2FA challenge instead of tokens.
  // Caller branches on `requires2fa` and posts to /v1/auth/login/2fa
  // with the user's TOTP code to complete.
  login: (input: LoginInput) =>
    post<AuthTokens | LoginRequires2fa>("/v1/auth/login", input),
  loginWith2fa: (input: { challengeToken: string; code: string }) =>
    post<AuthTokens>("/v1/auth/login/2fa", input),
  logout: () => {
    const tokens = loadTokens();
    clearTokens();
    if (tokens) {
      // fire-and-forget; server-side revoke is best-effort
      void post<void>("/v1/auth/logout", {
        refreshToken: tokens.refreshToken,
      }).catch(() => {});
    }
  },

  // EPIC-008 STORY-825 — step-up auth challenge. Re-verifies the
  // user's password (+ TOTP when 2FA is enabled) and bumps the
  // session's elevated_auth_at stamp on success. Sensitive admin
  // endpoints behind StepUpAuthGuard require a fresh stamp within
  // STEP_UP_AUTH_WINDOW_MINUTES (default 15).
  stepUpAuth: (input: { password: string; totpCode?: string }) =>
    authedPostJson<{ elevatedAt: string; validUntil: string }>(
      "/v1/auth/step-up",
      input,
    ),

  // STORY-104 — email verification flow.
  requestEmailVerification: () =>
    authedPostJson<void>("/v1/auth/email/verify-request", {}),
  verifyEmail: (token: string) =>
    post<{ userId: string }>("/v1/auth/email/verify", { token }),

  // STORY-105 — password reset flow. The reset-request endpoint is
  // ALWAYS 200 regardless of whether the email exists — UI should not
  // surface success/failure based on status (don't leak enumeration).
  requestPasswordReset: (email: string) =>
    post<{ ok: true }>("/v1/auth/password/reset-request", { email }),
  consumePasswordReset: (token: string, newPassword: string) =>
    post<{ userId: string; sessionsRevoked: number }>(
      "/v1/auth/password/reset",
      { token, newPassword },
    ),

  // STORY-377 — email change flow.
  requestEmailChange: (newEmail: string) =>
    authedPostJson<void>("/v1/auth/email/change-request", { newEmail }),
  confirmEmailChange: (token: string) =>
    post<{ userId: string; sessionsRevoked: number }>(
      "/v1/auth/email/change-confirm",
      { token },
    ),
  revokeEmailChange: (token: string) =>
    post<{ userId: string; rolledBack: boolean }>(
      "/v1/auth/email/change-revoke",
      { token },
    ),

  // EPIC-046 part 1d — followers' feed extension. Recent discussions
  // authored by users the viewer follows.
  listFeedDiscussions: () =>
    authedGet<{
      discussions: Array<TopicDiscussionRecord & { topicSlug: string }>;
    }>("/v1/me/feed/discussions"),

  // EPIC-046 part 1 — followers' feed extension (market comments).
  // Recent market comments authored by users the viewer follows.
  // Mirrors the discussions carousel one row above; the two surfaces
  // give /feed/posts the activity propagation the founder spec
  // calls for (comments + discussions → follower feed).
  listFeedMarketComments: () =>
    authedGet<{
      comments: Array<{
        id: string;
        authorUserId: string;
        authorUsername: string;
        authorDisplayName: string | null;
        marketSlug: string;
        marketTitle: string;
        bodyExcerpt: string;
        createdAt: string;
      }>;
    }>("/v1/me/feed/market-comments"),

  // Paginated variant of the followee-comments feed for the
  // /feed/comments full page (vs the carousel-style listFeedMarketComments
  // above which returns top-N flat). Cursor is `<createdAt>:<id>`.
  listFeedMarketCommentsPaged: (params?: {
    cursor?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    q.set("limit", String(params?.limit ?? 20));
    return authedGet<{
      comments: Array<{
        id: string;
        authorUserId: string;
        authorUsername: string;
        authorDisplayName: string | null;
        marketSlug: string;
        marketTitle: string;
        bodyExcerpt: string;
        createdAt: string;
      }>;
      nextCursor: string | null;
    }>(`/v1/me/feed/market-comments?${q.toString()}`);
  },

  // EPIC-044 part 4 — topic discussions.
  getDiscussion: (discussionId: string) =>
    get<{
      discussion: TopicDiscussionRecord & { topicSlug: string };
    }>(`/v1/discussions/${encodeURIComponent(discussionId)}`, {
      revalidate: 30,
    }),
  createTopicDiscussion: (
    slug: string,
    body: {
      title: string;
      body: string;
      // EPIC-047 — optional market embed from the composer's "Attach a
      // position" picker. Must be a market belonging to this topic.
      attachedMarketId?: string | null;
    },
  ) =>
    authedPostJson<{ discussion: TopicDiscussionRecord }>(
      `/v1/topics/${encodeURIComponent(slug)}/discussions`,
      body,
    ),
  listDiscussionReplies: (discussionId: string) =>
    get<{ replies: TopicDiscussionReplyRecord[] }>(
      `/v1/discussions/${encodeURIComponent(discussionId)}/replies`,
      { revalidate: 15 },
    ),
  createDiscussionReply: (
    discussionId: string,
    body: { body: string; parentReplyId?: string | null },
  ) =>
    authedPostJson<{ reply: TopicDiscussionReplyRecord }>(
      `/v1/discussions/${encodeURIComponent(discussionId)}/replies`,
      body,
    ),
  flagDiscussion: (discussionId: string, body: { reason?: string } = {}) =>
    authedPostJson<{ flagged: true }>(
      `/v1/discussions/${encodeURIComponent(discussionId)}/flag`,
      body,
    ),
  flagDiscussionReply: (
    discussionId: string,
    replyId: string,
    body: { reason?: string } = {},
  ) =>
    authedPostJson<{ flagged: true }>(
      `/v1/discussions/${encodeURIComponent(discussionId)}/replies/${encodeURIComponent(replyId)}/flag`,
      body,
    ),
  listTopicDiscussions: (
    slug: string,
    params: { sort?: "active" | "newest" | "top"; cursor?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.sort) qs.set("sort", params.sort);
    if (params.cursor) qs.set("cursor", params.cursor);
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return get<{
      discussions: TopicDiscussionRecord[];
      nextCursor: string | null;
    }>(`/v1/topics/${encodeURIComponent(slug)}/discussions${tail}`, {
      revalidate: 30,
    });
  },

  // EPIC-045 part 4 — user submits a URL for admin review.
  submitTopicNewsArticle: (
    slug: string,
    body: { url: string; title: string; sourceName?: string },
  ) =>
    authedPostJson<{ article: NewsArticleRecord | null }>(
      `/v1/topics/${encodeURIComponent(slug)}/news/submit`,
      body,
    ),

  // EPIC-045 part 1 — news articles per topic.
  listTopicNews: (slug: string) =>
    get<{ articles: NewsArticleRecord[] }>(
      `/v1/topics/${encodeURIComponent(slug)}/news`,
      { revalidate: 60 },
    ),

  // EPIC-044 follow-up — batched followed-topic slugs for the /topics
  // index Join CTAs. Single round-trip; the index then renders every
  // card's button with the right state. Returns [] for anonymous;
  // 401 when the request is unauthed.
  listMyFollowedTopics: () =>
    authedGet<{ slugs: string[] }>("/v1/me/followed-topics"),

  // EPIC-044 follow-up — Follow Topic button.
  getTopicFollowState: (slug: string) =>
    get<{ following: boolean; followerCount: number }>(
      `/v1/topics/${encodeURIComponent(slug)}/follow`,
      { revalidate: 0 },
    ),
  followTopic: (slug: string) =>
    authedPostJson<{ following: true }>(
      `/v1/topics/${encodeURIComponent(slug)}/follow`,
      {},
    ),
  unfollowTopic: (slug: string) =>
    authedDelete<void>(`/v1/topics/${encodeURIComponent(slug)}/follow`),

  // EPIC-046 part 1 — feed-events activity timeline reads. Both
  // surfaces share the same FeedEventRow shape so the renderer can
  // branch on `eventType` to format each row.
  listUserActivity: (
    handle: string,
    params: { cursor?: string; limit?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.cursor) qs.set("cursor", params.cursor);
    if (params.limit) qs.set("limit", String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return get<{ events: FeedEventRow[]; nextCursor: string | null }>(
      `/v1/users/${encodeURIComponent(handle)}/activity${tail}`,
      { revalidate: 0 },
    );
  },
  listMyFollowingFeed: (params: { cursor?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.cursor) qs.set("cursor", params.cursor);
    if (params.limit) qs.set("limit", String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return authedGet<{ events: FeedEventRow[]; nextCursor: string | null }>(
      `/v1/me/feed${tail}`,
    );
  },

  // EPIC-044 part 2 — topic pages. Public read, no auth required.
  listTopics: () =>
    get<{ topics: TopicRecord[] }>("/v1/topics", { revalidate: 60 }),
  getTopic: (slug: string) =>
    get<{ topic: TopicRecord }>(`/v1/topics/${encodeURIComponent(slug)}`, {
      revalidate: 60,
    }),
  // PR W.1 — public list of tags scoped to a topic. Used by the
  // topic-page Tag-filtered tab dropdown. Includes only `active` tags.
  //
  // PR-N2 (2026-05-13) — wrapped with a module-scoped sticky cache +
  // in-flight coalesce. The admin hub editor (Tab1Basics) iterates
  // every topic returned by listTopics() and fires one tag fetch per
  // topic on mount; with ~5-10 topics this trips the 30 req/sec
  // short-window throttler and the user sees a wall of 429s in the
  // console. Caching by slug for the page-lifetime + coalescing
  // concurrent fetches per slug collapses the fan-out to one network
  // request per slug per session. Tag lists change rarely (admin
  // edits via /admin/tags), so a session-lifetime cache is safe —
  // worst case the admin needs a hard reload to see a freshly-added
  // tag, which matches the existing /v1/topics revalidate=60 cache
  // window posture.
  listTopicTags: (slug: string): Promise<{ tags: TopicTagRecord[] }> => {
    const cached = topicTagsCache.get(slug);
    if (cached) return Promise.resolve(cached);
    const inflightExisting = topicTagsInflight.get(slug);
    if (inflightExisting) return inflightExisting;
    const fresh = get<{ tags: TopicTagRecord[] }>(
      `/v1/topics/${encodeURIComponent(slug)}/tags`,
      { revalidate: 60 },
    )
      .then((res) => {
        topicTagsCache.set(slug, res);
        return res;
      })
      .finally(() => {
        topicTagsInflight.delete(slug);
      });
    topicTagsInflight.set(slug, fresh);
    return fresh;
  },

  listTopicMarkets: (
    slug: string,
    params: {
      sort?: "volume" | "newest" | "closing_soon";
      limit?: number;
      // PR W.1 — Tag-filtered tab on /topic/[slug].
      tagSlug?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.sort) qs.set("sort", params.sort);
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.tagSlug) qs.set("tag_slug", params.tagSlug);
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return get<{ markets: TopicMarketCard[] }>(
      `/v1/topics/${encodeURIComponent(slug)}/markets${tail}`,
      { revalidate: 30 },
    );
  },

  // EPIC-047 — server-side topic search for the composer's
  // destination picker. Optional auth: when authed, `followedOnly`
  // scopes to the caller's followed topics.
  searchTopics: (
    params: {
      q?: string;
      followedOnly?: boolean;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.followedOnly) qs.set("followedOnly", "true");
    if (params.limit) qs.set("limit", String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return maybeAuthedGet<{ topics: TopicRecord[] }>(
      `/v1/topics/search${tail}`,
    );
  },

  // EPIC-047 — markets on a topic with the caller's open positions
  // pre-joined. Powers the composer's "Attach a position" picker.
  listMyPositionsOnTopic: (slug: string) =>
    authedGet<{ rows: TopicMarketWithUserPosition[] }>(
      `/v1/topics/${encodeURIComponent(slug)}/my-positions`,
    ),

  listMarkets: (params: ListMarketsParams = {}) =>
    get<MarketsListResponse>(`/v1/markets${buildMarketsQuery(params)}`),
  getMarket: (slug: string) => get<MarketDetail>(`/v1/markets/${slug}`),
  // Market Creation Hub — sports event sub-market routing (migration 0157).
  listMarketEvents: (
    params: {
      status?: "upcoming" | "live";
      category?: string;
      region?: string;
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.category) q.set("category", params.category);
    if (params.region) q.set("region", params.region);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return get<MarketEventSummary[]>(`/v1/market-events${qs ? `?${qs}` : ""}`);
  },
  getMarketEvent: (slug: string) =>
    get<MarketEventDetail>(`/v1/market-events/${slug}`),
  // Public fee rates — trade panel reads these on mount so the fee
  // line + math reflects the live admin-set value (STORY-902b)
  // instead of the previously-hardcoded 2%.
  getPublicFees: () =>
    get<{ takerFeeBps: number; makerFeeBps: number }>("/v1/platform/fees", {
      revalidate: 60,
    }),
  getOrderbook: (slug: string) =>
    get<OrderbookResponse>(`/v1/markets/${slug}/orderbook`, { revalidate: 5 }),
  // News Hub PR 3b — cluster summaries matched to this market by
  // keyword/category overlap. Used by the RelatedNewsSection on the
  // market detail page. Falls back gracefully when no matches exist
  // (returns { items: [] }) — caller hides the section.
  getMarketRelatedNews: (slug: string, limit = 6) =>
    get<MarketRelatedNewsResponse>(
      `/v1/markets/${slug}/related-news?limit=${limit}`,
      { revalidate: 120 },
    ),
  // 2026-04-29 — distinct-buyer sentiment gauge (binary markets).
  // Returns null for multi-outcome / scalar markets — caller renders
  // nothing rather than misleading bars.
  getMarketSentiment: (slug: string, windowHours?: number) => {
    const qs = windowHours ? `?windowHours=${windowHours}` : "";
    return get<MarketSentimentResponse | null>(
      `/v1/markets/${slug}/sentiment${qs}`,
      { revalidate: 60 },
    );
  },
  // Unified 0-100 score combining trade flow + post sentiment + news
  // sentiment. Replaces the discrete gauges per founder spec —
  // returns null on non-binary markets.
  getMarketUnifiedSentiment: (slug: string) =>
    get<UnifiedSentimentResponse | null>(
      `/v1/markets/${slug}/sentiment-unified`,
      { revalidate: 60 },
    ),
  // 2026-04-29 — Stocktwits-style message volume gauge. Counts
  // non-deleted, non-admin-hidden comments + replies on the market
  // over the window. Public + uncached-friendly TTL because the
  // counter is the whole point of the gauge.
  getMarketMessageVolume: (marketId: string, windowHours?: number) => {
    const qs = windowHours ? `?windowHours=${windowHours}` : "";
    return get<MessageVolumeResponse>(
      `/v1/markets/${marketId}/comments/volume${qs}`,
      { revalidate: 30 },
    );
  },
  getRecentTrades: (slug: string, limit = 20) =>
    get<RecentTrade[]>(`/v1/markets/${slug}/trades?limit=${limit}`, {
      revalidate: 5,
    }),
  getMarketHistory: (
    slug: string,
    params: { interval?: PriceHistoryInterval; outcome?: "yes" | "no" } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.interval) search.set("interval", params.interval);
    if (params.outcome) search.set("outcome", params.outcome);
    const qs = search.toString();
    return get<PriceHistoryResponse>(
      `/v1/markets/${slug}/history${qs ? `?${qs}` : ""}`,
      // History endpoint cache: short TTL so post-trade refreshes
      // pick up new buckets quickly. Same 5s window as orderbook.
      { revalidate: 5 },
    );
  },
  // STORY-C — per-outcome history for the multi-line chart on
  // multi-outcome market detail pages. Same endpoint as
  // `getMarketHistory()` with `?multi=true`; the response shape is
  // different (`outcomes: [...]` instead of a single `points` array)
  // so the type parameter on `get<...>` carries the discriminator.
  getMarketHistoryMulti: (
    slug: string,
    params: { interval?: PriceHistoryInterval } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.interval) search.set("interval", params.interval);
    search.set("multi", "true");
    return get<MultiOutcomeHistoryResponse>(
      `/v1/markets/${slug}/history?${search.toString()}`,
      { revalidate: 5 },
    );
  },

  // 2026-05-09 — coalesce: 12+ components on /home + /markets call api.me()
  // on mount (useDisplayCurrency, useDisplayCurrencyPrimary, DisplaySettings,
  // SiteHeader, page-level auth checks, etc.). Same fan-out class as
  // listMyWallets — without coalescing the per-user 'short' throttle trips
  // before paint on heavily-componented pages.
  me: coalesce("me", () => authedGet<MeProfile>("/v1/me")),
  // STORY-348 — toggle the per-user strict-market-orders default.
  // PATCH (RFC 5789 partial update); a future "patch multiple
  // settings" endpoint can subsume this same body shape.
  updateStrictMarketOrders: (value: boolean) =>
    authedPatchJson<{ strict_market_orders: boolean }>(
      "/v1/me/strict-market-orders",
      { strictMarketOrders: value },
    ),
  // STORY-817 — toggle the per-user "show advanced order types"
  // power-user pref. Default false. When true the trade panel
  // surfaces the FOK/GTD/GTC/POSTONLY execution-flag selector.
  updateAdvancedOrderTypesEnabled: (value: boolean) =>
    authedPatchJson<{ advancedOrderTypesEnabled: boolean }>(
      "/v1/me/advanced-order-types-enabled",
      { advancedOrderTypesEnabled: value },
    ),
  // EPIC-003 STORY-710 + STORY-711 — slippage preferences.
  getSlippagePrefs: () => authedGet<SlippagePrefs>("/v1/me/slippage-prefs"),
  updateSlippagePrefs: (input: Partial<SlippagePrefs>) =>
    authedPatchJson<SlippagePrefs>("/v1/me/slippage-prefs", input),
  // STORY-1121 phase 2 — cross-device theme persistence. Frontend
  // also writes localStorage on every toggle so the no-flash
  // bootstrap stays zero-round-trip; this call backs the toggle
  // up to the server for fresh-login devices.
  // EPIC-028 STORY-1604 — locale persistence.
  updateLocale: (
    locale: "pt" | "en" | "es" | "es-MX" | "en-CA" | "fr-CA" | "hi",
  ) => authedPatchJson<{ locale: string }>("/v1/me/locale", { locale }),
  // EPIC-050 STORY-2200 — Tier-1 light KYC submission.
  // Returns the user's post-state (tier + ageAtSubmission +
  // minAgeRequired). Idempotent — already-Tier-1 users get a no-op
  // 200 response. Throws on age-below-minimum / display-name
  // conflict / malformed DOB; the form surfaces structured errors.
  submitKycTier1: (input: {
    dob: string; // YYYY-MM-DD
    country?: string;
    displayName?: string;
  }) =>
    authedPostJson<{
      kycTier: number;
      kycStatus: string;
      dob: string;
      ageAtSubmission: number;
      minAgeRequired: number;
    }>("/v1/me/kyc/tier1", input),
  // EPIC-050 STORY-2202 — admin Tier-1 review queue.
  listAdminKycFlagged: (params: { cursor?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.cursor) q.set("cursor", params.cursor);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{
      items: Array<{
        id: string;
        email: string;
        username: string | null;
        displayName: string | null;
        country: string | null;
        kycTier: number;
        flaggedAt: string;
        flaggedReason: string;
      }>;
      nextCursor: string | null;
    }>(`/v1/admin/kyc/flagged${qs ? `?${qs}` : ""}`);
  },
  adminKycFlag: (userId: string, reason: string) =>
    authedPostJson<{ ok: true }>(`/v1/admin/kyc/${userId}/flag`, { reason }),
  adminKycClearFlag: (userId: string) =>
    authedPostJson<{ ok: true }>(`/v1/admin/kyc/${userId}/clear-flag`, {}),
  // EPIC-029 STORY-1701 — per-user display-currency preference.
  // Pass null to clear the explicit preference (UI falls back to
  // regions.default_currency at render time).
  updateDisplayCurrency: (
    displayCurrency:
      | "USD"
      | "USDC"
      | "BRL"
      | "EUR"
      | "GBP"
      | "MXN"
      | "ARS"
      | "COP"
      | null,
  ) =>
    authedPatchJson<{ displayCurrency: string | null }>(
      "/v1/me/display-currency",
      { displayCurrency },
    ),
  // EPIC-030 STORY-1805 phase 2 — toggle inline USD→local annotation.
  updateShowInlineConversion: (value: boolean) =>
    authedPatchJson<{ showInlineConversion: boolean }>(
      "/v1/me/show-inline-conversion",
      { showInlineConversion: value },
    ),
  // EPIC-029 BR-primary display flip Story 1 + 6 — read + write the
  // user's "which currency leads on every screen" preference.
  // Default per region: 'BRL' for region=brazil, 'USDC' otherwise.
  // Power-user toggle on /settings/display lets a region=brazil user
  // opt out (USDC primary) or a non-BR user opt in (their fiat
  // primary, when their region adds fiat support).
  getDisplayCurrencyPrimary: () =>
    authedGet<{ displayCurrencyPrimary: "USDC" | "BRL" }>(
      "/v1/me/display-currency-primary",
    ),
  // EPIC-051 Story 9 / ADR-043 — system-LP admin dashboard.
  getSystemLpDashboard: () =>
    authedGet<{
      systemLpUserId: string | null;
      spreadProfiles: Array<{
        categorySlug: string;
        spreadCents: number;
        seedSizeCents: string;
        inventoryLimitCents: string;
      }>;
      recentAudits: Array<{
        id: string;
        action: string;
        targetId: string | null;
        payload: Record<string, unknown>;
        createdAt: string;
      }>;
    }>("/v1/admin/system-lp/dashboard"),
  updateDisplayCurrencyPrimary: (value: "USDC" | "BRL") =>
    authedPatchJson<{ display_currency_primary: "USDC" | "BRL" }>(
      "/v1/me/display-currency-primary",
      { displayCurrencyPrimary: value },
    ),

  // EPIC-005 STORY-309d phase 1 — resolution disputes.
  fileDispute: (
    slug: string,
    input: { reason: string; evidenceUrl?: string },
  ) =>
    authedPostJson<{ id: string; status: "pending_review" }>(
      `/v1/markets/${slug}/disputes`,
      input,
    ),
  withdrawDispute: (id: string) => authedDelete(`/v1/me/disputes/${id}`),
  listAdminDisputes: (
    params: {
      status?: "pending_review" | "withdrawn" | "upheld" | "rejected";
      limit?: number;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return authedGet<DisputeAdminRow[]>(
      `/v1/admin/disputes${qs ? `?${qs}` : ""}`,
    );
  },
  // EPIC-COPY part 2 — admin copy-trader health surface.
  getCopyHealth: () => authedGet<CopyHealthResponse>("/v1/admin/copy-health"),
  // EPIC-COPY STORY-510 — admin list of recent stop-loss triggers.
  getCopyHealthStopLossTriggers: (params?: {
    windowDays?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.windowDays !== undefined)
      qs.set("windowDays", String(params.windowDays));
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    const tail = qs.toString();
    return authedGet<{
      rows: StopLossTriggerRow[];
      generatedAt: string;
    }>(`/v1/admin/copy-health/stop-loss-triggers${tail ? `?${tail}` : ""}`);
  },
  // EPIC-038 — admin notification inbox.
  getAdminInbox: () => authedGet<AdminInboxResponse>("/v1/admin/inbox"),
  // STORY-309d phase 2 — single-admin verdict on a pending dispute.
  issueDisputeVerdict: (
    id: string,
    input: { verdict: "uphold" | "reject"; notes: string },
  ) =>
    authedPostJson<{
      id: string;
      status: "upheld" | "rejected";
      resolvedAt: string;
    }>(`/v1/admin/disputes/${id}/verdict`, input),

  // STORY-309d phase 3 — multi-admin verdict workflow. Mirrors
  // STORY-309c's proposeResolution + voteOnProposal pattern. Admin A
  // proposes; Admin B votes; on 2+ approve the verdict applies.
  listDisputeVerdictProposals: (disputeId: string) =>
    authedGet<DisputeVerdictProposalRow[]>(
      `/v1/admin/disputes/${disputeId}/verdict-proposals`,
    ),
  proposeDisputeVerdict: (
    disputeId: string,
    input: { verdict: "uphold" | "reject"; notes: string },
  ) =>
    authedPostJson<{
      proposalId: string;
      status: "pending";
      disputeId: string;
    }>(`/v1/admin/disputes/${disputeId}/verdict-proposals`, input),
  voteOnDisputeVerdictProposal: (
    proposalId: string,
    input: { vote: "approve" | "reject"; reason?: string },
  ) =>
    authedPostJson<{
      proposalId: string;
      status: "pending" | "tiebreaker" | "agreed" | "rejected";
      approveCount: number;
      rejectCount: number;
      appliedVerdict: "upheld" | "rejected" | null;
    }>(`/v1/admin/disputes/verdict-proposals/${proposalId}/votes`, input),

  // EPIC-029 STORY-1721 — submit a new withdrawal request. The
  // server-side rule engine returns the verdict synchronously
  // (auto_approved / admin_review / rejected); the executor cron
  // picks up auto_approved rows and drives the Asaas transfer.
  createWithdrawal: (input: {
    amountUsdcCents: number;
    destinationKind: "pix_cpf" | "pix_email" | "pix_phone" | "pix_random";
    destinationValue: string;
    idempotencyKey?: string;
  }) =>
    authedPostJson<{ withdrawal: MyWithdrawalRow }>(
      "/v1/me/withdrawals",
      input,
    ),

  // EPIC-029 STORY-1721 — user-facing withdrawal history (their own
  // rows). Backed by GET /v1/me/withdrawals; cursor-paginated.
  listMyWithdrawals: (
    params: {
      status?: WithdrawalStatus;
      cursor?: string;
      limit?: number;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return authedGet<{
      withdrawals: MyWithdrawalRow[];
      nextCursor: string | null;
    }>(`/v1/me/withdrawals${qs ? `?${qs}` : ""}`);
  },

  // EPIC-029 STORY-1721 Part 3 — admin queue for the manual-review
  // branch of the withdrawal pipeline. Default filter is admin_review;
  // status='all' returns every row regardless.
  listAdminWithdrawals: (
    params: {
      status?: WithdrawalStatus | "all";
      cursor?: string;
      limit?: number;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return authedGet<{
      withdrawals: AdminWithdrawalRow[];
      nextCursor: string | null;
    }>(`/v1/admin/withdrawals${qs ? `?${qs}` : ""}`);
  },
  rejectAdminWithdrawal: (id: string, input: { notes: string }) =>
    authedPostJson<{
      id: string;
      status: "rejected";
      reviewedAt: string;
    }>(`/v1/admin/withdrawals/${id}/reject`, input),
  // EPIC-029 STORY-1721 Part 3.5 — admin approve. Promotes the row
  // back into the auto-approval lane; the executor handles the
  // burn + Asaas createPixTransfer in the next sweep tick.
  approveAdminWithdrawal: (id: string, input: { notes: string }) =>
    authedPostJson<{
      id: string;
      status: "auto_approved";
      reviewedAt: string;
    }>(`/v1/admin/withdrawals/${id}/approve`, input),
  // EPIC-029 STORY-1721 Part 3.6 — admin override on a hard-rejected
  // row. Audited under a distinct action so EPIC-019 audit-export
  // surfaces flag overrides for elevated scrutiny.
  overrideAdminWithdrawal: (id: string, input: { notes: string }) =>
    authedPostJson<{
      id: string;
      status: "auto_approved";
      reviewedAt: string;
    }>(`/v1/admin/withdrawals/${id}/override`, input),
  updateThemePref: (themePref: "system" | "light" | "dark" | null) =>
    authedPatchJson<{ theme_pref: "system" | "light" | "dark" | null }>(
      "/v1/me/theme",
      { themePref },
    ),
  // EPIC-021 STORY-375 — own-profile partial update.
  updateProfile: (input: {
    displayName?: string | null;
    avatarUrl?: string | null;
    bio?: string | null;
  }) =>
    authedPatchJson<{
      displayName: string | null;
      avatarUrl: string | null;
      bio: string | null;
    }>("/v1/me/profile", input),
  // EPIC-021 STORY-376 — notification prefs.
  getNotificationPrefs: () =>
    authedGet<NotificationPrefs>("/v1/me/notification-prefs"),
  updateNotificationPrefs: (patch: Partial<NotificationPrefs>) =>
    authedPatchJson<NotificationPrefs>("/v1/me/notification-prefs", patch),

  // EPIC-006 STORY-601 + 602 — market comments + voting.
  listMarketComments: (
    marketId: string,
    params?: {
      cursor?: string | null;
      limit?: number;
      // 2026-04-29 (slice 3 part B) — Stocktwits-parity sort modes:
      //   new      — created_at DESC (default)
      //   top      — score DESC
      //   active   — updated_at DESC
      //   trending — score DESC over the last 7 days
      sort?: "new" | "top" | "active" | "trending";
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.sort) q.set("sort", params.sort);
    const qs = q.toString();
    return get<{ comments: CommentRow[]; nextCursor: string | null }>(
      `/v1/markets/${marketId}/comments${qs ? `?${qs}` : ""}`,
    );
  },
  listMyMarketComments: (
    marketId: string,
    params?: {
      cursor?: string | null;
      limit?: number;
      // 2026-04-29 (slice 3 part B) — Stocktwits-parity sort modes:
      //   new      — created_at DESC (default)
      //   top      — score DESC
      //   active   — updated_at DESC
      //   trending — score DESC over the last 7 days
      sort?: "new" | "top" | "active" | "trending";
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.sort) q.set("sort", params.sort);
    const qs = q.toString();
    return authedGet<{ comments: CommentRow[]; nextCursor: string | null }>(
      `/v1/me/markets/${marketId}/comments${qs ? `?${qs}` : ""}`,
    );
  },
  createMarketComment: (
    marketId: string,
    input: {
      body: string;
      parentCommentId?: string;
      // Slice 2A — per-comment opt-out from the verifiable position
      // badge. When true the snapshot is skipped (column stays NULL,
      // badge can never render).
      postAnonymously?: boolean;
    },
  ) => authedPostJson<CommentRow>(`/v1/me/markets/${marketId}/comments`, input),
  listCommentReplies: (
    commentId: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ comments: CommentRow[]; nextCursor: string | null }>(
      `/v1/comments/${commentId}/replies${qs ? `?${qs}` : ""}`,
    );
  },
  voteOnComment: (commentId: string, value: 1 | -1) =>
    authedPostJson<{ score: number; myVote: -1 | 0 | 1 }>(
      `/v1/comments/${commentId}/vote`,
      { value },
    ),
  deleteComment: (commentId: string) =>
    authedDelete<{ id: string; deletedAt: string }>(
      `/v1/comments/${commentId}`,
    ),
  // EPIC-006 STORY-605 — admin moderation surface.
  listAdminComments: (params?: {
    cursor?: string | null;
    limit?: number;
    hiddenOnly?: boolean;
    includeDeleted?: boolean;
    marketId?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.hiddenOnly) q.set("hiddenOnly", "true");
    if (params?.includeDeleted) q.set("includeDeleted", "true");
    if (params?.marketId) q.set("marketId", params.marketId);
    const qs = q.toString();
    return authedGet<{
      comments: AdminCommentRow[];
      nextCursor: string | null;
    }>(`/v1/admin/comments${qs ? `?${qs}` : ""}`);
  },
  hideComment: (commentId: string, reason: string) =>
    authedPostJson<{ id: string; hiddenAt: string }>(
      `/v1/admin/comments/${commentId}/hide`,
      { reason },
    ),
  unhideComment: (commentId: string) =>
    authedPostJson<{ id: string }>(
      `/v1/admin/comments/${commentId}/unhide`,
      {},
    ),

  // EPIC-006 STORY-603 — follow / unfollow.
  followUser: (username: string) =>
    authedPostJson<{ followed: true; followeeId: string }>(
      `/v1/me/follows/${encodeURIComponent(username)}`,
      {},
    ),
  unfollowUser: (username: string) =>
    authedDelete<{ unfollowed: true }>(
      `/v1/me/follows/${encodeURIComponent(username)}`,
    ),
  listMyFollowing: (params?: { cursor?: string | null; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ edges: FollowEdge[]; nextCursor: string | null }>(
      `/v1/me/follows${qs ? `?${qs}` : ""}`,
    );
  },
  listMyFollowers: (params?: { cursor?: string | null; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ edges: FollowEdge[]; nextCursor: string | null }>(
      `/v1/me/followers${qs ? `?${qs}` : ""}`,
    );
  },
  getUserRelationship: (username: string) =>
    authedGet<UserRelationship>(
      `/v1/users/${encodeURIComponent(username)}/relationship`,
    ),

  // EPIC-006 STORY-604 — personalized feed.
  // 2026-05-04 — moved from /v1/me/feed → /v1/me/markets-feed in
  // lockstep with the backend rename. Two NestJS controllers used to
  // both register `me/feed`, the older STORY-604 markets-followees
  // controller won the route (SocialModule imported first in
  // app.module.ts) and returned `{ items, nextCursor }` regardless of
  // which surface was calling. /feed/activity expected `{ events, ... }`
  // and blanked client-side on `events.length` of undefined. The newer
  // feed-events surface is the canonical /v1/me/feed; this older
  // markets-feed lives at /v1/me/markets-feed.
  listMyFeed: (params?: {
    cursor?: string | null;
    limit?: number;
    recentDays?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.recentDays !== undefined)
      q.set("recentDays", String(params.recentDays));
    const qs = q.toString();
    return authedGet<{ items: FeedItemRow[]; nextCursor: string | null }>(
      `/v1/me/markets-feed${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-006 STORY-611 — win-share preferences.
  getSharePrefs: () => authedGet<SharePrefs>("/v1/me/share-prefs"),
  updateSharePrefs: (patch: Partial<SharePrefs>) =>
    authedPatchJson<SharePrefs>("/v1/me/share-prefs", patch),

  // EPIC-021 STORY-378 — privacy preferences.
  getPrivacyPrefs: () => authedGet<PrivacyPrefs>("/v1/me/privacy-prefs"),
  updatePrivacyPrefs: (patch: Partial<PrivacyPrefs>) =>
    authedPatchJson<PrivacyPrefs>("/v1/me/privacy-prefs", patch),

  // EPIC-021 STORY-381 + STORY-382 — general-user public profile.
  getPublicProfile: (username: string) =>
    maybeAuthedGet<PublicProfileResponse | AccessGatedProfileResponse>(
      `/v1/u/${encodeURIComponent(username)}`,
    ),

  // EPIC-046 Part 1 — public activity feed (comments, posts, trades,
  // resolutions) for a handle. Cursor-paginated.
  getUserActivity: (
    username: string,
    params: { cursor?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return maybeAuthedGet<{
      events: FeedEventRow[];
      nextCursor: string | null;
    }>(
      `/v1/users/${encodeURIComponent(username)}/activity${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-046 Part 1 — main feed: events authored by anyone the
  // caller follows, chronological union.
  getMyFeed: (params: { cursor?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit) search.set("limit", String(params.limit));
    const qs = search.toString();
    return authedGet<{
      events: FeedEventRow[];
      nextCursor: string | null;
    }>(`/v1/me/feed${qs ? `?${qs}` : ""}`);
  },

  // EPIC-022 STORY-622/623 — post creation + read.
  createPost: (input: {
    title?: string | null;
    bodyMd: string;
    visibility: PostVisibility;
    kind?: PostKind;
    slides?: PostSlide[];
    photos?: string[];
    quotedPostId?: string | null;
    // EPIC-047 phase 1 — composer destination flag + sentiment chips.
    authorAnonymous?: boolean;
    sentiment?: "bullish" | "bearish";
  }) => authedPostJson<PostRecord>("/v1/posts", input),
  updatePost: (
    id: string,
    input: {
      title?: string | null;
      bodyMd?: string;
      visibility?: PostVisibility;
      kind?: PostKind;
      slides?: PostSlide[];
    },
  ) => authedPatchJson<PostRecord>(`/v1/posts/${id}`, input),
  deletePost: (id: string) => authedDelete<{ id: string }>(`/v1/posts/${id}`),
  getPost: (id: string) => maybeAuthedGet<PostRecord>(`/v1/posts/${id}`),
  // EPIC-046 part 2 phase C — toggle a Like. Server returns the
  // updated count + viewer flag.
  togglePostLike: (id: string) =>
    authedPostJson<{ likeCount: number; likedByViewer: boolean }>(
      `/v1/posts/${id}/like`,
      {},
    ),

  // EPIC-046 polish — toggle a private bookmark on a post. No count
  // exposed; just the viewer flag flips.
  togglePostBookmark: (id: string) =>
    authedPostJson<{ bookmarkedByViewer: boolean }>(
      `/v1/posts/${id}/bookmark`,
      {},
    ),

  // EPIC-046 polish — viewer's private bookmarks feed.
  // 2026-05-06 — endpoint renamed from /v1/me/bookmarks to
  // /v1/me/post-bookmarks to disambiguate from the older market-
  // bookmarks endpoint at the same URL (UsersController). See
  // posts.controller.ts comment for the collision history.
  listMyBookmarks: (params?: { cursor?: string | null; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/me/post-bookmarks${qs ? `?${qs}` : ""}`,
    );
  },
  listPostsByAuthor: (
    authorId: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return maybeAuthedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/users/${encodeURIComponent(authorId)}/posts${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-046 part 2 phase C step 4 — posts the named user has liked.
  // Public posts only; backend enforces the visibility gate.
  listPostsLikedByUser: (
    userId: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return maybeAuthedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/users/${encodeURIComponent(userId)}/likes${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-046 phase E follow-up — list posts that quote the named
  // post. Public surface; backend enforces public-only visibility
  // for the quoter so gated bodies don't leak through this list.
  listPostsQuotedBy: (
    postId: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return maybeAuthedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/posts/${encodeURIComponent(postId)}/quoted-by${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-041 follow-up — posts that attach the named market via
  // $TICKER. Used on /markets/[slug] to surface social commentary.
  listPostsAttachedToMarket: (
    marketId: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return maybeAuthedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/markets/${encodeURIComponent(marketId)}/posts${qs ? `?${qs}` : ""}`,
    );
  },

  // Admin observability — top-liked posts. Window in days; 0 = all-time.
  // Surfaces hidden / followers-only / subscribers-only too (admin
  // needs full visibility for moderation).
  adminListTopLikedPosts: (params?: {
    cursor?: string | null;
    limit?: number;
    windowDays?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.windowDays !== undefined) {
      q.set("windowDays", String(params.windowDays));
    }
    const qs = q.toString();
    return authedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/admin/posts/top-liked${qs ? `?${qs}` : ""}`,
    );
  },

  // Admin observability — top-volume markets. Window in days; 0 = all-time.
  // Surfaces every market regardless of status (open / closed / resolved)
  // — admin needs full visibility for moderation triage.
  adminListTopVolumeMarkets: (params?: {
    limit?: number;
    windowDays?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.windowDays !== undefined) {
      q.set("windowDays", String(params.windowDays));
    }
    const qs = q.toString();
    return authedGet<{
      markets: {
        id: string;
        slug: string;
        title: string;
        status: string;
        kind: string;
        categorySlug: string;
        volumeCents: string;
        tradeCount: number;
        uniqueTraders: number;
        createdAt: string;
        resolvedAt: string | null;
      }[];
    }>(`/v1/admin/markets/top-volume${qs ? `?${qs}` : ""}`);
  },

  // Admin observability — top-volume traders. Window in days; 0 = all-time.
  // Surfaces every user regardless of test/QA flag (admin needs full
  // visibility); the page renders chips per row so admins can filter
  // visually.
  adminListTopTraders: (params?: { limit?: number; windowDays?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.windowDays !== undefined) {
      q.set("windowDays", String(params.windowDays));
    }
    const qs = q.toString();
    return authedGet<{
      users: {
        id: string;
        username: string;
        displayName: string | null;
        role: string;
        country: string | null;
        isTest: boolean;
        isQaBot: boolean;
        volumeCents: string;
        tradeCount: number;
        uniqueMarkets: number;
        lastTradeAt: string | null;
        createdAt: string;
      }[];
    }>(`/v1/admin/users/top-traders${qs ? `?${qs}` : ""}`);
  },

  // EPIC-029 STORY-1702/1709/1710 — Transak deposit + withdrawal init.
  // Returns the Hosted Widget URL with the user's context pre-filled.
  // Sandbox env unlocks the surface for testing without flipping
  // regions.real_money_enabled; production env gates by it.
  //
  // STORY-1718 Part 2 (2026-05-03): widened `widgetUrl` to nullable
  // and added `pixDeposit` for direct-PSP adapters (AsaasAdapter PIX
  // flow). Today's response is byte-identical for Transak callers
  // (widgetUrl always populated, pixDeposit always null); BR users
  // post-Asaas-KYB will see widgetUrl=null + pixDeposit populated.
  // Optional `amountCents` body field passes the deposit amount the
  // PIX modal captured; widget-style adapters ignore it.
  initTransakDeposit: (body?: { amountCents?: number }) =>
    authedPostJson<DepositInitResponse>(`/v1/me/deposit/init`, body ?? {}),
  initTransakWithdrawal: (body?: { amountCents?: number }) =>
    authedPostJson<DepositInitResponse>(`/v1/me/withdrawal/init`, body ?? {}),

  // EPIC-046 part 2 phase F — outbound URL OG-preview. Auth-gated;
  // server caches both successes (24h) and failures (1h). Returns
  // `{ preview: null }` for blocked URLs (SSRF / unsupported scheme /
  // upstream error) — caller silently drops the card.
  fetchOgPreview: (url: string) =>
    authedGet<{
      preview: {
        url: string;
        title: string | null;
        description: string | null;
        imageUrl: string | null;
        siteName: string | null;
        finalUrl: string | null;
      } | null;
    }>(`/v1/og-preview?url=${encodeURIComponent(url)}`),

  // EPIC-022 STORY-625 phase 2 — flag a market comment.
  flagComment: (commentId: string, reason: string) =>
    authedPostJson<{ id: string; flagCount: number }>(
      `/v1/comments/${commentId}/flag`,
      { reason },
    ),

  // EPIC-022 STORY-625 — flag a post.
  flagPost: (postId: string, reason: string) =>
    authedPostJson<{ id: string; flagCount: number }>(
      `/v1/posts/${postId}/flag`,
      { reason },
    ),

  // EPIC-022 STORY-625 — admin moderation.
  getModerationQueue: () =>
    authedGet<ModerationQueueItem[]>("/v1/admin/moderation"),
  adminHidePost: (postId: string, reason: string) =>
    authedPostJson<{ id: string; status: "hidden" }>(
      `/v1/admin/posts/${postId}/hide`,
      { reason },
    ),
  adminUnhidePost: (postId: string, reason: string) =>
    authedPostJson<{ id: string; status: "visible" }>(
      `/v1/admin/posts/${postId}/unhide`,
      { reason },
    ),
  adminDismissPostFlags: (postId: string, reason: string) =>
    authedPostJson<{ dismissedCount: number }>(
      `/v1/admin/posts/${postId}/dismiss-flags`,
      { reason },
    ),

  // EPIC-022 STORY-625 phase 2 — comment moderation queue.
  getCommentModerationQueue: () =>
    authedGet<CommentModerationQueueItem[]>("/v1/admin/moderation/comments"),
  adminHideComment: (commentId: string, reason: string) =>
    authedPostJson<{ id: string; hiddenAt: string }>(
      `/v1/admin/comments/${commentId}/hide`,
      { reason },
    ),
  adminUnhideComment: (commentId: string) =>
    authedPostJson<{ id: string }>(
      `/v1/admin/comments/${commentId}/unhide`,
      {},
    ),
  adminDismissCommentFlags: (commentId: string, reason: string) =>
    authedPostJson<{ dismissedCount: number }>(
      `/v1/admin/comments/${commentId}/dismiss-flags`,
      { reason },
    ),

  // EPIC-022 STORY-625 phase 2 — topic discussion + reply moderation queue.
  // Returns a unified list of flagged discussions and replies tagged
  // by `kind`. Hide / dismiss-flags actions live on
  // /v1/admin/discussions/* (already shipped); this endpoint is
  // read-only.
  getTopicFlagQueue: () =>
    authedGet<TopicFlagQueueItem[]>("/v1/admin/moderation/topics"),

  adminHideDiscussion: (discussionId: string, reason?: string) =>
    authedPostJson<void>(`/v1/admin/discussions/${discussionId}/hide`, {
      reason,
    }),
  adminDismissDiscussionFlags: (discussionId: string) =>
    authedPostJson<{ dismissed: number }>(
      `/v1/admin/discussions/${discussionId}/dismiss-flags`,
      {},
    ),
  adminHideDiscussionReply: (
    discussionId: string,
    replyId: string,
    reason?: string,
  ) =>
    authedPostJson<void>(
      `/v1/admin/discussions/${discussionId}/replies/${replyId}/hide`,
      { reason },
    ),
  adminDismissDiscussionReplyFlags: (discussionId: string, replyId: string) =>
    authedPostJson<{ dismissed: number }>(
      `/v1/admin/discussions/${discussionId}/replies/${replyId}/dismiss-flags`,
      {},
    ),

  // EPIC-022 STORY-628 — paid subscriptions + tipping.
  setMySubscriptionPrice: (priceCents: number | null) =>
    authedPatchJson<{ priceCents: string | null }>(
      "/v1/me/subscription-price",
      { priceCents },
    ),
  subscribeToCreator: (username: string) =>
    authedPostJson<{
      subscription: SubscriptionRecord;
      ledgerTxId: string;
    }>(`/v1/me/subscriptions/${encodeURIComponent(username)}`, {}),
  listMySubscriptions: () =>
    authedGet<SubscriptionRecord[]>("/v1/me/subscriptions"),
  cancelSubscription: (id: string) =>
    authedDelete<SubscriptionRecord>(`/v1/me/subscriptions/${id}`),
  tipCreator: (
    username: string,
    input: {
      amountCents: string;
      note?: string;
      sourcePostId?: string;
    },
  ) =>
    authedPostJson<TipRecord>(
      `/v1/users/${encodeURIComponent(username)}/tips`,
      input,
    ),
  listMyTipsSent: () => authedGet<TipRecord[]>("/v1/me/tips/sent"),
  listMyTipsReceived: () => authedGet<TipRecord[]>("/v1/me/tips/received"),

  // EPIC-022 STORY-627 — public posts by hashtag (topic page).
  listPostsByTag: (
    tag: string,
    params?: { cursor?: string | null; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return get<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/tags/${encodeURIComponent(tag)}/posts${qs ? `?${qs}` : ""}`,
    );
  },

  // 2026-05-04 redesign phase 2 + 3 — News feed sub-tab.
  // Phase 2 (v1) was market resolutions only.
  // Phase 3 — mixed timeline: market resolutions (FeedEventRow) +
  // admin-curated news items (NewsItemRow). The backend merges them
  // by timestamp DESC and returns a discriminated-union `entries`
  // array; the frontend renders each row by `kind`.
  listNewsFeed: (params?: { cursor?: string | null; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return get<{
      entries: NewsFeedEntry[];
      nextCursor: string | null;
    }>(`/v1/feed/news${qs ? `?${qs}` : ""}`, { revalidate: 0 });
  },

  // 2026-05-04 phase 3 — admin CRUD for news_items (platform-wide
  // news that surfaces in the /feed/posts News tab alongside
  // market.resolved events).
  adminListNewsItems: (params?: {
    cursor?: string | null;
    limit?: number;
    includeDeleted?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.includeDeleted) q.set("includeDeleted", "true");
    const qs = q.toString();
    return authedGet<{
      items: NewsItemRow[];
      nextCursor: string | null;
    }>(`/v1/admin/news-items${qs ? `?${qs}` : ""}`);
  },
  adminCreateNewsItem: (input: {
    title: string;
    bodyMd?: string | null;
    sourceUrl?: string | null;
    imageUrl?: string | null;
    regionCode?: string | null;
    publishedAt?: string | null;
  }) => authedPostJson<NewsItemRow>("/v1/admin/news-items", input),
  adminUpdateNewsItem: (
    id: string,
    input: {
      title?: string;
      bodyMd?: string | null;
      sourceUrl?: string | null;
      imageUrl?: string | null;
      regionCode?: string | null;
      publishedAt?: string | null;
    },
  ) =>
    authedPatchJson<NewsItemRow>(
      `/v1/admin/news-items/${encodeURIComponent(id)}`,
      input,
    ),
  adminDeleteNewsItem: (id: string) =>
    authedDelete<{ ok: true }>(
      `/v1/admin/news-items/${encodeURIComponent(id)}`,
    ),

  // 2026-05-04 — news-bot aggregator admin surface (migration 0120).
  adminListNewsBots: () =>
    authedGet<{ bots: NewsBotSummary[] }>("/v1/admin/news-bots"),
  adminPromoteNewsBot: (userId: string) =>
    authedPostJson<{ ok: true }>(
      `/v1/admin/news-bots/${encodeURIComponent(userId)}/promote`,
      {},
    ),
  adminDemoteNewsBot: (userId: string) =>
    authedPostJson<{ ok: true }>(
      `/v1/admin/news-bots/${encodeURIComponent(userId)}/demote`,
      {},
    ),
  adminListNewsBotSources: (userId: string) =>
    authedGet<{ sources: NewsBotSource[] }>(
      `/v1/admin/news-bots/${encodeURIComponent(userId)}/sources`,
    ),
  adminCreateNewsBotSource: (
    userId: string,
    input: {
      name: string;
      rssUrl: string;
      topicSlug?: string | null;
      publishingMode?: "auto" | "review_required";
    },
  ) =>
    authedPostJson<{ source: NewsBotSource }>(
      `/v1/admin/news-bots/${encodeURIComponent(userId)}/sources`,
      input,
    ),
  adminUpdateNewsBotSource: (
    sourceId: string,
    input: {
      name?: string;
      rssUrl?: string;
      enabled?: boolean;
      topicSlug?: string | null;
      publishingMode?: "auto" | "review_required";
    },
  ) =>
    authedPatchJson<{ source: NewsBotSource }>(
      `/v1/admin/news-bots/sources/${encodeURIComponent(sourceId)}`,
      input,
    ),
  adminListAllNewsBotSources: () =>
    authedGet<{ sources: NewsBotSource[] }>("/v1/admin/news-bots/sources"),

  // ─── PR F (2026-05-06) — platform secrets management ──────────
  adminVerifyPasswordForStepUp: (input: {
    currentPassword: string;
    scope: StepUpScope;
    resource: string;
  }) =>
    authedPostJson<StepUpTokenResponse>(
      "/v1/admin/auth/verify-password",
      input,
    ),
  adminListSecrets: () =>
    authedGet<{ secrets: PlatformSecret[] }>("/v1/admin/secrets"),
  adminRevealSecret: (
    key: string,
    input: { reason: string; stepUpToken: string },
  ) =>
    authedRequestJson<{ value: string }>(
      "POST",
      `/v1/admin/secrets/${encodeURIComponent(key)}/reveal`,
      { reason: input.reason },
      { "x-step-up-token": input.stepUpToken },
    ),
  adminRotateSecret: (
    key: string,
    input: {
      newValue: string;
      testBeforeSave?: boolean;
      reason?: string;
      stepUpToken: string;
    },
  ) =>
    authedRequestJson<{ secret: PlatformSecret }>(
      "PUT",
      `/v1/admin/secrets/${encodeURIComponent(key)}`,
      {
        newValue: input.newValue,
        testBeforeSave: input.testBeforeSave,
        reason: input.reason,
      },
      { "x-step-up-token": input.stepUpToken },
    ),
  adminTestSecret: (key: string) =>
    authedPostJson<SecretTestResult>(
      `/v1/admin/secrets/${encodeURIComponent(key)}/test`,
      {},
    ),
  adminCreateSecret: (input: {
    key: string;
    value: string;
    description?: string;
    testBeforeSave?: boolean;
    stepUpToken: string;
  }) =>
    authedRequestJson<{ secret: PlatformSecret }>(
      "POST",
      "/v1/admin/secrets",
      {
        key: input.key,
        value: input.value,
        description: input.description,
        testBeforeSave: input.testBeforeSave,
      },
      { "x-step-up-token": input.stepUpToken },
    ),
  adminDeleteNewsBotSource: (sourceId: string) =>
    authedDelete<null>(
      `/v1/admin/news-bots/sources/${encodeURIComponent(sourceId)}`,
    ),
  adminRefreshNewsBotSource: (sourceId: string) =>
    authedPostJson<{ ok: true; postsCreated: number }>(
      `/v1/admin/news-bots/sources/${encodeURIComponent(sourceId)}/refresh`,
      {},
    ),

  // 2026-05-04 phase 3 — post comments primitive.
  // 2026-05-06 — sort param added for StockTwits redesign
  // (newest/top/active/trending). When unset, server defaults to newest.
  // Routed through authedGet when authed so the response includes
  // myVote enrichment per comment (server reads viewer via
  // OptionalJwtAuthGuard).
  listPostComments: (
    postId: string,
    params?: {
      cursor?: string | null;
      limit?: number;
      sort?: PostCommentSort;
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.sort) q.set("sort", params.sort);
    const qs = q.toString();
    const path = `/v1/posts/${encodeURIComponent(postId)}/comments${qs ? `?${qs}` : ""}`;
    const tokens = loadTokens();
    if (tokens) {
      return authedGet<{
        comments: PostCommentRow[];
        nextCursor: string | null;
      }>(path);
    }
    return get<{
      comments: PostCommentRow[];
      nextCursor: string | null;
    }>(path, { revalidate: 0 });
  },
  createPostComment: (
    postId: string,
    input: {
      bodyMd: string;
      parentCommentId?: string | null;
      // StockTwits Bullish/Bearish chip selected on the composer.
      // Server (post-#1475 backend) persists when present; older
      // backends ignore.
      sentiment?: "bullish" | "bearish" | null;
    },
  ) =>
    authedPostJson<PostCommentRow>(
      `/v1/posts/${encodeURIComponent(postId)}/comments`,
      input,
    ),
  deletePostComment: (postId: string, commentId: string) =>
    authedDelete<{ ok: true }>(
      `/v1/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
    ),
  // StockTwits redesign — vote up/down on a post comment. value is
  // -1 or +1; toggle semantics are server-side (re-cast same direction
  // = unvote, opposite = flip). Returns the new score + the viewer's
  // resolved vote so the optimistic UI can confirm the round-trip.
  votePostComment: (commentId: string, value: -1 | 1) =>
    authedPostJson<{ score: number; myVote: -1 | 0 | 1 }>(
      `/v1/post-comments/${encodeURIComponent(commentId)}/vote`,
      { value },
    ),
  // StockTwits redesign — flag a post comment with a free-text reason.
  // Server enforces 1-500 char reason, rejects self-flags, idempotent
  // on (flagger, comment).
  flagPostComment: (commentId: string, reason: string) =>
    authedPostJson<{ id: string; flagCount: number }>(
      `/v1/post-comments/${encodeURIComponent(commentId)}/flag`,
      { reason },
    ),

  // 2026-05-04 redesign phase 2 — viewer social context (which of my
  // followees liked / commented on each post). Lazy-fetched after
  // the main feed lands so the post list renders without waiting.
  listFeedSocialContext: (postIds: string[]) => {
    if (postIds.length === 0) {
      return Promise.resolve({} as Record<string, FeedSocialContext>);
    }
    const q = new URLSearchParams();
    q.set("postIds", postIds.join(","));
    return authedGet<Record<string, FeedSocialContext>>(
      `/v1/me/feed/posts/social-context?${q.toString()}`,
    );
  },

  // 2026-05-04 redesign phase 2 — friend-interaction post fan-in.
  // Returns posts NOT authored by my followees but liked / commented
  // by them. The /feed/posts page merges these into the main list
  // (deduped by post id, sorted by recency). Same PostRecord shape
  // as listMyPostsFeed; the social-context badge tells the viewer
  // why the post landed in their feed.
  listFeedFriendActivity: (params?: {
    cursor?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/me/feed/posts/friend-activity${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-022 STORY-624 — post feed (followees' posts).
  // EPIC-040 FEED-FILTERS — tab segments: 'following' (default) |
  // 'premium' (subscribers-only posts from active subs).
  listMyPostsFeed: (params?: {
    cursor?: string | null;
    limit?: number;
    tab?: "following" | "premium";
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.tab) q.set("tab", params.tab);
    const qs = q.toString();
    return authedGet<{ posts: PostRecord[]; nextCursor: string | null }>(
      `/v1/me/feed/posts${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-041 STORY-2104 — unauthenticated public-feed read.
  // Bodies are truncated server-side to ~30 words; the full body never
  // reaches the client unauth path. Auto-trade posts render fully per
  // the acquisition-fuel rule.
  listPublicPostsFeed: async (params?: {
    cursor?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    const res = await fetch(
      `${getApiBase()}/v1/posts/feed/public${qs ? `?${qs}` : ""}`,
      { cache: "no-store" },
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new ApiError(
        res.status,
        "fetch_failed",
        txt || `HTTP ${res.status}`,
      );
    }
    return (await res.json()) as {
      posts: PostRecord[];
      nextCursor: string | null;
    };
  },

  // EPIC-046 part 2 phase D — trending public posts (ranked by like
  // count over the last N days). Uses maybeAuthed so signed-in
  // viewers get `likedByViewer` populated server-side; anon viewers
  // get null/false.
  listTrendingPostsFeed: async (params?: {
    cursor?: string | null;
    limit?: number;
    windowDays?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.windowDays !== undefined) {
      q.set("windowDays", String(params.windowDays));
    }
    const qs = q.toString();
    return maybeAuthedGet<{
      posts: PostRecord[];
      nextCursor: string | null;
    }>(`/v1/posts/feed/trending${qs ? `?${qs}` : ""}`);
  },

  // Twitter-style news feed (feat/news-feed-twitter-style-tier3).
  // Returns admin-published news-hub cluster summaries, each enriched
  // with up to 3 source articles for the card footer attribution chips.
  // Filters: topicSlug, tagSlug, marketSlug — AND together. Distinct
  // from listNewsFeed() above which merges market.resolved events +
  // admin news_items into a heterogeneous timeline; this surface is
  // posts-only so the NewsFeedCard component renders a uniform shape.
  listNewsFeedPosts: (params?: {
    cursor?: string | null;
    limit?: number;
    topicSlug?: string | null;
    tagSlug?: string | null;
    marketSlug?: string | null;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.topicSlug) q.set("topicSlug", params.topicSlug);
    if (params?.tagSlug) q.set("tagSlug", params.tagSlug);
    if (params?.marketSlug) q.set("marketSlug", params.marketSlug);
    const qs = q.toString();
    return get<{
      posts: NewsFeedPost[];
      nextCursor: string | null;
    }>(`/v1/feed/news/posts${qs ? `?${qs}` : ""}`, { revalidate: 30 });
  },

  // EPIC-006 STORY-606 — in-app notifications.
  listMyNotifications: (params?: {
    cursor?: string | null;
    limit?: number;
    unreadOnly?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.unreadOnly) q.set("unreadOnly", "true");
    const qs = q.toString();
    return authedGet<{
      notifications: NotificationRow[];
      nextCursor: string | null;
    }>(`/v1/me/notifications${qs ? `?${qs}` : ""}`);
  },
  getUnreadNotificationCount: () =>
    authedGet<{ count: number }>("/v1/me/notifications/unread-count"),
  markNotificationRead: (id: string) =>
    authedPostJson<{ id: string; readAt: string }>(
      `/v1/me/notifications/${id}/read`,
      {},
    ),
  markAllNotificationsRead: () =>
    authedPostJson<{ updated: number }>("/v1/me/notifications/read-all", {}),

  // EPIC-008 STORY-808 — Terms of Service. Public read; auth-required
  // status + accept.
  getCurrentTos: () => get<TosVersion>("/v1/legal/tos"),
  getTosStatus: () => authedGet<TosAcceptanceStatus>("/v1/me/tos/status"),
  acceptTos: (tosVersionId: string) =>
    authedPostJson<{ accepted: boolean; acceptedAt: string }>(
      "/v1/me/tos/accept",
      { tosVersionId },
    ),
  placeOrder: (input: PlaceOrderInput) =>
    authedPostJson<OrderRecord>("/v1/orders", input),
  // 2026-05-09 — coalesce listMyOrders + listMyPositions alongside
  // listMyWallets / me. The same fan-out class (TradePanel,
  // MultiOutcomeTradePanel, MyOrdersAndPositions, /home, DashboardTabs,
  // WinAutoTrigger all hit these on mount) hit 429s. Founder symptom:
  // "$58.89 reserved in 0 orders" on TradePanel — listMyOrders 429'd
  // and the .catch silently emptied → reserved cents from wallets call
  // disagreed with the empty orders list. Coalescing dedupes the
  // concurrent calls so the panel sees the true list.
  // Note: status param is part of the cache key so listMyOrders('open')
  // and listMyOrders() coalesce to separate in-flight slots.
  listMyOrders: (status?: string) =>
    coalesce(`listMyOrders:${status ?? ""}`, () =>
      authedGet<OrderRecord[]>(
        `/v1/orders${status ? `?status=${status}` : ""}`,
      ),
    )(),
  cancelOrder: (orderId: string) =>
    authedDelete<{ id: string; status: string }>(`/v1/orders/${orderId}`),
  listMyPositions: coalesce("listMyPositions", () =>
    authedGet<PositionRecord[]>("/v1/me/positions"),
  ),
  // 2026-05-11 — authoritative sell-availability for a position.
  // ClosePositionModal calls this immediately before submit so the
  // disclosure (and the allLocked guard) uses the same number the
  // matcher would. Replaces the stale-listMyOrders reservation count
  // pattern. Not coalesced — every modal open / submit wants fresh
  // state.
  getPositionSellAvailability: (positionId: string) =>
    authedGet<{
      positionId: string;
      marketId: string;
      outcomeId: string;
      heldShares: string;
      reservedShares: string;
      availableShares: string;
      openSellOrders: Array<{
        id: string;
        sizeShares: string;
        filledShares: string;
        priceCents: number | null;
        orderType: string;
        status: string;
        createdAt: string;
      }>;
    }>(`/v1/me/positions/${positionId}/sell-availability`),
  // 2026-05-09 — request coalescing for /v1/me/wallets. Founder reported
  // 6× 429 (Too Many Requests) on /home because 7+ components (SiteHeader,
  // MobileBottomNav, /home page, VirtualResetBanner, TestFundsBanner,
  // TradePanel, MultiOutcomeTradePanel) all call listMyWallets on mount.
  // Even with the per-user throttle bumped, that's a wasteful fan-out
  // — every concurrent caller wants the same authoritative wallet list.
  // Coalesce: if a request is already in flight, every concurrent
  // listMyWallets() resolves the same promise. The cache is request-
  // scoped (cleared on resolve/reject), so a follow-up call after the
  // first one resolves gets a fresh fetch.
  listMyWallets: coalesce("listMyWallets", () =>
    authedGet<WalletRow[]>("/v1/me/wallets"),
  ),
  // ADR-046 — `/v1/me/balance` is the dedicated endpoint for the
  // derived free-balance + per-kind breakdown. Use this on surfaces
  // that show "what can I spend" — it returns a single derived
  // number so the frontend never subtracts across kinds. Existing
  // chrome surfaces (SiteHeader, MobileBottomNav, TradePanel, /home,
  // AsaasDepositPanel) already filter `wallets` by kind='cash' via
  // pickPrimaryCashWallet so the new locked_copy_allocation kind
  // is silently ignored; they can migrate to this endpoint at
  // leisure. Same coalesce shape as listMyWallets.
  getMyBalance: coalesce("getMyBalance", () =>
    authedGet<{
      free_balance_cents: string;
      by_kind: {
        cash: string;
        locked_orders: string;
        locked_positions: string;
        locked_copy_allocation: string;
        bonus: string;
        escrow: string;
      };
    }>("/v1/me/balance"),
  ),
  // EPIC-029 STORY-1805 — public fx-rate fetch. Powers the
  // converted-display path on /home position values and trade
  // panel notional. Public endpoint; no auth required.
  getFxRate: (quote: "USD" | "BRL" | "EUR" | "GBP" | "CAD" | "MXN" | "INR") =>
    get<{
      base: "USDC";
      quote: "USD" | "BRL" | "EUR" | "GBP" | "CAD" | "MXN" | "INR";
      rate: number;
      fetchedAt: string;
      source: string;
    }>(`/v1/fx-rates/${quote}`),
  // EPIC-021 STORY-373 — LGPD self-service. Backend phase 1 shipped
  // at #283: data export, 30-day-grace request, cancel.
  exportMyData: () => authedGet<Record<string, unknown>>("/v1/me/data-export"),
  requestAccountDeletion: (reason: string) =>
    authedPostJson<{
      deleteRequestedAt: string;
      graceEndsAt: string;
      revokedSessionCount: number;
    }>("/v1/me/account/delete-request", { reason }),
  cancelAccountDeletion: () =>
    authedPostJson<{ cancelledAt: string }>("/v1/me/account/delete-cancel", {}),
  // EPIC-005 STORY-501 — Popular Investor application + admin review.
  // Backend at #295. User endpoints under /v1/me/pi/applications;
  // admin endpoints under /v1/admin/pi-applications.
  submitPiApplication: (input: {
    motivation: string;
    trackRecord: string;
    externalLinks?: string[];
  }) => authedPostJson<PiApplication>("/v1/me/pi/applications", input),
  withdrawPiApplication: (applicationId: string) =>
    authedPostJson<PiApplication>(
      `/v1/me/pi/applications/${applicationId}/withdraw`,
      {},
    ),
  listMyPiApplications: () =>
    authedGet<PiApplication[]>("/v1/me/pi/applications"),
  // EPIC-005 STORY-507 — PI earnings dashboard. Returns the PI's own
  // commission-from-copies income (pending + lifetime + recent).
  // Pairs with apps/api/src/pi/pi-earnings.service.ts.
  getMyPiEarnings: () =>
    authedGet<{
      pendingCommissionCents: string;
      lifetimeAccruedCents: string;
      lifetimeClawbackCents: string;
      lifetimePaidOutCents: string;
      netEarnedCents: string;
      copierCount: number;
      recentAccruals: Array<{
        accruedCents: string;
        accruedAt: string;
        tradeId: string | null;
        marketSlug: string | null;
        copierUsername: string | null;
      }>;
    }>("/v1/me/pi/earnings"),
  listAdminPiApplications: (params?: {
    status?: PiApplicationStatus;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<AdminPiApplicationsPage>(
      `/v1/admin/pi-applications${qs ? `?${qs}` : ""}`,
    );
  },
  decidePiApplication: (
    applicationId: string,
    decision: "approved" | "rejected",
    decisionNote: string,
  ) =>
    authedPostJson<PiApplication>(
      `/v1/admin/pi-applications/${applicationId}/decide`,
      { decision, decisionNote },
    ),
  // EPIC-005 STORY-502 — public PI profile read.
  getPiProfile: (username: string) =>
    authedGet<PiPublicProfile>(`/v1/pi/profile/${username}`),
  // EPIC-005 STORY-509 — PI leaderboard.
  getPiLeaderboard: (limit?: number) =>
    authedGet<PiLeaderboardResponse>(
      `/v1/pi/leaderboard${limit ? `?limit=${limit}` : ""}`,
    ),
  // EPIC-005 STORY-503 — copy-trader subscriptions.
  listMyCopySubscriptions: (includeCancelled?: boolean) =>
    authedGet<CopySubscription[]>(
      `/v1/me/copy-subscriptions${includeCancelled ? "?includeCancelled=true" : ""}`,
    ),
  getMyCopySubscription: (id: string) =>
    authedGet<CopySubscription>(`/v1/me/copy-subscriptions/${id}`),
  subscribeToPi: (input: {
    piUsername: string;
    allocationCents: number;
    filters?: CopySubscriptionFilters;
  }) => authedPostJson<CopySubscription>("/v1/me/copy-subscriptions", input),
  updateCopySubscription: (
    id: string,
    input: {
      allocationCents?: number;
      filters?: CopySubscriptionFilters;
    },
  ) =>
    authedPatchJson<CopySubscription>(`/v1/me/copy-subscriptions/${id}`, input),
  pauseCopySubscription: (id: string) =>
    authedPostJson<CopySubscription>(
      `/v1/me/copy-subscriptions/${id}/pause`,
      {},
    ),
  resumeCopySubscription: (id: string) =>
    authedPostJson<CopySubscription>(
      `/v1/me/copy-subscriptions/${id}/resume`,
      {},
    ),
  // EPIC-005 STORY-503 visibility — list trades the worker placed
  // on this subscription's behalf, plus aggregate stats.
  listCopySubscriptionTrades: (id: string, limit?: number) => {
    const qs = limit ? `?limit=${limit}` : "";
    return authedGet<CopySubscriptionTrade[]>(
      `/v1/me/copy-subscriptions/${id}/trades${qs}`,
    );
  },
  getCopySubscriptionStats: (id: string) =>
    authedGet<CopySubscriptionStats>(`/v1/me/copy-subscriptions/${id}/stats`),
  // STORY-503 phase 2 — open + closed positions tabs on the activity page.
  listCopySubscriptionOpenPositions: (id: string) =>
    authedGet<CopySubscriptionOpenPosition[]>(
      `/v1/me/copy-subscriptions/${id}/positions/open`,
    ),
  listCopySubscriptionClosedPositions: (id: string) =>
    authedGet<CopySubscriptionClosedPosition[]>(
      `/v1/me/copy-subscriptions/${id}/positions/closed`,
    ),
  unsubscribeFromPi: (id: string) =>
    authedDelete<{ id: string; unsubscribedAt: string }>(
      `/v1/me/copy-subscriptions/${id}`,
    ),
  // EPIC-COPY STORY-510 — set the per-subscription stop-loss
  // override. `stopLossPct` accepts 5..95 (integer) to set a
  // tighter or looser threshold; `null` clears the override and
  // falls back to the platform_config default (seed value 50).
  setCopyStopLoss: (id: string, stopLossPct: number | null) =>
    authedPatchJson<{ stopLossPct: number | null }>(
      `/v1/me/copy-subscriptions/${id}/stop-loss`,
      { stopLossPct },
    ),
  // EPIC-019 STORY-367 — user-facing trade history + monthly statement.
  listMyTrades: (params?: {
    marketSlug?: string;
    side?: "buy" | "sell";
    occurredAfter?: string;
    occurredBefore?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.marketSlug) q.set("marketSlug", params.marketSlug);
    if (params?.side) q.set("side", params.side);
    if (params?.occurredAfter) q.set("occurredAfter", params.occurredAfter);
    if (params?.occurredBefore) q.set("occurredBefore", params.occurredBefore);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<MyTradesPage>(`/v1/me/trades${qs ? `?${qs}` : ""}`);
  },
  getMyMonthlyStatement: (monthsBack?: number) =>
    authedGet<MyMonthlyStatement>(
      `/v1/me/trades/statement${monthsBack ? `?monthsBack=${monthsBack}` : ""}`,
    ),
  // STORY-107 — TOTP 2FA lifecycle.
  startTwoFaSetup: () =>
    authedPostJson<{ otpauthUrl: string; secret: string }>(
      "/v1/me/2fa/setup",
      {},
    ),
  verifyAndEnableTwoFa: (code: string) =>
    authedPostJson<{ enabled: true; enabledAt: string }>(
      "/v1/me/2fa/verify-and-enable",
      { code },
    ),
  disableTwoFa: (code: string) =>
    authedPostJson<{ enabled: false }>("/v1/me/2fa/disable", { code }),
  grantTestFunds: () =>
    authedPostJson<TestFundsResponse>("/v1/me/test-funds", {}),
  // EPIC-013 STORY-1307 — closed-beta virtual money reset.
  requestVirtualReset: () =>
    authedPostJson<{
      minted: boolean;
      balanceBeforeCents: string;
      mintAmountCents: string;
      targetTotalCents: string;
      nextEligibleAt: string | null;
    }>("/v1/me/virtual-money-reset", {}),
  listMySessions: () => authedGet<SessionRecord[]>("/v1/me/sessions"),
  revokeSession: (sessionId: string) =>
    authedDelete<{ id: string; revoked: boolean }>(
      `/v1/me/sessions/${sessionId}`,
    ),
  // Admin — backend gates with AdminGuard; frontend should also gate
  // by user.role to avoid showing forms that will 403.
  createMarket: (input: CreateMarketInput) =>
    authedPostJson<{ id: string; slug: string; kind: string }>(
      "/v1/admin/markets",
      input,
    ),
  // EPIC-030 STORY-1801 — admin translation editor. The PATCH endpoint
  // already exists on the backend (STORY-903 editMarket), but until
  // this PR the only consumer was the build-time create flow, never
  // an edit-existing surface for translations.
  // Pass any subset of fields. Locale keys on title/description are
  // open Record<string,string> server-side (see EditMarketDto).
  editAdminMarket: (
    marketId: string,
    input: {
      title?: Record<string, string>;
      description?: Record<string, string>;
      imageUrl?: string | null;
      resolutionSource?: string;
      closesAt?: string;
      resolvesAt?: string;
      // STORY-903 outcome-sync — present when an admin reorders /
      // adds / removes outcomes on Tab 2 of the Market Creation Hub.
      // `id` is the existing outcome row to UPSERT (omitted for new
      // INSERTs); `sortOrder` is the 0-indexed position; `name` is
      // the i18n locale-keyed JSONB. Outcomes whose `id` is in DB
      // but missing here get DELETE'd — only allowed on draft +
      // pending_review markets per the editMarket safety guard.
      outcomes?: Array<{
        id?: string;
        sortOrder: number;
        name: Record<string, string>;
      }>;
    },
  ) =>
    authedPatchJson<{ marketId: string }>(
      `/v1/admin/markets/${marketId}`,
      input,
    ),
  adminEnableMarket: (marketId: string) =>
    authedPostJson<{ id: string; authoringStatus: string }>(
      `/v1/admin/markets/${marketId}/enable`,
      {},
    ),
  // EPIC-030 STORY-1801 — admin translation auto-suggest. Consumes
  // the global TranslationsService (STORY-1802 infrastructure).
  // Returns the translated text so the editor can pre-fill the
  // target-locale field; the admin still edits + saves separately
  // via editAdminMarket.
  suggestTranslation: (input: {
    sourceLocale: string;
    targetLocale: string;
    text: string;
  }) =>
    authedPostJson<{
      translated: string;
      providerUsed: string;
      costEstimateCents: number;
      fromCache: boolean;
    }>(`/v1/admin/translations/suggest`, input),
  // PR-M (editor UX polish, 2026-05-13 item b) — tag suggestion chips
  // for the Market Creation Hub Tab 1. Maps category slug -> topic ->
  // top 10 most-recent active tags. Falls through to platform-wide
  // most-recent tags when no topic matches.
  adminTagSuggestions: (categorySlug: string) =>
    authedGet<{
      tags: Array<{
        id: string;
        slug: string;
        name: string;
        topicSlug: string;
        topicName: string;
      }>;
    }>(
      `/v1/admin/tags/suggestions?categorySlug=${encodeURIComponent(categorySlug)}`,
    ),
  // Market Creation Hub Tab 2 (2026-05-13) — canonical disclaimer text
  // per locale. Backend reads from the same `disclaimers/<locale>.md`
  // files RegionComplianceService.validatePublish checks against, so
  // the chip can never drift from the publish-time gate.
  getRegionDisclaimer: (locale: string) =>
    authedGet<{ locale: string; text: string }>(
      `/v1/regions/admin/${encodeURIComponent(locale)}/disclaimer`,
    ),
  // Resolve a market. `outcome` is 'YES' / 'NO' / 'refund' for binary
  // markets, or an outcome UUID for multi_outcome.
  resolveMarket: (
    marketId: string,
    input: { outcome: string; reason: string; sourceUrl?: string },
  ) =>
    authedPostJson<{
      marketId: string;
      outcome: string;
      resolvedOutcomeId: string | null;
      settledPositions: number;
      totalPayoutCents: string;
    }>(`/v1/admin/markets/${marketId}/resolve`, input),
  // STORY-309c — multi-resolver workflow (Gate 3 blocker).
  proposeResolution: (
    marketId: string,
    input: { outcome: string; reason: string; sourceUrl?: string },
  ) =>
    authedPostJson<{
      proposalId: string;
      status: ResolutionProposalStatus;
      marketId: string;
    }>(`/v1/admin/markets/${marketId}/resolution-proposals`, input),
  voteOnProposal: (
    proposalId: string,
    input: { vote: "approve" | "reject"; reason?: string },
  ) =>
    authedPostJson<{
      proposalId: string;
      status: ResolutionProposalStatus;
      approveCount: number;
      rejectCount: number;
      settlement: {
        marketId: string;
        outcome: string;
        resolvedOutcomeId: string | null;
        settledPositions: number;
        totalPayoutCents: string;
      } | null;
    }>(`/v1/admin/resolution-proposals/${proposalId}/votes`, input),
  getResolutionProposal: (proposalId: string) =>
    authedGet<ResolutionProposalDetail>(
      `/v1/admin/resolution-proposals/${proposalId}`,
    ),
  listResolutionProposals: (params?: {
    status?: ResolutionProposalStatus;
    marketId?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.marketId) q.set("marketId", params.marketId);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<ResolutionProposalListItem[]>(
      `/v1/admin/resolution-proposals${qs ? `?${qs}` : ""}`,
    );
  },
  // ───────── STORY-309c PR-K — multi-resolver scaffold ─────────
  // Endpoints introduced by ResolutionMultiController. Cohabit with
  // the legacy voteOnProposal/getResolutionProposal/list above during
  // the Gate 3 prep transition window.
  submitResolutionVote: (
    proposalId: string,
    input: {
      vote: "approve" | "reject" | "abstain";
      altOutcome?: string;
      reasoning?: string;
    },
  ) =>
    authedPostJson<{
      ok: true;
      currentState: ResolutionMultiSnapshot;
      autoExecuted: boolean;
    }>(`/v1/admin/resolution-proposals/${proposalId}/vote`, input),
  castResolutionTiebreaker: (
    proposalId: string,
    input: {
      vote: "approve" | "reject";
      altOutcome?: string;
      reasoning?: string;
    },
  ) =>
    authedPostJson<{
      ok: true;
      currentState: ResolutionMultiSnapshot;
      autoExecuted: boolean;
    }>(`/v1/admin/resolution-proposals/${proposalId}/tiebreaker-vote`, input),
  listPendingResolutionProposals: (params?: {
    voterUserId?: "me" | "all";
    marketId?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.voterUserId) q.set("voterUserId", params.voterUserId);
    if (params?.marketId) q.set("marketId", params.marketId);
    const qs = q.toString();
    return authedGet<ResolutionMultiPendingSummary[]>(
      `/v1/admin/resolution-proposals/pending${qs ? `?${qs}` : ""}`,
    );
  },
  getResolutionProposalState: (proposalId: string) =>
    authedGet<ResolutionMultiDetail>(
      `/v1/admin/resolution-proposals/${proposalId}/state`,
    ),
  createInviteCodes: (input: {
    count: number;
    expiresAt?: string;
    label?: string;
  }) => authedPostJson<{ codes: string[] }>("/v1/admin/invites", input),
  listInviteCodes: (params?: {
    state?: "unused" | "used" | "expired" | "all";
    label?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.state) q.set("state", params.state);
    if (params?.label) q.set("label", params.label);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<InviteCodeRecord[]>(
      `/v1/admin/invites${qs ? `?${qs}` : ""}`,
    );
  },
  // ──────────────────────────────────────────────────────────────────
  // News Hub admin (PR 1 + PR 2 + PR 3 + PR 3b — full epic surface)
  // ──────────────────────────────────────────────────────────────────
  adminListNewsHubClusters: (params?: {
    status?: "pending" | "reviewed" | "skipped" | "archived" | "drafted";
    region?: string;
    primary_topic_slug?: string;
    tag_slug?: string;
    sort_by?: "created_at" | "article_count";
    sort_order?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.region) q.set("region", params.region);
    if (params?.primary_topic_slug)
      q.set("primary_topic_slug", params.primary_topic_slug);
    if (params?.tag_slug) q.set("tag_slug", params.tag_slug);
    if (params?.sort_by) q.set("sort_by", params.sort_by);
    if (params?.sort_order) q.set("sort_order", params.sort_order);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return authedGet<{ clusters: NewsHubClusterRow[] }>(
      `/v1/admin/news/clusters${qs ? `?${qs}` : ""}`,
    );
  },
  adminGetNewsHubCluster: (id: string) =>
    authedGet<NewsHubClusterDetail>(`/v1/admin/news/clusters/${id}`),
  adminSkipNewsHubCluster: (id: string, reason?: string) =>
    authedPostJson<{ id: string; status: string }>(
      `/v1/admin/news/clusters/${id}/skip`,
      reason ? { reason } : {},
    ),
  adminArchiveNewsHubCluster: (id: string) =>
    authedPostJson<{ id: string; status: string }>(
      `/v1/admin/news/clusters/${id}/archive`,
      {},
    ),
  adminIngestNewsNow: () =>
    authedPostJson<{
      regionsProcessed: number;
      articlesFetched: number;
      articlesInserted: number;
      clustersCreated: number;
      durationMs: number;
      errors: unknown[];
    }>(`/v1/admin/news/ingest-now`, {}),
  adminDraftFromCluster: (id: string) =>
    authedPostJson<{
      draftId: string;
      ok: boolean;
      error: string | null;
      costUsd: number;
    }>(`/v1/admin/news/clusters/${id}/draft`, {}),
  adminSummarizeCluster: (id: string) =>
    authedPostJson<{
      draftId: string;
      ok: boolean;
      error: string | null;
      costUsd: number;
    }>(`/v1/admin/news/clusters/${id}/summarize`, {}),
  // PR-J — direct-publish a cluster summary to the feed without
  // routing through the AI draft + DraftsController.publish flow. The
  // service builds the post body from the cluster topic + first 3
  // article links + the admin's summary text and creates a posts row
  // authored by the admin. Returns {postId}; the UI links to /post/:id.
  adminPublishClusterSummaryPost: (
    id: string,
    body: {
      summary: string;
      tagIds?: string[];
      visibility?: "public" | "followers";
    },
  ) =>
    authedPostJson<{ postId: string }>(
      `/v1/admin/news/clusters/${id}/publish-summary-post`,
      body,
    ),
  adminListRecentNewsHubArticles: (limit = 50) =>
    authedGet<{ articles: NewsHubArticleRow[] }>(
      `/v1/admin/news/recent?limit=${limit}`,
    ),

  // Market Creation Hub (2026-05-12) — unified read across content_drafts
  // (paths A + B, AI-drafted from cluster or competitor) AND markets
  // (path C, manually authored). Used by /admin/markets/hub.
  adminListCreationHub: (params?: {
    status?: string;
    source?: string;
    region?: string;
    topicSlug?: string;
    // v2 (2026-05-12) — search box + cluster filter for event-grouping.
    q?: string;
    clusterId?: string;
    // v3 (2026-05-13) — tag-slug filter + sort dropdown.
    tagSlug?: string;
    sort?: "created_desc" | "created_asc" | "title_asc" | "title_desc";
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.source) q.set("source", params.source);
    if (params?.region) q.set("region", params.region);
    if (params?.topicSlug) q.set("topicSlug", params.topicSlug);
    if (params?.tagSlug) q.set("tagSlug", params.tagSlug);
    if (params?.q) q.set("q", params.q);
    if (params?.clusterId) q.set("clusterId", params.clusterId);
    if (params?.sort) q.set("sort", params.sort);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return authedGet<{
      items: CreationHubItem[];
      total: number;
    }>(`/v1/admin/markets/creation-hub${qs ? `?${qs}` : ""}`);
  },

  adminListDrafts: (params?: {
    status?: "pending" | "reviewed" | "published" | "discarded";
    draft_type?: "market" | "post" | "translation";
    primary_topic_slug?: string;
    tag_slug?: string;
    sort_order?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.draft_type) q.set("draft_type", params.draft_type);
    if (params?.primary_topic_slug)
      q.set("primary_topic_slug", params.primary_topic_slug);
    if (params?.tag_slug) q.set("tag_slug", params.tag_slug);
    if (params?.sort_order) q.set("sort_order", params.sort_order);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return authedGet<{ drafts: ContentDraftRow[] }>(
      `/v1/admin/drafts${qs ? `?${qs}` : ""}`,
    );
  },
  adminGetDraft: (id: string) =>
    authedGet<ContentDraftRow>(`/v1/admin/drafts/${id}`),
  adminPublishDraft: (id: string, note?: string) =>
    // PR R-1 (2026-05-06) — `POST /v1/admin/drafts/:id/publish` is decorated
    // `@HttpCode(OK)` blanket but has 4 early-return shapes that surface
    // refusals as `{ error, …context }` with HTTP 200. The frontend used
    // to assume `downstream` always existed and crashed with
    // "Cannot read properties of undefined (reading 'slice')" when any
    // refusal path fired. Typing the response as a discriminated union
    // forces callers to branch on `.error` first; PR R-1.5 will convert
    // the refusal shapes to proper status codes per PR #1495 discipline.
    authedPostJson<AdminPublishDraftResult>(
      `/v1/admin/drafts/${id}/publish`,
      note ? { note } : {},
    ),
  adminDiscardDraft: (id: string, reason?: string) =>
    authedPostJson<{ id: string; status: string }>(
      `/v1/admin/drafts/${id}/discard`,
      reason ? { reason } : {},
    ),
  // 2026-05-13 — image-upload presigning. Returns a presigned PUT URL
  // the browser uses to upload directly to DO Spaces, plus the eventual
  // public URL the caller stores in markets.image_url / posts.image_url.
  adminPresignImageUpload: (input: {
    scope: "markets" | "posts" | "avatars";
    contentType: string;
    contentLength: number;
  }) =>
    authedPostJson<{
      uploadUrl: string;
      publicUrl: string;
      key: string;
      expiresAt: string;
      maxBytes: number;
      contentType: string;
    }>("/v1/admin/uploads/image-presigned", input),
  // 2026-05-13 — Market creation hub PR-C: server-side image
  // normalization pipeline (Polymarket/Kalshi-style hero images).
  // Distinct from adminPresignImageUpload (browser→Spaces direct):
  // here the API pod fetches/decodes/resizes/optimizes the bytes
  // before storing under granbay-media. Returns the final CDN URL
  // + image dimensions + payload size.
  adminUploadMediaImage: (input: {
    /** Either a multipart File (browser-side) or a remote sourceUrl. */
    file?: File;
    sourceUrl?: string;
    scope?: "markets" | "market-templates" | "market-events";
    /** Optional onProgress (0..100) — only fires on multipart uploads. */
    onProgress?: (pct: number) => void;
  }) =>
    uploadMediaImageImpl({
      path: "/v1/admin/media/images",
      file: input.file,
      sourceUrl: input.sourceUrl,
      scope: input.scope,
      onProgress: input.onProgress,
    }),
  // DELETE /v1/admin/media/images — pass the exact `key` returned by
  // the upload endpoint to remove the Spaces object. Returns 204.
  adminDeleteMediaImage: (key: string) =>
    authedRequestJson<void>("DELETE", "/v1/admin/media/images", { key }),
  // Market-bound convenience endpoint: upload + atomically write
  // markets.image_url in one round-trip. Server emits a
  // market.image_uploaded audit row.
  adminUploadMarketImage: (
    marketId: string,
    input: {
      file?: File;
      sourceUrl?: string;
      onProgress?: (pct: number) => void;
    },
  ) =>
    uploadMediaImageImpl({
      path: `/v1/admin/markets/${marketId}/image`,
      file: input.file,
      sourceUrl: input.sourceUrl,
      onProgress: input.onProgress,
    }),
  // DELETE /v1/admin/markets/:id/image — unsets markets.image_url and
  // best-effort deletes the underlying Spaces object. Audited.
  adminDeleteMarketImage: (marketId: string) =>
    authedDelete<void>(`/v1/admin/markets/${marketId}/image`),
  // STORY-R-2.3 (2026-05-12) — draft history (audit-log replay per draft).
  // Returns every admin_action_log row whose target_type='content_draft'
  // + target_id=:id, ascending by created_at. Used by the History tab in
  // the Drafts preview modal.
  adminGetDraftHistory: (id: string) =>
    authedGet<{
      events: Array<{
        id: string;
        action: string;
        aiAssisted: boolean;
        actorUserId: string | null;
        actorEmail: string | null;
        actorDisplayName: string | null;
        details: Record<string, unknown>;
        createdAt: string;
      }>;
    }>(`/v1/admin/drafts/${id}/history`),
  // PR R-2 (2026-05-06) — admin edits AI-generated content before publish.
  // Body: full replacement `content` JSON (per-field shape varies by
  // draft_type; backend doesn't enforce the inner shape — caller is
  // responsible for valid JSON). 404 if missing, 422 if not editable
  // (status not pending/reviewed), 400 if body invalid.
  adminUpdateDraftContent: (
    id: string,
    content: Record<string, unknown>,
    edit_reason?: string,
  ) =>
    authedPatchJson<ContentDraftRow>(
      `/v1/admin/drafts/${id}`,
      edit_reason ? { content, edit_reason } : { content },
    ),
  // PR R-2.1 (2026-05-06) — pre-flight compliance check. Modal calls
  // this on open + after every Save edits so admin sees what will
  // fail/warn BEFORE clicking Publish. No mutation; cheap.
  adminPreviewDraftCompliance: (id: string) =>
    authedGet<{
      regionCode: string;
      passed: boolean;
      errors: Array<{
        code: string;
        message: string;
        detail?: Record<string, unknown>;
      }>;
      warnings: Array<{
        code: string;
        message: string;
        detail?: Record<string, unknown>;
      }>;
      policyApplied: {
        regionCode: string;
        legalReviewStatus: string;
        requiredDisclaimerLocale: string;
      } | null;
    }>(`/v1/admin/drafts/${id}/compliance`),

  // PR E (2026-05-08) — region-exclusive market authoring. Uses
  // Granbay's per-country signal (sport calendar / political landscape
  // / news) to generate markets that Polymarket and Kalshi don't cover.
  // No competitor source — AI authors fresh markets from scratch.
  adminDraftRegionExclusive: (input: {
    region: "brazil" | "mexico" | "canada" | "india";
    locale: string;
    focus_topic?:
      | "politics"
      | "economics"
      | "sports"
      | "crypto"
      | "technology"
      | "culture"
      | "world"
      | "business"
      | "climate"
      | "entertainment";
    count?: number;
  }) =>
    authedPostJson<{
      draftIds: string[];
      costUsd: number;
      model: string | null;
    }>("/v1/admin/drafts/region-exclusive", input),

  adminListCompetitorMarkets: (params?: {
    source?: "polymarket" | "kalshi" | "manifold";
    status?: string;
    min_volume_usd?: number;
    // 2026-05-08 — slug-based topic + tag filters (U.1b junctions) +
    // sort controls. Response rows include `draftId` + `draftStatus`
    // so the admin UI can gray out the "Draft" button when a
    // content_drafts row already exists.
    topic_slug?: string;
    tag_slug?: string;
    // 2026-05-08 (PR D) — region filter. Codes match RegionCode.
    region?: RegionCode;
    sort_by?: "volume_24h_usd" | "scraped_at" | "created_at";
    sort_order?: "asc" | "desc";
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.source) q.set("source", params.source);
    if (params?.status) q.set("status", params.status);
    if (params?.min_volume_usd != null)
      q.set("min_volume_usd", String(params.min_volume_usd));
    if (params?.topic_slug) q.set("topic_slug", params.topic_slug);
    if (params?.tag_slug) q.set("tag_slug", params.tag_slug);
    if (params?.region) q.set("region", params.region);
    if (params?.sort_by) q.set("sort_by", params.sort_by);
    if (params?.sort_order) q.set("sort_order", params.sort_order);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ markets: CompetitorMarketRow[] }>(
      `/v1/admin/competitors/markets${qs ? `?${qs}` : ""}`,
    );
  },
  adminCompetitorScrapeNow: () =>
    authedPostJson<{
      totalFetched: number;
      totalInserted: number;
      totalUpdated: number;
      perSource: Array<{
        source: string;
        fetched: number;
        inserted: number;
        updated: number;
        errored: number;
        error: string | null;
      }>;
    }>(`/v1/admin/competitors/scrape-now`, {}),
  adminDraftFromCompetitor: (id: string) =>
    authedPostJson<{
      draftId: string;
      ok: boolean;
      error: string | null;
      costUsd: number;
    }>(`/v1/admin/competitors/markets/${id}/draft`, {}),

  // Market Creation Hub PR-D — competitor-import live search + seed.
  adminCompetitorImportSearch: (params?: {
    q?: string;
    competitor?: "polymarket" | "kalshi" | "all";
    region?: RegionCode;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.competitor) qs.set("competitor", params.competitor);
    if (params?.region) qs.set("region", params.region);
    if (params?.limit) qs.set("limit", String(params.limit));
    const search = qs.toString();
    return authedGet<{
      competitor: "polymarket" | "kalshi" | "all";
      region: RegionCode | null;
      query: string;
      results: CompetitorImportMarket[];
    }>(`/v1/admin/competitor-import/search${search ? `?${search}` : ""}`);
  },
  adminCompetitorImportSeedDraft: (body: {
    competitor: "polymarket" | "kalshi";
    externalId: string;
    payload: CompetitorImportMarket;
  }) =>
    authedPostJson<{
      draftId: string;
      marketRowSnapshot: Record<string, unknown>;
      deduped: boolean;
      importId: string;
    }>(`/v1/admin/competitor-import/seed-draft`, body),
  adminCompetitorImportRefreshCache: () =>
    authedPostJson<{ cleared: number }>(
      `/v1/admin/competitor-import/refresh-cache`,
      {},
    ),

  adminGetAiUsageToday: () =>
    authedGet<{
      date: string;
      totalUsd: number;
      byFeature: Record<string, { costUsd: number; callCount: number }>;
    }>(`/v1/admin/usage/today`),

  // STORY-1306 — validation metrics for the founder dashboard.
  getValidationMetrics: () => authedGet<ValidationMetrics>("/v1/admin/metrics"),
  // STORY-902 — fee revenue dashboard.
  getFeeRevenue: () => authedGet<FeeRevenue>("/v1/admin/fee-revenue"),
  // EPIC-020 STORY-906 — reserve & treasury snapshot.
  getTreasurySnapshot: () =>
    authedGet<TreasurySnapshotResponse>("/v1/admin/treasury/snapshot"),
  // EPIC-008 STORY-810b — postgres-backup CronJob health.
  getBackupHealth: () =>
    authedGet<BackupHealthResponse>("/v1/admin/backups/health"),

  // EPIC-012 STORY-913 — system-health dashboard probes.
  // /health is unauthed (public probe); /health/fx-rates same.
  // Both fall through the SiteHeader's regular fetch helper so
  // they use the same retry + JSON-decode behaviour as the rest
  // of the app.
  getApiHealth: () => get<ApiHealthResponse>("/health"),
  // 2026-05-12 — route is at /v1/health/fx-rates: the API's
  // setGlobalPrefix('v1') has an exclude list for the bare-root
  // health endpoints (`/`, `/health`, `/healthz`) but NOT for the
  // longer-path fx-rates endpoint, so this one gets the v1 prefix.
  // Older code called /health/fx-rates and 404'd silently, which
  // left the /admin/system-health FX card stuck in its error state.
  getFxRatesHealth: () => get<FxRatesHealthResponse>("/v1/health/fx-rates"),

  // EPIC-012 STORY-911 — admin region management (read-only Phase 1).
  listAdminRegions: () => authedGet<AdminRegionRow[]>("/v1/regions/admin/all"),

  // EPIC-012 STORY-916 — site-wide announcements (broadcast).
  listActiveAnnouncements: () =>
    get<AnnouncementRow[]>("/v1/announcements/active", { revalidate: 60 }),
  listAllAnnouncementsAdmin: () =>
    authedGet<AnnouncementRow[]>("/v1/admin/announcements"),
  createAnnouncement: (input: {
    body: string;
    title?: string | null;
    severity?: "info" | "warning" | "critical";
    startsAt?: string | null;
    endsAt?: string | null;
  }) => authedPostJson<AnnouncementRow>("/v1/admin/announcements", input),
  deleteAnnouncement: (id: string) =>
    authedDelete<void>(`/v1/admin/announcements/${id}`),

  // EPIC-016 STORY-356 — financial integrity invariant runs.
  getIntegrityLatest: () =>
    authedGet<IntegrityLatestRun | null>("/v1/admin/integrity/latest"),
  listIntegrityRuns: (limit = 25) =>
    authedGet<IntegrityRunSummary[]>(`/v1/admin/integrity/runs?limit=${limit}`),
  runIntegrityNow: () =>
    authedPostJson<IntegrityRunReport>("/v1/admin/integrity/run-now", {}),
  // EPIC-016 follow-up — STORY-357 correction proposals derived from
  // the latest invariant run. Read-only; Apply uses STORY-357 below.
  getProposedCorrections: () =>
    authedGet<IntegrityProposedCorrections>(
      "/v1/admin/integrity/proposed-corrections",
    ),
  // EPIC-029 STORY-1716 Part 2 follow-up — recent FX-feed stale-skip
  // events sourced from audit_events filtered by
  // action='platform.fx_rate_stale'. Renders as a "FX feed health"
  // card on /admin/integrity so the founder + admins can spot when
  // the upstream rate provider degrades.
  getFxStaleAlerts: (limit = 25) =>
    authedGet<{
      events: Array<{
        id: string;
        firedAt: string;
        payload: Record<string, unknown>;
      }>;
    }>(`/v1/admin/integrity/fx-stale-alerts?limit=${limit}`),
  // EPIC-016 STORY-357 — generic ledger correction. Used by the
  // /admin/integrity Apply buttons to clear historical drift after
  // admin reviews each proposal.
  applyWalletCorrection: (input: {
    userId: string;
    walletKind:
      | "cash"
      | "locked_orders"
      | "locked_positions"
      | "bonus"
      | "escrow"
      | "platform_fees";
    currency: "BRL" | "USD" | "USDC";
    deltaCents: string;
    refType:
      | "wallet_drift_correction"
      | "admin_correction"
      | "support_resolution";
    justificationText: string;
    idempotencyKey?: string;
    // STORY-309d phase 3 — payout-pause gate. Set when correcting a
    // payout from a market that may have an active dispute. Server
    // refuses with 409 if a dispute is still under review.
    marketId?: string;
    // STORY-309d phase 3 piece 3 — anchor a correction to an upheld
    // dispute. Server validates dispute exists, status='upheld', and
    // dispute.market_id matches marketId. Folded into audit_events
    // payload so the correction-to-dispute link is on the chain.
    disputeId?: string;
  }) =>
    authedPostJson<{
      txId: string;
      refId: string;
      appliedAt: string;
      deltaCents: string;
      walletKind: string;
      currency: string;
      targetUserId: string;
      idempotencyReplay: boolean;
    }>("/v1/admin/wallet/correct", input),

  // EPIC-027 STORY-692 + 698 — liquidity snapshots.
  getLiquidityForMarket: (slug: string) =>
    get<LiquiditySnapshot | null>(`/v1/markets/${slug}/liquidity`),
  listAdminLiquidity: () =>
    authedGet<LiquiditySnapshot[]>("/v1/admin/liquidity"),
  getLiquidityTrend: (marketId: string, windowHours = 24) =>
    authedGet<LiquiditySnapshot[]>(
      `/v1/admin/markets/${marketId}/liquidity/trend?windowHours=${windowHours}`,
    ),
  recordLiquiditySnapshot: () =>
    authedPostJson<{ scanned: number; persisted: number }>(
      "/v1/admin/liquidity/snapshot",
      {},
    ),

  // EPIC-027 STORY-690 + 691 — reward config + manual triggers.
  getMarketRewardConfig: (marketId: string) =>
    authedGet<MarketRewardConfig>(`/v1/admin/markets/${marketId}/rewards`),
  updateMarketRewardConfig: (
    marketId: string,
    input: {
      rewardsEnabled?: boolean;
      dailyRewardPoolCents?: number | null;
      maxSpreadCents?: number;
      minSizeShares?: number | null;
    },
  ) =>
    authedPatchJson<MarketRewardConfig>(
      `/v1/admin/markets/${marketId}/rewards`,
      input,
    ),
  topUpMarketRewardPool: (
    marketId: string,
    input: { amountCents: number; reason: string },
  ) =>
    authedPostJson<{
      marketId: string;
      addedCents: string;
      poolBalanceCents: string;
    }>(`/v1/admin/markets/${marketId}/rewards/topup`, input),
  triggerRewardsRefill: () =>
    authedPostJson<{
      poolEventsWritten: number;
      totalToppedUpCents: string;
      perMarket: Array<{
        marketId: string;
        marketSlug: string;
        addedCents: string;
        poolBalanceCents: string;
      }>;
    }>("/v1/admin/rewards/refill", {}),

  // EPIC-027 STORY-696 — user-facing rewards summary.
  getMyRewardsSummary: () =>
    authedGet<UserRewardsSummary>("/v1/me/rewards/summary"),
  // STORY-902b — fee-config read + edit. Read endpoint already
  // existed; the edit-side admin UI is what STORY-902b adds.
  getFeeConfig: () => authedGet<FeeConfig>("/v1/admin/fees"),
  updateFeeConfig: (input: {
    takerFeeBps?: number;
    makerFeeBps?: number;
    reason: string;
  }) =>
    authedPatchJson<{ takerFeeBps: number; makerFeeBps: number }>(
      "/v1/admin/fees",
      input,
    ),
  // EPIC-042 — admin platform config (post_max_chars, photo limits,
  // PI eligibility constants, etc.). Read returns every row grouped
  // by category for the /admin/config UI; write enforces type/range
  // server-side and 422s on violation.
  listAdminConfig: () =>
    authedGet<{ rows: AdminConfigRow[] }>("/v1/admin/config"),
  updateAdminConfig: (key: string, value: unknown) =>
    authedPatchJson<{ row: AdminConfigRow }>(
      `/v1/admin/config/${encodeURIComponent(key)}`,
      { value },
    ),
  // EPIC-041 part 3a — auto-share-to-feed toggle.
  getAutoShareToFeed: () =>
    authedGet<{ autoShareToFeed: boolean }>("/v1/me/auto-share-to-feed"),
  updateAutoShareToFeed: (autoShareToFeed: boolean) =>
    authedPatchJson<{ autoShareToFeed: boolean; lockedByPi: boolean }>(
      "/v1/me/auto-share-to-feed",
      { autoShareToFeed },
    ),

  // EPIC-029 STORY-1706 — fiat conversion spread config.
  listConversionSpreads: () =>
    authedGet<{ rows: ConversionSpreadRow[] }>("/v1/admin/conversion-spreads"),
  updateConversionSpread: (input: {
    currency: string;
    direction: "deposit" | "withdrawal";
    spreadBps: number;
    reason?: string;
  }) =>
    authedPatchJson<{
      currency: string;
      direction: "deposit" | "withdrawal";
      spreadBps: number;
      updatedAt: string;
    }>("/v1/admin/conversion-spreads", input),
  // EPIC-017 STORY-386b — provision N test/qa-bot accounts. Closed-
  // beta only (refused in production). Each account gets is_test=TRUE
  // (+ optionally is_qa_bot=TRUE) and a R$1,000 virtual-cash grant
  // so scenarios can place orders immediately.
  provisionBotAccounts: (input: {
    count: number;
    isQaBot?: boolean;
    password?: string;
  }) =>
    authedPostJson<{
      created: Array<{ userId: string; email: string; username: string }>;
      isQaBot: boolean;
      grantedCentsPerAccount: string;
    }>("/v1/admin/users/provision-bot-accounts", input),
  // EPIC-006 STORY-614 — bookmark / unbookmark a market. POST is
  // idempotent at the schema level via UNIQUE(user_id, market_id);
  // both endpoints return the new state so the optimistic UI can
  // confirm the round-trip without a separate refetch.
  addBookmark: (marketId: string) =>
    authedPostJson<{ bookmarked: boolean; bookmarkCount: number }>(
      `/v1/me/bookmarks/${marketId}`,
      {},
    ),
  removeBookmark: (marketId: string) =>
    authedDelete<{ bookmarked: boolean; bookmarkCount: number }>(
      `/v1/me/bookmarks/${marketId}`,
    ),
  // 2026-04-30 — lightweight ID-only listing for hydrating per-card
  // BookmarkToggleButton state in one round-trip.
  listBookmarkIds: () =>
    authedGet<{ marketIds: string[] }>("/v1/me/bookmarks/ids"),
  // 2026-04-30 — public users search.
  searchUsers: (q: string, limit = 20) => {
    const qs = new URLSearchParams({ q, limit: String(limit) }).toString();
    return get<{ users: UserSearchHit[] }>(`/v1/users/search?${qs}`);
  },
  // EPIC-006 STORY-615 — list the user's bookmarks for the dashboard
  // tab. Joined with market state so the tab renders price + status
  // + close-date without an N+1 fetch.
  listBookmarks: () => authedGet<BookmarkRow[]>("/v1/me/bookmarks"),
  // EPIC-021 STORY-907a — paginated admin user list.
  listAdminUsers: (params?: {
    search?: string;
    status?: "active" | "suspended" | "closed";
    role?: "user" | "creator" | "admin" | "resolver" | "support";
    kycTier?: number;
    has2fa?: boolean;
    isTest?: boolean;
    isQaBot?: boolean;
    signupAfter?: string;
    signupBefore?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.search) q.set("search", params.search);
    if (params?.status) q.set("status", params.status);
    if (params?.role) q.set("role", params.role);
    if (params?.kycTier !== undefined) q.set("kycTier", String(params.kycTier));
    if (params?.has2fa !== undefined) q.set("has2fa", String(params.has2fa));
    if (params?.isTest !== undefined) q.set("isTest", String(params.isTest));
    if (params?.isQaBot !== undefined) q.set("isQaBot", String(params.isQaBot));
    if (params?.signupAfter) q.set("signupAfter", params.signupAfter);
    if (params?.signupBefore) q.set("signupBefore", params.signupBefore);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<AdminUsersPage>(`/v1/admin/users${qs ? `?${qs}` : ""}`);
  },
  // EPIC-021 STORY-907b — admin user profile detail.
  getAdminUserDetail: (userId: string) =>
    authedGet<AdminUserDetail>(`/v1/admin/users/${userId}`),
  // EPIC-021 STORY-907c — admin user actions.
  suspendUser: (userId: string, reason: string) =>
    authedPatchJson<{
      userId: string;
      status: "suspended";
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/suspend`, { reason }),
  unsuspendUser: (userId: string, reason: string) =>
    authedPatchJson<{
      userId: string;
      status: "active";
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/unsuspend`, { reason }),
  setAdminUserRole: (
    userId: string,
    newRole: "user" | "creator" | "admin" | "resolver" | "support",
    reason: string,
  ) =>
    authedPatchJson<{
      userId: string;
      role: string;
      previousRole: string;
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/role`, { newRole, reason }),
  setAdminUserKycTier: (
    userId: string,
    newTier: 0 | 1 | 2 | 3,
    reason: string,
  ) =>
    authedPatchJson<{
      userId: string;
      kycTier: number;
      previousTier: number;
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/kyc-tier`, { newTier, reason }),
  resetAdminUser2fa: (userId: string, reason: string) =>
    authedPatchJson<{
      userId: string;
      twoFaEnabled: false;
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/2fa-reset`, { reason }),
  forceRevokeAdminUserSessions: (userId: string, reason: string) =>
    authedPatchJson<{
      userId: string;
      revokedSessionCount: number;
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/sessions/revoke-all`, { reason }),
  // EPIC-021 STORY-371 — per-user analytics drill-down.
  getAdminUserAnalytics: (userId: string) =>
    authedGet<AdminUserAnalytics>(`/v1/admin/users/${userId}/analytics`),
  listAdminUserTrades: (
    userId: string,
    params?: {
      marketSlug?: string;
      side?: "buy" | "sell";
      occurredAfter?: string;
      occurredBefore?: string;
      cursor?: string;
      limit?: number;
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.marketSlug) q.set("marketSlug", params.marketSlug);
    if (params?.side) q.set("side", params.side);
    if (params?.occurredAfter) q.set("occurredAfter", params.occurredAfter);
    if (params?.occurredBefore) q.set("occurredBefore", params.occurredBefore);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<AdminUserTradesPage>(
      `/v1/admin/users/${userId}/trades${qs ? `?${qs}` : ""}`,
    );
  },
  listAdminUserOrders: (
    userId: string,
    params?: {
      status?: "open" | "partially_filled" | "filled" | "cancelled" | "expired";
      marketSlug?: string;
      cursor?: string;
      limit?: number;
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.marketSlug) q.set("marketSlug", params.marketSlug);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<AdminUserOrdersPage>(
      `/v1/admin/users/${userId}/orders${qs ? `?${qs}` : ""}`,
    );
  },
  // EPIC-019 STORY-803 — paginated audit-events viewer.
  listAuditEvents: (params?: {
    actor?: string;
    action?: string;
    targetType?: string;
    targetId?: string;
    /** ISO timestamp; only events at or after this moment are returned. */
    occurredAfter?: string;
    /** ISO timestamp; only events strictly before this moment are returned. */
    occurredBefore?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.actor) q.set("actor", params.actor);
    if (params?.action) q.set("action", params.action);
    if (params?.targetType) q.set("targetType", params.targetType);
    if (params?.targetId) q.set("targetId", params.targetId);
    if (params?.occurredAfter) q.set("occurredAfter", params.occurredAfter);
    if (params?.occurredBefore) q.set("occurredBefore", params.occurredBefore);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<AuditEventsPage>(
      `/v1/admin/audit-events${qs ? `?${qs}` : ""}`,
    );
  },
  // EPIC-017 STORY-361 — test bot harness status. Backend gates with
  // both AdminGuard and TestBotGuard (NODE_ENV + feature flag), so a
  // 403 from this call is the normal "harness disabled" path.
  getTestBotStatus: () => authedGet<TestBotStatus>("/v1/admin/test-bot/status"),
  // EPIC-017 STORY-361 follow-up — runtime toggle. These three are NOT
  // gated by the TestBotGuard (so the page can render the "Enable now"
  // button while the harness is currently off). Production NODE_ENV
  // still hard-blocks runtime-enable inside the controller.
  getTestBotRuntimeState: () =>
    authedGet<TestBotRuntimeState>("/v1/admin/test-bot/runtime-state"),
  enableTestBotRuntime: () =>
    authedPostJson<TestBotRuntimeState>(
      "/v1/admin/test-bot/runtime-enable",
      {},
    ),
  disableTestBotRuntime: () =>
    authedPostJson<TestBotRuntimeState>(
      "/v1/admin/test-bot/runtime-disable",
      {},
    ),
  // EPIC-017 STORY-385 — admin test-bot control endpoints.
  // Backend lives behind triple-guard (Jwt + Admin + TestBot —
  // NODE_ENV-gated + TEST_BOT_ENABLED). 403 from any of these is
  // the normal "harness disabled" path; the page handles it.
  listTestBotScenarios: () =>
    authedGet<{ scenarios: TestBotScenarioName[] }>(
      "/v1/admin/test-bot/scenarios",
    ),
  startTestBotSession: (label: string) =>
    authedPostJson<TestBotStatus>("/v1/admin/test-bot/start", { label }),
  stopTestBotSession: () =>
    authedPostJson<TestBotStatus>("/v1/admin/test-bot/stop", {}),
  runTestBotScenario: (input: {
    name: TestBotScenarioName;
    marketId: string;
    takerUserId: string;
    makerUserId: string;
  }) =>
    authedPostJson<{
      scenario: TestBotScenarioName;
      placedOrderIds: string[];
    }>("/v1/admin/test-bot/run-scenario", input),
  bootstrapOrderbook: (input: {
    marketId: string;
    outcomeId: string;
    user1: string;
    user2: string;
    levels: number[];
    sharesPerLevel: number;
  }) =>
    authedPostJson<{ placedOrderIds: string[] }>(
      "/v1/admin/test-bot/bootstrap-orderbook",
      input,
    ),

  // EPIC-024 STORY-660 — help-desk admin queue.
  listAdminTickets: (params?: {
    status?: TicketStatus;
    category?: TicketCategory;
    cursor?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.category) qs.set("category", params.category);
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return authedGet<TicketListResponse>(`/v1/admin/tickets${suffix}`);
  },
  getAdminTicket: (id: string) =>
    authedGet<TicketDetailResponse>(`/v1/admin/tickets/${id}`),
  // EPIC-024 STORY-668 — SLA-met percentage card data.
  getTicketSlaMetric: () =>
    authedGet<TicketSlaMetric>("/v1/admin/tickets/sla-metric"),

  // EPIC-022 STORY-629 — admin grants/revokes the Verified Expert
  // badge. Note (10..1000 chars) lives in audit; never returned via
  // /me/* or public profile reads.
  setUserVerifiedExpert: (
    userId: string,
    input: { verified: boolean; note: string },
  ) =>
    authedPatchJson<{
      userId: string;
      verifiedExpert: boolean;
      appliedAt: string;
    }>(`/v1/admin/users/${userId}/verified-expert`, input),

  // EPIC-024 STORY-667 — admin response templates.
  listTicketTemplates: () =>
    authedGet<TicketTemplateRecord[]>("/v1/admin/ticket-templates"),
  createTicketTemplate: (input: { name: string; body: string }) =>
    authedPostJson<TicketTemplateRecord>("/v1/admin/ticket-templates", input),
  updateTicketTemplate: (id: string, input: { name?: string; body?: string }) =>
    authedPatchJson<TicketTemplateRecord>(
      `/v1/admin/ticket-templates/${id}`,
      input,
    ),
  deleteTicketTemplate: (id: string) =>
    authedDelete<{ id: string }>(`/v1/admin/ticket-templates/${id}`),
  replyAdminTicket: (id: string, body: string) =>
    authedPostJson<TicketMessage>(`/v1/admin/tickets/${id}/reply`, { body }),
  transitionAdminTicketStatus: (id: string, nextStatus: TicketStatus) =>
    authedPostJson<TicketRecord>(`/v1/admin/tickets/${id}/status`, {
      nextStatus,
    }),
  postAdminTicketNote: (id: string, body: string) =>
    authedPostJson<TicketAdminNote>(`/v1/admin/tickets/${id}/note`, { body }),

  // STORY-661 — user-facing help-desk surface.
  submitMyTicket: (input: {
    category: TicketCategory;
    subject: string;
    body: string;
    contactEmail?: string;
  }) => authedPostJson<TicketRecord>("/v1/me/tickets", input),
  listMyTickets: (params?: {
    status?: TicketStatus;
    cursor?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return authedGet<TicketListResponse>(`/v1/me/tickets${suffix}`);
  },
  getMyTicket: (id: string) =>
    authedGet<TicketDetailResponse>(`/v1/me/tickets/${id}`),
  replyMyTicket: (id: string, body: string) =>
    authedPostJson<TicketMessage>(`/v1/me/tickets/${id}/reply`, { body }),

  // EPIC-025 STORY-680 — admin bot reports queue.
  listAdminBotReports: (params?: {
    status?: BotReportStatus;
    severity?: BotReportSeverity;
    scenarioName?: string;
    realSignalOnly?: boolean;
    cursor?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.severity) qs.set("severity", params.severity);
    if (params?.scenarioName) qs.set("scenarioName", params.scenarioName);
    if (params?.realSignalOnly) qs.set("realSignalOnly", "true");
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return authedGet<BotReportListResponse>(`/v1/admin/bot-reports${suffix}`);
  },
  getAdminBotReport: (id: string) =>
    authedGet<BotReportRecord>(`/v1/admin/bot-reports/${id}`),
  transitionAdminBotReportStatus: (id: string, nextStatus: BotReportStatus) =>
    authedPostJson<BotReportRecord>(`/v1/admin/bot-reports/${id}/status`, {
      nextStatus,
    }),
  convertBotReportToTicket: (botReportId: string) =>
    authedPostJson<TicketRecord>(
      `/v1/admin/tickets/convert-from-bot-report/${botReportId}`,
      {},
    ),
  getAdminBotReportAnalysis: () =>
    authedGet<BotReportAnalysis>("/v1/admin/bot-reports/analysis"),

  // Stress / perf testing harness — admin trigger.
  runPerfTest: (input: { profile: PerfProfile; marketId: string }) =>
    authedPostJson<PerfRunResult>("/v1/admin/perf-testing/run", input),

  listQaBotPersonas: () =>
    authedGet<QaBotPersonasResponse>("/v1/admin/qa-bot/personas"),

  // EPIC-026 STORY-733 — per-merge test results dashboard.
  listQaBotRuns: (params?: {
    tier?: 1 | 2 | 3;
    outcome?: "running" | "ok" | "fail" | "skipped" | "errored";
    commitSha?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.tier) q.set("tier", String(params.tier));
    if (params?.outcome) q.set("outcome", params.outcome);
    if (params?.commitSha) q.set("commitSha", params.commitSha);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<QaBotRunRow[]>(
      `/v1/admin/qa-bot/runs${qs ? `?${qs}` : ""}`,
    );
  },
  getQaBotRun: (id: string) =>
    authedGet<QaBotRunRow>(`/v1/admin/qa-bot/runs/${id}`),

  // EPIC-017 STORY-386 — Class 2 harness master kill switch + state.
  // The verbatim confirmation phrase is intentionally not a constant
  // here — it ships in the UI prompt + the backend rejects mismatch
  // with a clear error so a typo doesn't accidentally enable.
  getQaBotStatus: () =>
    authedGet<QaBotStatusResponse>("/v1/admin/qa-bot/status"),
  enableQaBotHarness: (confirmationPhrase: string) =>
    authedPostJson<QaBotHarnessState>("/v1/admin/qa-bot/enable", {
      confirmationPhrase,
    }),
  // 2026-04-29 — admin override that bypasses BOTH the 7-day
  // cooldown AND the real-user-count gate. Different verbatim
  // phrase + a required reason string. Audit-logged with kind
  // `qa_bot.harness.force_enabled`.
  forceEnableQaBotHarness: (confirmationPhrase: string, reason: string) =>
    authedPostJson<QaBotHarnessState>("/v1/admin/qa-bot/force-enable", {
      confirmationPhrase,
      reason,
    }),
  disableQaBotHarness: (reason: string) =>
    authedPostJson<QaBotHarnessState>("/v1/admin/qa-bot/disable", {
      reason,
    }),

  // EPIC-026 STORY-722 — bot data reset. Dry-run reports counts;
  // actual reset requires the verbatim confirmation phrase.
  qaBotDataResetDryRun: () =>
    authedGet<QaBotDataResetCounts>("/v1/admin/qa-bot/data-reset/dry-run"),
  qaBotDataResetPerform: (confirmationPhrase: string) =>
    authedPostJson<QaBotDataResetResult>("/v1/admin/qa-bot/data-reset", {
      confirmationPhrase,
    }),

  // EPIC-026 STORY-724 — bot activity heatmap.
  getBotActionHeatmap: (params?: {
    facet?: "action" | "persona" | "tier";
    windowHours?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.facet) q.set("facet", params.facet);
    if (params?.windowHours !== undefined)
      q.set("windowHours", String(params.windowHours));
    const qs = q.toString();
    return authedGet<BotActionHeatmap>(
      `/v1/admin/qa-bot/actions/heatmap${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-026 STORY-728 — bot activity summary tile.
  getBotActionsSummary: (sinceMinutes?: number) => {
    const qs =
      sinceMinutes !== undefined ? `?sinceMinutes=${sinceMinutes}` : "";
    return authedGet<BotActionsSummary>(
      `/v1/admin/qa-bot/actions/summary${qs}`,
    );
  },

  // EPIC-026 STORY-725 — bot activity feed (polling-based; the
  // defer rationale called for WebSockets but a 5s-poll on the same
  // backend list endpoint covers closed-beta needs without the infra
  // overhead). Pass cursorId to walk into the past; omit for newest-
  // first.
  listBotActions: (params?: {
    persona?: string;
    action?: string;
    tier?: number;
    botUserId?: string;
    sinceMinutes?: number;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.persona) q.set("persona", params.persona);
    if (params?.action) q.set("action", params.action);
    if (params?.tier !== undefined) q.set("tier", String(params.tier));
    if (params?.botUserId) q.set("botUserId", params.botUserId);
    if (params?.sinceMinutes !== undefined)
      q.set("sinceMinutes", String(params.sinceMinutes));
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<BotActionListResult>(
      `/v1/admin/qa-bot/actions${qs ? `?${qs}` : ""}`,
    );
  },

  // EPIC-048 STORY-2006 — admin push-test send.
  adminSendTestPush: (userId: string, input: { title: string; body: string }) =>
    authedPostJson<{
      targeted: number;
      sent: number;
      revoked: number;
      delivered: boolean;
    }>(`/v1/admin/users/${userId}/push-test`, input),

  // EPIC-048 STORY-2006 follow-up — admin broadcast push.
  // Two-step UX: first call without `confirm` returns `confirmRequired`
  // + the resolved count if above the threshold; second call with
  // `confirm: true` actually fires.
  adminSendBroadcastPush: (input: {
    title: string;
    body: string;
    audience:
      | { kind: "all" }
      | { kind: "region"; regionCode: string }
      | { kind: "role"; role: string };
    confirm?: boolean;
  }) =>
    authedPostJson<{
      targeted: number;
      sent: number;
      revoked: number;
      audience: typeof input.audience;
      confirmRequired?: true;
    }>(`/v1/admin/announcements/push`, input),

  // EPIC-008 STORY-827 — admin downloads a signed audit-events export.
  // Returned blob is JSON. Caller saves it off-cluster (DO Spaces / S3
  // / cold-storage). The in-cluster `audit.exported` row is the
  // cross-reference written by the backend.
  adminExportAuditEvents: (params: { from: string; to: string }) => {
    const q = new URLSearchParams();
    q.set("from", params.from);
    q.set("to", params.to);
    return authedGet<{
      range: { from: string; to: string };
      exportedAt: string;
      rowCount: number;
      rows: unknown[];
      signature: string;
      signatureAlgorithm: "HMAC-SHA256";
    }>(`/v1/admin/audit-events/export?${q.toString()}`);
  },

  // EPIC-031 STORY-1100 — builder profile signup + read.
  applyBuilder: (input: {
    slug: string;
    displayName: string;
    intendedAudience?: string;
    payoutMethod?: string;
    payoutAddress?: string;
  }) => authedPostJson<BuilderRecord>("/v1/builders/apply", input),
  getMyBuilder: () =>
    authedGet<{ builder: BuilderRecord | null }>("/v1/builders/me"),
  // EPIC-031 STORY-1103 — public builder leaderboard.
  getBuildersLeaderboard: async (params?: { limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    const res = await fetch(
      `${getApiBase()}/v1/builders/leaderboard${qs ? `?${qs}` : ""}`,
      { cache: "no-store" },
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new ApiError(
        res.status,
        "fetch_failed",
        txt || `HTTP ${res.status}`,
      );
    }
    return (await res.json()) as {
      builders: Array<{
        id: string;
        slug: string;
        displayName: string;
        approvedAt: string | null;
        attributedVolumeCents: string;
        attributedTradeCount: number;
        attributedUniqueUsers: number;
      }>;
      phase: "placeholder" | "with_attribution";
    };
  },

  // Public builder profile lookup (only approved rows surface; 404 otherwise).
  // Returns the redacted projection — no payout fields, no codeHex.
  getBuilderBySlug: async (slug: string) => {
    const res = await fetch(
      `${getApiBase()}/v1/builders/${encodeURIComponent(slug)}`,
      { cache: "no-store" },
    );
    if (res.status === 404) return null;
    if (!res.ok) {
      const txt = await res.text();
      throw new ApiError(
        res.status,
        "fetch_failed",
        txt || `HTTP ${res.status}`,
      );
    }
    return (await res.json()) as {
      builder: {
        id: string;
        slug: string;
        displayName: string;
        intendedAudience: string | null;
        createdAt: string;
      };
    };
  },

  // EPIC-031 STORY-1100 — admin builder review queue.
  listAdminBuilders: (params?: {
    status?: "pending" | "approved" | "rejected";
    cursor?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.cursor) q.set("cursor", params.cursor);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    const qs = q.toString();
    return authedGet<{ builders: BuilderRecord[]; nextCursor: string | null }>(
      `/v1/admin/builders${qs ? `?${qs}` : ""}`,
    );
  },
  reviewBuilder: (
    builderId: string,
    input: { decision: "approved" | "rejected"; notes?: string },
  ) =>
    authedPostJson<BuilderRecord>(
      `/v1/admin/builders/${builderId}/review`,
      input,
    ),

  // EPIC-048 STORY-2004 — web-push subscription lifecycle.
  // The public-key call is unauth so that the toggle can render its
  // "not configured" state on the signed-out variants of the settings
  // page (admin previews, etc.) without forcing a 401.
  getPushPublicKey: async (): Promise<{ publicKey: string } | null> => {
    try {
      const res = await fetch(`${getApiBase()}/v1/push/public-key`, {
        cache: "no-store",
      });
      if (!res.ok) return null;
      return (await res.json()) as { publicKey: string };
    } catch {
      return null;
    }
  },
  subscribePush: (input: {
    endpoint: string;
    p256dhKey: string;
    authKey: string;
    userAgent?: string;
  }) =>
    authedPostJson<{ id: string; endpoint: string; createdAt: string }>(
      "/v1/me/push-subscriptions",
      input,
    ),
  unsubscribePush: (endpoint: string) =>
    authedFetch<{ ok: true }>((token) =>
      fetch(`${getApiBase()}/v1/me/push-subscriptions`, {
        method: "DELETE",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({ endpoint }),
        cache: "no-store",
      }),
    ),

  // PR-D2 (Market Creation Hub, 2026-05-13) — free image-source
  // suggester. Returns 4-8 candidate photo URLs per market across
  // Wikipedia + CoinGecko + logo.dev + Pexels + Openverse + DDG.
  // UI wiring lands in a follow-up PR; this method exists so the
  // Hub editor can call it.
  adminSuggestImages: (input: { title: string; entity?: string }) =>
    authedGet<{ candidates: ImageCandidate[] }>(
      `/v1/admin/markets/image-suggestions?title=${encodeURIComponent(input.title)}${
        input.entity ? `&entity=${encodeURIComponent(input.entity)}` : ""
      }`,
    ),
  // 2026-05-13 — Admin GET /full endpoint (wired backend-side via
  // PR-B but the lib helper was never added, so MarketEditor's
  // fallback threw ApiError 503 -> "Unexpected server error" after
  // Smart Draft redirect. Typed as unknown — MarketEditor casts
  // via FullMarketResponse-shaped helper.
  adminGetMarketFull: (marketId: string) =>
    authedGet<unknown>(
      `/v1/admin/markets/${encodeURIComponent(marketId)}/full`,
    ),
  // Phase 1 — admin HEAD-probe a resolution_source URL. Returns the
  // last-known check status; cached server-side for 30 min so repeat
  // checks during editor focus + blur do not re-fetch the origin.
  adminCheckUrl: (url: string) =>
    authedGet<{ url: string; status: string; httpCode?: number; error?: string }>(
      `/v1/admin/markets/check-url?url=${encodeURIComponent(url)}`,
    ),
  // Migration 0166 — admin updates a single outcome's icon override.
  // Body `{ iconUrl: null }` clears the override and falls back to the
  // auto-detect chain in apps/web/lib/outcome-icons.ts. Non-null
  // accepts either a public URL ("https://...") or an "emoji:<glyph>"
  // sentinel (library picker).
  adminUpdateOutcomeIcon: (input: {
    marketId: string;
    outcomeId: string;
    iconUrl: string | null;
  }) =>
    authedPatchJson<{ outcomeId: string; iconUrl: string | null }>(
      `/v1/admin/markets/${encodeURIComponent(input.marketId)}/outcomes/${encodeURIComponent(input.outcomeId)}/icon`,
      { iconUrl: input.iconUrl },
    ),
};

export interface BotActionHeatmapCell {
  facetValue: string | null;
  bucket: string;
  count: number;
}

export interface BotActionHeatmap {
  facetField: "action" | "persona_name" | "tier";
  windowHours: number;
  cells: BotActionHeatmapCell[];
}

export interface BotActionsSummary {
  windowMinutes: number;
  totalActions: number;
  byAction: Array<{ action: string; count: number }>;
  byPersona: Array<{ persona_name: string; count: number }>;
  byTier: Array<{ tier: number; count: number }>;
}

export interface BotActionRow {
  id: string;
  botUserId: string | null;
  personaName: string | null;
  tier: number | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface BotActionListResult {
  actions: BotActionRow[];
  nextCursor: string | null;
}

// EPIC-031 STORY-1100 — builder profile shape.
export interface BuilderRecord {
  id: string;
  userId: string;
  slug: string;
  displayName: string;
  intendedAudience: string | null;
  payoutMethod: string | null;
  payoutAddress: string | null;
  status: "pending" | "approved" | "rejected";
  reviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  // STORY-1101 — bytes32 attribution code, hex-encoded. Null on
  // pending / rejected rows; populated post-approve.
  codeHex: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PerfProfile = "light" | "medium" | "heavy";

export interface PerfProfileSpec {
  ordersPerSecond: number;
  concurrentBots: number;
  durationSeconds: number;
}

export interface PerfRunResult {
  profile: PerfProfile;
  spec: PerfProfileSpec;
  startedAt: string;
  endedAt: string;
  totalRequests: number;
  successCount: number;
  errorCount: number;
  errorRate: number;
  achievedRps: number;
  latencyMs: {
    p50: number;
    p95: number;
    p99: number;
    max: number;
    mean: number;
  };
  dbConnections: {
    atStart: number;
    atEnd: number;
    peak: number;
  };
}

export type PersonaBehaviorProfile =
  | "balanced"
  | "heavy_buyer"
  | "sparse"
  | "one_sided";

export interface PersonaConfig {
  name: string;
  description: string;
  locale: "en" | "pt" | "es";
  country: string;
  currency: "USD" | "BRL" | "EUR";
  timezone: string;
  behavior: PersonaBehaviorProfile;
  tiers: { tier1: boolean; tier2: boolean; tier3: boolean };
}

export interface QaBotPersonasResponse {
  names: string[];
  personas: Record<string, PersonaConfig>;
}

// EPIC-012 STORY-911 — admin region management.
export interface AdminRegionRow {
  code: string;
  // Migration 0006 (regions table) declares `name JSONB` —
  // `{"en":"Brazil","pt":"Brasil","es":"Brasil"}`. The legacy type
  // was wrong (`string`), which silently passed type-checking and
  // crashed at render time with "Objects are not valid as a React
  // child" when /admin/regions tried `{r.name}`.
  name: I18nText;
  default_locale: string;
  supported_locales: string[];
  default_currency: string;
  supported_currencies: string[];
  payment_methods: string[];
  payment_routing: Record<string, unknown> | null;
  blocked: boolean;
  required_kyc_tier: number | null;
  kyc_provider: string | null;
  countries: string[];
}

// EPIC-012 STORY-916 — site-wide announcements.
export interface AnnouncementRow {
  id: string;
  severity: "info" | "warning" | "critical";
  body: string;
  title: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// EPIC-012 STORY-913 — system-health dashboard.
export interface ApiHealthResponse {
  status: "ok" | "degraded";
  checks: Record<string, string>;
}

export interface FxRatesHealthResponse {
  provider: string;
  rates: Record<string, { rate: number; fetchedAt: string }>;
  errors?: Record<string, string>;
}

// EPIC-026 STORY-733 — per-merge test results dashboard.
export interface QaBotRunRow {
  id: string;
  startedAt: string;
  completedAt: string | null;
  tier: 1 | 2 | 3;
  commitSha: string | null;
  scenarioId: string | null;
  personaId: string | null;
  outcome: "running" | "ok" | "fail" | "skipped" | "errored";
  errorsCount: number;
  reportsGenerated: number;
  skippedReason: string | null;
  metadata: Record<string, unknown> | null;
}

// EPIC-026 STORY-722 — bot data reset shapes. Counts = pre-reset
// pre-flight; Result = post-reset summary including actual mutation
// counts (which match counts unless rows raced in between).
export interface QaBotDataResetCounts {
  testUsers: number;
  openOrders: number;
}

export interface QaBotDataResetResult extends QaBotDataResetCounts {
  ordersCancelled: number;
  usersSoftDeleted: number;
}

export const QA_BOT_DATA_RESET_PHRASE = "RESET BOT DATA";

// EPIC-017 STORY-386 — Class 2 harness state shapes.
export interface QaBotHarnessState {
  enabled: boolean;
  lastEnabledAt: string | null;
  lastEnabledBy: string | null;
  lastDisabledAt: string | null;
  lastDisabledReason: string | null;
}

export interface QaBotCooldownStatus {
  inCooldown: boolean;
  cooldownEndsAt: string | null;
}

export interface QaBotStatusResponse {
  state: QaBotHarnessState;
  cooldown: QaBotCooldownStatus;
  realUsers: number;
}

export const QA_BOT_ENABLE_PHRASE = "I confirm enabling production QA bots";
// 2026-04-29 — distinct phrase for the force-enable override. The
// override exists because the cron's "real user" definition (NOT
// is_test) counts closed-beta invitees as real users even though
// they aren't real users in the product sense. Different phrase
// from the safe path so an admin can't autopilot through.
export const QA_BOT_FORCE_ENABLE_PHRASE =
  "I am overriding the QA bot safeguards";

// EPIC-024 STORY-660 — help-desk types.
export type TicketCategory =
  | "account"
  | "trading"
  | "payment"
  | "kyc"
  | "bug"
  | "feature_request"
  | "other";

export type TicketStatus =
  | "open"
  | "in_progress"
  | "waiting_user"
  | "resolved"
  | "closed";

export type TicketMessageAuthorRole = "user" | "admin" | "system";

export interface TicketRecord {
  id: string;
  userId: string | null;
  category: TicketCategory;
  status: TicketStatus;
  subject: string;
  body: string;
  contactEmail: string | null;
  slaBreachAt: string | null;
  lastAdminResponseAt: string | null;
  sourceBotReportId: string | null;
  // EPIC-024 STORY-668 — captured on first transition to terminal status.
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// EPIC-024 STORY-668 — SLA-met percentage over the last 30 days.
export interface TicketSlaMetric {
  windowDays: number;
  totalResolved: number;
  resolvedWithinSla: number;
  percentage: number | null;
}

// EPIC-024 STORY-667 — admin response template row.
export interface TicketTemplateRecord {
  id: string;
  name: string;
  body: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  id: string;
  ticketId: string;
  authorUserId: string | null;
  authorRole: TicketMessageAuthorRole;
  body: string;
  attachments: unknown[];
  createdAt: string;
}

export interface TicketAdminNote {
  id: string;
  ticketId: string;
  authorAdminId: string;
  body: string;
  createdAt: string;
}

export interface TicketListResponse {
  tickets: TicketRecord[];
  nextCursor: string | null;
}

export interface TicketDetailResponse {
  ticket: TicketRecord;
  messages: TicketMessage[];
  adminNotes?: TicketAdminNote[];
}

// EPIC-025 STORY-680 — bot bug reports types.
export type BotReportSeverity = "low" | "med" | "high" | "critical";

export type BotReportStatus =
  | "new"
  | "triaged"
  | "known_issue"
  | "converted_to_ticket"
  | "fixed_verified"
  | "false_positive";

export interface BotReportRecord {
  id: string;
  scenarioName: string;
  scenarioStep: string | null;
  severity: BotReportSeverity;
  status: BotReportStatus;
  stackTraceHash: string;
  stackTraceFull: string | null;
  screenshots: string[];
  userState: Record<string, unknown>;
  requestPayload: Record<string, unknown>;
  responsePayload: Record<string, unknown>;
  frequencyCount: number;
  // EPIC-026 STORY-732 — true when frequency_count crossed the
  // real-signal threshold (3+ repeats). Distinct from severity.
  isRealSignal: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  lastAdminActionAt: string | null;
  lastAdminActionBy: string | null;
  ticketId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BotReportListResponse {
  reports: BotReportRecord[];
  nextCursor: string | null;
}

export interface BotReportAnalysis {
  totals: {
    allTime: number;
    last24h: number;
    last7d: number;
    open: number;
    triaged: number;
    convertedToTicket: number;
    fixedVerified: number;
    falsePositive: number;
    knownIssue: number;
  };
  severityDistribution: Array<{ severity: string; count: number }>;
  topScenarios: Array<{
    scenarioName: string;
    distinctReports: number;
    totalFrequency: number;
    maxSeverity: string;
  }>;
  freshRegressionsLast24h: Array<{
    reportId: string;
    scenarioName: string;
    severity: string;
    stackTraceHash: string;
    firstSeenAt: string;
    frequencyCount: number;
  }>;
  triageLatencyHours: {
    avg: number | null;
    p95: number | null;
    sampleSize: number;
  };
  // EPIC-026 STORY-726 — extended analysis fields. Optional because
  // older deploys may serve a payload without them; UI gates rendering
  // on presence.
  trendDaily?: Array<{
    day: string;
    newReports: number;
    totalFrequency: number;
  }>;
  crossScenarioHashes?: Array<{
    stackTraceHash: string;
    distinctScenarios: number;
    totalFrequency: number;
    maxSeverity: string;
  }>;
}

// STORY-902b — fee-config read shape. Singleton row;
// `updateFeeConfig` returns just the rates (not the timestamps).
export interface FeeConfig {
  takerFeeBps: number;
  makerFeeBps: number;
  updatedAt: string;
  updatedBy: string | null;
}

// EPIC-029 STORY-1706 — per-(currency, direction) fiat conversion
// spread row. Returned by GET /v1/admin/conversion-spreads.
export interface ConversionSpreadRow {
  currency: string;
  direction: "deposit" | "withdrawal";
  spreadBps: number;
  updatedAt: string;
  updatedByUserId: string | null;
}

// EPIC-027 STORY-692 + 698 — liquidity snapshot shape.
export interface LiquiditySnapshot {
  marketId: string;
  marketSlug: string;
  snapshotAt: string;
  bestBidCents: number | null;
  bestAskCents: number | null;
  midpointCents: number | null;
  spreadCents: number | null;
  depth1cShares: string | null;
  depth3cShares: string | null;
  depth5cShares: string | null;
  depth10cShares: string | null;
}

// EPIC-027 STORY-696 — user rewards summary.
/**
 * EPIC-029 STORY-1718 Part 2 — `/v1/me/deposit/init` +
 * `/v1/me/withdrawal/init` response shape. Mirrors the backend
 * `PaymentMintResult` interface in `apps/api/src/payments/
 * payment-gateway.types.ts`.
 *
 * `widgetUrl` and `pixDeposit` are mutually exclusive — exactly one
 * is non-null per response. Widget-style adapters (Transak) populate
 * `widgetUrl`; direct-PSP adapters (AsaasAdapter PIX) populate
 * `pixDeposit`.
 */
export interface PixDepositMintResponse {
  externalPaymentId: string;
  /** Base64-encoded PNG of the PIX QR code. Null on PSP fetch error. */
  qrCodePayloadBase64: string | null;
  /** "Copia e cola" string the user can paste in their bank app. */
  pixCopiaCola: string | null;
  /** PSP-hosted invoice URL — fallback if QR fetch failed. */
  invoiceUrl: string | null;
  /** BRL major units (e.g. 50.0 = R$50). */
  brlAmount: number;
  /** ISO timestamp of charge expiry. Modal renders a countdown. */
  expiresAt: string;
}

export interface DepositInitResponse {
  widgetUrl: string | null;
  pixDeposit: PixDepositMintResponse | null;
  environment: "sandbox" | "production";
  testMode: boolean;
  adapterId: string;
}

export interface UserRewardsSummary {
  lifetimeCents: string;
  last30dCents: string;
  perMarket: Array<{
    marketId: string;
    marketSlug: string;
    lifetimeCents: string;
  }>;
  recentDistributions: Array<{
    distributionDate: string;
    marketSlug: string;
    payoutCents: string;
  }>;
}

// EPIC-027 STORY-690 + 691 — admin reward config shape.
export interface MarketRewardConfig {
  marketId: string;
  marketSlug: string;
  rewardsEnabled: boolean;
  dailyRewardPoolCents: string | null;
  maxSpreadCents: number;
  minSizeShares: string | null;
  rewardPoolCents: string;
}

// EPIC-008 STORY-810b — postgres-backup health shape.
export type BackupHealthStatus = "healthy" | "stale" | "critical" | "no-data";

export interface BackupHealthRun {
  env: "staging" | "production";
  status: BackupHealthStatus;
  lastSuccessAt: string | null;
  lastRunAt: string | null;
  lastRunSuccess: boolean | null;
  lastErrorMessage: string | null;
  hoursSinceLastSuccess: number | null;
}

// EPIC-016 STORY-356 — financial integrity invariants.
export type IntegrityInvariantName =
  | "ledger_zero_sum"
  | "wallet_vs_ledger"
  | "reservation_accounting"
  | "per_user_reserves";

export type IntegrityTriggeredBy = "cron" | "admin" | "test";

export interface IntegrityInvariantSummary {
  invariantName: IntegrityInvariantName;
  violationCount: number | null;
  violationSummary: unknown[];
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export interface IntegrityLatestRun {
  runId: string;
  triggeredBy: IntegrityTriggeredBy;
  startedAt: string;
  completedAtMax: string | null;
  healthy: boolean;
  invariants: IntegrityInvariantSummary[];
}

export interface IntegrityRunSummary {
  runId: string;
  triggeredBy: IntegrityTriggeredBy;
  startedAt: string;
  completedAt: string | null;
  totalViolations: number;
  healthy: boolean;
}

export interface IntegrityRunReport {
  runId: string;
  triggeredBy: IntegrityTriggeredBy;
  healthy: boolean;
  invariants: Array<{
    invariantName: IntegrityInvariantName;
    violationCount: number;
    violationSummary: unknown[];
    startedAt: string;
    completedAt: string;
    errorMessage: string | null;
  }>;
}

// EPIC-016 follow-up — STORY-357 correction proposer.
export interface IntegrityProposedCorrections {
  runId: string | null;
  actionable: Array<{
    invariantName: IntegrityInvariantName;
    targetUserId: string;
    walletKind: "locked_orders" | "cash" | "locked_positions";
    currency: "BRL";
    deltaCents: string;
    summary: string;
  }>;
  manualReview: Array<{
    invariantName: IntegrityInvariantName;
    reason: string;
    sample: unknown;
  }>;
}

export interface BackupHealthResponse {
  generatedAt: string;
  runs: BackupHealthRun[];
}

// EPIC-020 STORY-906 — reserve & treasury snapshot shape.
export interface TreasurySnapshot {
  currency: string;
  userBalances: {
    cashCents: string;
    lockedOrdersCents: string;
    lockedPositionsCents: string;
    bonusCents: string;
    escrowCents: string;
    totalCents: string;
    userCount: number;
  };
  testBalances: {
    cashCents: string;
    lockedOrdersCents: string;
    lockedPositionsCents: string;
    totalCents: string;
    testUserCount: number;
  };
  platformFeesCents: string;
  systemMintCents: string;
  netDriftCents: string;
}

export interface TreasurySnapshotResponse {
  generatedAt: string;
  snapshots: TreasurySnapshot[];
}

export interface FeeRevenue {
  generatedAt: string;
  totals: {
    lifetimeFeeCents: string;
    tradeCount: number;
    avgFeePerTradeCents: string;
  };
  currentRates: {
    takerFeeBps: number;
    makerFeeBps: number;
  };
  perMarket: Array<{
    marketId: string;
    marketSlug: string;
    tradeCount: number;
    feeTotalCents: string;
  }>;
  recentTrades: Array<{
    tradeId: string;
    occurredAt: string;
    marketSlug: string;
    feeCents: string;
    sizeShares: string;
    priceCents: number;
  }>;
}

export interface AdminUserRow {
  id: string;
  email: string;
  username: string;
  locale: string;
  country: string | null;
  role: string;
  status: string;
  kycStatus: string;
  kycTier: number;
  twoFaEnabled: boolean;
  isTest: boolean;
  isQaBot: boolean;
  createdAt: string;
}

export interface AdminUsersPage {
  users: AdminUserRow[];
  nextCursor: string | null;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    username: string;
    locale: string;
    country: string | null;
    role: string;
    status: string;
    kycStatus: string;
    kycTier: number;
    twoFaEnabled: boolean;
    twoFaEnabledAt: string | null;
    isTest: boolean;
    strictMarketOrders: boolean;
    // EPIC-022 STORY-629 — admin-curated badge.
    verifiedExpert: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  activity: {
    lifetimeTradeCount: number;
    lifetimeVolumeCents: string;
    lifetimeFeesPaidCents: string;
    openOrdersCount: number;
    openOrdersReservedCents: string;
    positionsCount: number;
  };
  wallets: Array<{
    kind: string;
    currency: string;
    balanceCents: string;
  }>;
}

// EPIC-021 STORY-371 — admin per-user analytics drill-down.
export interface AdminUserAnalytics {
  wallets: Array<{
    kind: string;
    currency: string;
    balanceCents: string;
  }>;
  positions: Array<{
    positionId: string;
    marketId: string;
    marketSlug: string;
    marketStatus: string;
    outcomeId: string;
    outcomeName: { en: string; pt?: string } | string;
    shares: string;
    avgCostCents: string;
    currentPriceCents: number;
    unrealizedPnlCents: string;
    realizedPnlCents: string;
    totalFeesPaidCents: string;
    updatedAt: string;
  }>;
  fees: {
    lifetimePaidCents: string;
    perMonth: Array<{
      yearMonth: string;
      feeCents: string;
      tradeCount: number;
    }>;
  };
}

export interface AdminUserTradeRow {
  tradeId: string;
  occurredAt: string;
  direction: "buy" | "sell";
  marketId: string;
  marketSlug: string;
  marketTitle: { en: string; pt?: string } | string;
  outcomeId: string;
  outcomeName: { en: string; pt?: string } | string;
  sizeShares: string;
  priceCents: number;
  notionalCents: string;
  feeCents: string;
}

export interface AdminUserTradesPage {
  trades: AdminUserTradeRow[];
  nextCursor: string | null;
}

// EPIC-019 STORY-367 — user-facing trade history. Same row shape
// as the admin view but reached through the user's own scope at
// /v1/me/trades.
export type MyTradeRow = AdminUserTradeRow;

export interface MyTradesPage {
  trades: MyTradeRow[];
  nextCursor: string | null;
}

export interface MyMonthlyStatement {
  months: Array<{
    yearMonth: string;
    tradeCount: number;
    buyCount: number;
    sellCount: number;
    volumeCents: string;
    feesPaidCents: string;
  }>;
  lifetime: {
    tradeCount: number;
    volumeCents: string;
    feesPaidCents: string;
    firstTradeAt: string | null;
    lastTradeAt: string | null;
  };
}

export interface AdminUserOrderRow {
  orderId: string;
  createdAt: string;
  status: string;
  side: string;
  orderType: string;
  marketId: string;
  marketSlug: string;
  marketTitle: { en: string; pt?: string } | string;
  outcomeId: string;
  outcomeName: { en: string; pt?: string } | string;
  sizeShares: string;
  filledShares: string;
  priceCents: number | null;
  reservedCents: string;
  originTag: string | null;
  cancelledAt: string | null;
}

export interface AdminUserOrdersPage {
  orders: AdminUserOrderRow[];
  nextCursor: string | null;
}

// EPIC-005 STORY-501 — Popular Investor application shapes.
export type PiApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn";

export interface PiApplication {
  id: string;
  userId: string;
  status: PiApplicationStatus;
  motivation: string;
  trackRecord: string;
  externalLinks: string[];
  appliedAt: string;
  reviewedAt: string | null;
  reviewerAdminId: string | null;
  decisionNote: string | null;
  withdrawnAt: string | null;
}

export interface AdminPiApplicationRow extends PiApplication {
  applicantEmail: string;
  applicantUsername: string;
}

export interface AdminPiApplicationsPage {
  applications: AdminPiApplicationRow[];
  nextCursor: string | null;
}

// EPIC-006 STORY-601 + 602 — market comment shape.
// Slice 2A — verifiable position-at-comment-time. Binary markets only
// for v1; multi-outcome markets and authors who opted out leave this
// null on the row.
export interface CommentAuthorPosition {
  outcome: "yes" | "no";
  shares: number;
}

export interface CommentRow {
  id: string;
  marketId: string;
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  parentCommentId: string | null;
  body: string | null;
  isDeleted: boolean;
  isHiddenByAdmin: boolean;
  hiddenReason: string | null;
  score: number;
  replyCount: number;
  myVote: -1 | 0 | 1;
  // Slice 2A — null when the author had no position at create time,
  // opted out (privacy_prefs.showPositionOnComments=false or
  // per-comment postAnonymously=true), or the market is non-binary.
  authorPosition: CommentAuthorPosition | null;
  createdAt: string;
  updatedAt: string;
}

// EPIC-006 STORY-603 — follow edge + relationship shapes.
export interface FollowEdge {
  followerId: string;
  followeeId: string;
  followeeUsername: string;
  followeeDisplayName: string | null;
  followeeAvatarUrl: string | null;
  createdAt: string;
}

export interface UserRelationship {
  targetUserId: string;
  isFollowing: boolean;
  followerCount: number;
  followingCount: number;
}

// EPIC-006 STORY-604 — feed item shape.
export interface FeedItemRow {
  marketId: string;
  marketSlug: string;
  marketTitle: I18nText | string;
  marketStatus: string;
  marketKind: string;
  imageUrl: string | null;
  yesPriceCents: number | null;
  closesAt: string;
  byUserId: string;
  byUsername: string;
  byDisplayName: string | null;
  byAvatarUrl: string | null;
  bySide: "buy" | "sell";
  bySizeShares: string;
  byPriceCents: number;
  byTradedAt: string;
}

// EPIC-006 STORY-611 — win-share user preferences.
export interface SharePrefs {
  promptsEnabled: boolean;
  showUsername: boolean;
  showAmount: boolean;
  showMarketTitle: boolean;
}

// EPIC-021 STORY-378 — user privacy preferences.
export type ProfileVisibility = "public" | "followers" | "private";
export interface PrivacyPrefs {
  profileVisibility: ProfileVisibility;
  showVolume: boolean;
  showWinRate: boolean;
  showTradeHistory: boolean;
  followerOnlyMode: boolean;
  // Slice 2A — verifiable position badge on comments. Default true;
  // user can opt out globally here, OR per-comment via the
  // postAnonymously toggle on the composer.
  showPositionOnComments: boolean;
}

// EPIC-022 STORY-628 — subscription + tip records.
export type SubscriptionState =
  | "active"
  | "cancelled"
  | "expired"
  | "failed_renewal_grace";

export interface SubscriptionRecord {
  id: string;
  subscriberId: string;
  creatorId: string;
  creatorUsername: string;
  monthlyPriceCents: string;
  state: SubscriptionState;
  startedAt: string;
  currentPeriodStartedAt: string;
  currentPeriodExpiresAt: string;
  cancelledAt: string | null;
  lastRenewalAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TipRecord {
  id: string;
  tipperId: string;
  recipientId: string;
  recipientUsername: string;
  amountCents: string;
  note: string | null;
  ledgerTxId: string;
  sourcePostId: string | null;
  createdAt: string;
}

// EPIC-022 STORY-625 — admin moderation queue row.
export interface ModerationQueueItem {
  postId: string;
  authorUserId: string;
  authorUsername: string;
  title: string | null;
  bodyMd: string;
  flagCount: number;
  hiddenByAdminAt: string | null;
  createdAt: string;
  recentReasons: string[];
}

export interface CommentModerationQueueItem {
  commentId: string;
  marketSlug: string;
  authorUserId: string;
  authorUsername: string;
  body: string;
  flagCount: number;
  hiddenByAdminAt: string | null;
  createdAt: string;
  recentReasons: string[];
}

export interface TopicFlagQueueItem {
  kind: "discussion" | "reply";
  id: string;
  discussionId: string;
  topicSlug: string;
  title: string;
  body: string;
  authorUsername: string;
  flagCount: number;
  hiddenByAdminAt: string | null;
  recentReasons: string[];
}

// EPIC-022 STORY-622 — post visibility + record shape.
// EPIC-047 — composer visibility selector adds 'authed' (All Members)
// and 'private' (Only Me).
export type PostVisibility =
  | "public"
  | "authed"
  | "followers"
  | "subscribers"
  | "private";

// EPIC-022 STORY-623 — content format.
// EPIC-041 part 3b — `auto_trade` extends the existing user-content
// kinds with system-generated post-on-fill posts. Frontend renders
// auto-trade specially (no title, the body is a generated line, the
// EmbeddedMarketCard is the visual focal point).
export type PostKind =
  | "long_form"
  | "slides"
  | "short"
  | "auto_trade"
  | "news_bot";

export interface PostSlide {
  imageUrl: string;
  caption: string;
}

export interface PostAttachedMarket {
  id: string;
  slug: string;
  title: string;
  resolved: boolean;
  // For binary markets: current YES price (1-99). For multi-outcome:
  // null — top outcome surfaces via topOutcomeName +
  // topOutcomePriceCents instead.
  currentYesPriceCents: number | null;
  outcomeKind: "binary" | "multi";
  // EPIC-041 STORY-2105 — multi-outcome embed surface. Populated for
  // markets where outcomeKind="multi"; null on binary. The "top
  // outcome" is the one with the highest current_price_cents — the
  // market's leading bet. outcomeCount is the total count so the
  // card can render a "+N more" pill when count > 1.
  topOutcomeName: string | null;
  topOutcomePriceCents: number | null;
  outcomeCount: number;
  // EPIC-041 STORY-2106 — resolved-market embed surface. Populated
  // when resolved=true. sort_order=0 → YES (green badge), 1 → NO
  // (red badge), else neutral; multi-outcome winners use the
  // outcome name with neutral coloring.
  resolvedOutcomeName: string | null;
  resolvedOutcomeSortOrder: number | null;
  // EPIC-046 part 2 phase B — author's net position in the embedded
  // binary market (BIGINT shares, returned as a numeric string by the
  // API). Both null on multi-outcome markets and on the
  // unauthenticated public feed (the leaner query skips this LATERAL).
  authorYesShares: string | null;
  authorNoShares: string | null;
}

export interface PostRecord {
  id: string;
  authorUserId: string;
  authorUsername: string;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  authorVerifiedExpert: boolean;
  title: string | null;
  bodyMd: string;
  bodyHtml: string;
  visibility: PostVisibility;
  kind: PostKind;
  slides: PostSlide[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  hiddenByAdminAt: string | null;
  // EPIC-039 — true when the post is subscribers-only and the
  // viewer doesn't have an active subscription. bodyMd carries
  // the 12-word teaser; bodyHtml is empty; slides is []. Frontend
  // renders the blurred-preview Subscribe CTA in this case.
  paywalled?: boolean;
  // EPIC-041 part 1 — set when the post body contains a `$TICKER`
  // that resolved to a markets.slug. The frontend renders an
  // EmbeddedMarketCard below the prose. Resolved markets keep the
  // attachment but the card swaps to a "Resolved" badge.
  attachedMarket: PostAttachedMarket | null;
  // STORY-1681 follow-up — structured order detail on kind='auto_trade'
  // posts. NULL on user-authored posts AND on historical auto-trade
  // posts created before migration 0153 (the embed falls back to
  // parseAutoTradePostBody for those).
  attachedOrder: {
    side: "buy" | "sell";
    sharesFilled: string;
    outcomeName: string;
    fillPriceCents: number;
  } | null;
  // EPIC-041 photo addition — array of attached photo URLs. Empty
  // when the post has no photos. Always populated.
  photos: string[];
  // EPIC-046 part 2 phase C — Like reaction. `likedByViewer` is true
  // only when the request was authenticated AND the viewer holds a
  // post_likes row. Backend defaults both to 0/false when the query
  // didn't compute them (auto-trade create path, listPublicFeed).
  likeCount: number;
  likedByViewer: boolean;
  // 2026-05-04 phase 4 — comment count from post_comments LATERAL.
  // Defaults to 0 on queries that don't compute it.
  commentCount: number;
  // EPIC-046 polish — private per-viewer bookmark flag. NOT exposed
  // as a count; only the viewer's own state.
  bookmarkedByViewer: boolean;
  // EPIC-046 part 2 phase E — quoted post inline preview. NULL when
  // the post doesn't quote OR when the original was hard-deleted
  // (FK is ON DELETE SET NULL).
  quotedPost: QuotedPost | null;
  // EPIC-047 phase 1 — composer destination flag. When TRUE the
  // backend strips identity on the wire (authorUsername='anonymous',
  // authorDisplayName='Anonymous', authorAvatarUrl=null,
  // authorVerifiedExpert=false). The viewer sees no handle.
  authorAnonymous: boolean;
  // EPIC-047 phase 1 — explicit composer-chosen sentiment. Distinct
  // from the SentimentPill (EPIC-046 part 2 phase B) which derives
  // from the user's market position; the explicit value, when set,
  // takes precedence on render. NULL when unselected.
  sentiment: "bullish" | "bearish" | null;
  // 2026-05-04 — feed-ranking annotations populated only on the
  // /v1/me/feed/posts surface. `tier` is the FeedRankingService
  // classification (1-5; 1=best); `isSeen` is the per-viewer seen
  // flag from the Redis-backed seen-tracker. Optional so the
  // frontend can ignore them on call-sites that don't render
  // tiered badging. Backend defaults to tier=4, isSeen=false on
  // queries that don't compute them.
  tier?: 1 | 2 | 3 | 4 | 5;
  isSeen?: boolean;
  // 2026-05-04 — per-post hashtag list (lower-cased). Empty array
  // on auto-trade posts. Used for tag-based filtering / topic
  // navigation on the frontend.
  tags?: string[];
}

export interface QuotedPost {
  id: string;
  authorUsername: string;
  authorDisplayName: string | null;
  title: string | null;
  bodyExcerpt: string;
  photoCount: number;
  createdAt: string;
  /** True when the original is soft-deleted or admin-hidden. */
  unavailable: boolean;
}

// Twitter-style news-feed types (feat/news-feed-twitter-style-tier3).
//
// One source article extracted from the underlying news-hub cluster.
// Up to 3 per post, ranked by published_at DESC. Used by the
// NewsFeedCard footer to render attribution chips.
export interface NewsFeedSource {
  title: string;
  url: string;
  sourceName: string;
  publishedAt: string;
}

// Primary topic surfaced from topic_clusters (is_primary=true).
// May be null on the rare case the cluster has no primary topic yet
// (closed-beta backfill made this near-impossible, but the type
// stays nullable so the card render handles it gracefully).
export interface NewsFeedTopic {
  slug: string;
  name: string;
}

// One row from GET /v1/feed/news/posts. Distinct from PostRecord —
// this is a leaner shape with the cluster-source attribution baked
// in. PostRecord's social-feed fields (likeCount, comments,
// bookmarks, sentiment, etc.) aren't needed on the news-feed
// surface today; the card focuses on the news content + sources.
export interface NewsFeedPost {
  id: string;
  authorUserId: string;
  authorUsername: string;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  authorVerifiedExpert: boolean;
  title: string | null;
  bodyMd: string;
  bodyHtml: string;
  createdAt: string;
  sources: NewsFeedSource[];
  topic: NewsFeedTopic | null;
  clusterId: string;
}

// EPIC-021 STORY-381 + STORY-382 — general-user public profile shape.
// EPIC-037 part 2a — PI-conditional sections on the merged profile.
export interface PiApprovedApplication {
  id: string;
  motivation: string;
  trackRecord: string;
  externalLinks: string[];
  approvedAt: string | null;
  decisionNote: string | null;
}

export interface PiMetricsBlock {
  riskScore: number | null;
  riskScoreLimitedHistory: boolean;
  return30dPct: number | null;
  winRate30dPct: number | null;
  drawdownPct: number | null;
  aumCents: string | null;
  activeSubscriberCount: number;
}

// EPIC-046 Part 1 — feed-events activity union.
export type FeedEventType =
  | "comment.authored"
  | "comment.replied"
  | "post.authored"
  | "post.liked"
  | "discussion.authored"
  | "trade.executed"
  | "market.resolved"
  | "copy_subscription.created";

export interface FeedEventRow {
  id: string;
  authorUserId: string;
  // EPIC-046 part 1 — author display fields. NULL when the author was
  // soft-deleted between the event being recorded and the read; the
  // ActivityTimeline renders a "deleted user" placeholder in that case.
  authorUsername: string | null;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  eventType: FeedEventType;
  sourceTable: string | null;
  sourceId: string | null;
  payload: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
}

/**
 * 2026-05-04 redesign phase 2 — viewer social context per post.
 *
 * For each post in the feed, we tell the viewer "X (a person you
 * follow) liked / commented on this." Drives the IG-style highlight
 * badge above each post card. Server-side caps top 3 most-recent
 * actors per kind to keep payload light.
 */
export interface FeedSocialActor {
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}
export interface FeedSocialContext {
  likedBy: FeedSocialActor[];
  commentedBy: FeedSocialActor[];
}

/**
 * 2026-05-04 phase 3 — admin-curated news (platform-wide, distinct
 * from EPIC-045 per-topic news_articles).
 */
export interface NewsItemRow {
  id: string;
  title: string;
  bodyMd: string | null;
  sourceUrl: string | null;
  imageUrl: string | null;
  regionCode: "global" | "brazil" | "mexico" | "canada" | "india" | null;
  publishedAt: string;
  createdByAdminId: string;
  updatedByAdminId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// 2026-05-04 — news-bot aggregator (migration 0120).
export interface NewsBotSummary {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  sourceCount: number;
  totalPostsCreated: number;
}

// PR F (2026-05-06) — encrypted runtime secrets.
export interface PlatformSecret {
  key: string;
  maskedValue: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  updatedByUserId: string | null;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
}

export interface SecretTestResult {
  success: boolean;
  message: string;
  latencyMs: number;
}

export interface StepUpTokenResponse {
  stepUpToken: string;
  expiresAt: string;
}

export type StepUpScope = "reveal" | "rotate" | "create";

export interface NewsBotSource {
  id: string;
  botUserId: string;
  name: string;
  rssUrl: string;
  // 2026-05-04 (migration 0121) — optional topic association.
  topicSlug: string | null;
  enabled: boolean;
  // 'auto' = direct-post legacy flow; 'review_required' = News Hub
  // clustered admin review (migration 0127).
  publishingMode: "auto" | "review_required";
  lastFetchedAt: string | null;
  lastFetchError: string | null;
  lastItemCount: number;
  totalPostsCreated: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 2026-05-04 phase 3 — News tab on /feed/posts mixes market.resolved
 * events with admin-curated news items in one chronological timeline.
 * Discriminated union; the renderer branches on `kind`.
 */
export type NewsFeedEntry =
  | {
      kind: "event";
      id: string;
      occurredAt: string;
      data: FeedEventRow;
    }
  | {
      kind: "item";
      id: string;
      occurredAt: string;
      data: NewsItemRow;
    };

/** 2026-05-04 phase 3 — post comments primitive. */
// StockTwits redesign — author's current position in the post's
// attached binary market. Optional (only present when post has a
// binary market and the author holds shares).
export interface PostCommenterPosition {
  side: "long" | "short";
  shares: string;
}

export type PostCommentSort = "newest" | "top" | "active" | "trending";

export interface PostCommentRow {
  id: string;
  postId: string;
  authorUserId: string;
  authorUsername: string | null;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  authorIsPi: boolean;
  parentCommentId: string | null;
  bodyMd: string;
  replyCount: number;
  // StockTwits redesign — denormalized vote score; viewer's vote
  // direction (-1, 0, +1); commenter's position on the attached
  // market when applicable. Older deploys (pre-#1464 backend) omit
  // these fields — frontend treats undefined as 0/null.
  score?: number;
  myVote?: -1 | 0 | 1;
  commenterPosition?: PostCommenterPosition | null;
  // StockTwits-style explicit sentiment chip selected by the
  // commenter at create time. Optional so older deploys
  // (pre-#1475 backend) gracefully omit. Frontend renders a
  // chip next to the author name when present.
  sentiment?: "bullish" | "bearish" | null;
  hiddenByAdminAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicProfileResponse {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    bio: string | null;
    country: string | null;
    role: string;
    memberSince: string;
    piTier: PiTier | null;
    verifiedExpert: boolean;
    // EPIC-022 STORY-628 — creator subscription price; NULL when subs disabled.
    subscriptionPriceCents: string | null;
    // EPIC-037 part 1 — gates the Copy CTA. NULL = uncomputed
    // (fresh PI before the daily cron has run); riskScore >= 8
    // disables Copy per the founder hard rule.
    riskScore: number | null;
    riskScoreLimitedHistory: boolean;
  };
  visibility: ProfileVisibility;
  isOwnProfile: boolean;
  isFollowing: boolean;
  followerCount: number;
  followingCount: number;
  stats: {
    lifetimeTradeCount: number | null;
    lifetimeVolumeCents: string | null;
    winRatePercent: number | null;
  };
  recentTrades: Array<{
    marketSlug: string;
    marketTitleEn: string | null;
    side: "buy" | "sell";
    outcomeSortOrder: number;
    sizeShares: string;
    priceCents: number;
    tradedAt: string;
  }> | null;
  // EPIC-046 part 1a — recent topic discussions authored by the user.
  // Always populated (empty array when none); the UI renders an
  // "Activity" section consistently. Limited to 5 most recent.
  recentDiscussions: Array<{
    id: string;
    topicSlug: string;
    title: string;
    bodyExcerpt: string;
    replyCount: number;
    participantCount: number;
    createdAt: string;
  }>;
  // EPIC-046 part 1b — recent replies authored by the user on any
  // discussion. discussionTitle gives parent context for the card.
  recentDiscussionReplies: Array<{
    id: string;
    discussionId: string;
    discussionTitle: string;
    topicSlug: string;
    bodyExcerpt: string;
    createdAt: string;
  }>;
  // EPIC-046 part 1c — recent comments authored by the user on a
  // market. marketTitle gives parent context for the card.
  recentMarketComments: Array<{
    id: string;
    marketId: string;
    marketSlug: string;
    marketTitle: string;
    bodyExcerpt: string;
    createdAt: string;
  }>;
  // EPIC-037 part 2a — PI-conditional payload. Both null when the
  // profile owner is not PI-approved.
  piApplication: PiApprovedApplication | null;
  piMetrics: PiMetricsBlock | null;
}

export interface AccessGatedProfileResponse {
  accessGated: true;
  visibility: ProfileVisibility;
  user: { username: string };
}

export function isAccessGatedProfile(
  res: PublicProfileResponse | AccessGatedProfileResponse,
): res is AccessGatedProfileResponse {
  return (res as AccessGatedProfileResponse).accessGated === true;
}

// EPIC-006 STORY-606 — in-app notification shape.
export interface NotificationRow {
  id: string;
  userId: string;
  kind: string;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

// EPIC-006 STORY-605 — admin moderation row (CommentRow + market context).
export interface AdminCommentRow extends CommentRow {
  marketSlug: string;
  marketTitleEn: string | null;
}

// EPIC-008 STORY-808 — Terms of Service shapes.
export interface TosVersion {
  id: string;
  version: string;
  effectiveAt: string;
  summary: string;
  bodyMd: string;
  createdAt: string;
}

export interface TosAcceptanceStatus {
  current: TosVersion;
  acceptedThisVersion: boolean;
  acceptedAt: string | null;
  acceptedVersion: string | null;
}

// EPIC-021 STORY-376 — notification prefs.
export type NotificationCategory =
  | "trade_fill"
  | "market_resolution"
  | "withdrawal_status"
  | "social_interaction";

export interface NotificationChannelPrefs {
  email: boolean;
  push: boolean;
}

export type NotificationPrefs = Record<
  NotificationCategory,
  NotificationChannelPrefs
>;

export const NOTIFICATION_CATEGORY_LABELS: Record<
  NotificationCategory,
  { en: string; pt: string; description: { en: string; pt: string } }
> = {
  trade_fill: {
    en: "Trade fills",
    pt: "Operações executadas",
    description: {
      en: "When one of your orders fills (fully or partially).",
      pt: "Quando uma das suas ordens é executada (parcial ou total).",
    },
  },
  market_resolution: {
    en: "Market resolutions",
    pt: "Resolução de mercados",
    description: {
      en: "When a market you traded resolves and your position settles.",
      pt: "Quando um mercado que você operou é resolvido e sua posição é liquidada.",
    },
  },
  withdrawal_status: {
    en: "Withdrawal status",
    pt: "Status de saques",
    description: {
      en: "Status updates on your withdrawal requests (Gate B feature).",
      pt: "Atualizações sobre seus pedidos de saque (recurso Gate B).",
    },
  },
  social_interaction: {
    en: "Social interactions",
    pt: "Interações sociais",
    description: {
      en: "When someone follows you, comments on a market, or replies.",
      pt: "Quando alguém te segue, comenta em um mercado ou responde.",
    },
  },
};

// EPIC-005 STORY-503 — copy-trader subscription shapes.
export interface CopySubscriptionFilters {
  minPriceCents?: number;
  maxPriceCents?: number;
  outcomeKinds?: Array<"yes" | "no">;
}

export interface CopySubscription {
  id: string;
  subscriberId: string;
  piUserId: string;
  piUsername: string;
  allocationCents: string;
  committedCents: string;
  filters: CopySubscriptionFilters;
  paused: boolean;
  unsubscribedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // EPIC-COPY STORY-510 — stop-loss configuration. NULL on
  // stopLossPct means "use platform_config default"
  // (copy.stop_loss_default_pct, seed value 50). entryEquityCents
  // is the watcher's anchor; NULL until the first scan after
  // subscribe. stopLossTriggeredAt distinguishes a stop-loss
  // exit from a user-driven cancel.
  stopLossPct: number | null;
  entryEquityCents: string | null;
  stopLossTriggeredAt: string | null;
}

// STORY-503 visibility — orders the worker placed for this subscription.
export interface CopySubscriptionTrade {
  orderId: string;
  marketId: string;
  marketSlug: string;
  marketTitle: I18nText | null;
  outcomeId: string;
  side: "buy" | "sell";
  sizeShares: string;
  filledShares: string;
  avgFillPriceCents: number | null;
  status: string;
  parentTradeId: string | null;
  createdAt: string;
}

export interface CopySubscriptionOpenPosition {
  outcomeId: string;
  outcomeName: I18nText;
  marketId: string;
  marketSlug: string;
  marketTitle: I18nText;
  sharesHeld: string;
  basisCents: string;
  avgBasisPriceCents: number;
  currentPriceCents: number;
  currentValueCents: string;
  unrealizedPnlCents: string;
  unrealizedPnlPct: number | null;
}

export interface CopySubscriptionClosedPosition {
  outcomeId: string;
  outcomeName: I18nText;
  marketId: string;
  marketSlug: string;
  marketTitle: I18nText;
  closeKind: "sold" | "resolved";
  sharesClosed: string;
  basisCents: string;
  proceedsCents: string;
  realizedPnlCents: string;
  realizedPnlPct: number | null;
  closedAt: string | null;
}

export interface CopySubscriptionStats {
  tradeCount: number;
  filledCount: number;
  openCount: number;
  totalNotionalCents: string;
  avgFillPriceCents: number | null;
  // STORY-503 phase 2 — per-sub P&L for the "am I making money on
  // this PI" surface. All cents fields are stringified for bigint
  // safety; pnlPct is a number (1 decimal place) or null when
  // no actual fills happened.
  realizedPnlCents: string;
  unrealizedPnlCents: string;
  totalPnlCents: string;
  pnlPct: number | null;
  investedCents: string;
  wastedAllocationCents: string;
}

// EPIC-005 STORY-506 — PI tier (bronze/silver/gold/diamond), null
// when the nightly cron hasn't run yet for a freshly approved PI.
export type PiTier = "bronze" | "silver" | "gold" | "diamond";

// EPIC-005 STORY-509 — PI leaderboard row.
//
// PI-PHASE-2 (founder spec 2026-05-02) extends the row with snapshot
// fields populated by the daily/weekly cron. Every snapshot field is
// nullable: pre-cron the leaderboard still renders correctly with
// the fields hidden / "—" placeholders. Backend wires the JOIN at
// migration 0074 + service edit; cron services land in the next PR.
export interface PiLeaderboardRow {
  userId: string;
  username: string;
  country: string | null;
  piTier: PiTier | null;
  memberSince: string;
  lifetimeTradeCount: number;
  lifetimeVolumeCents: string;
  activeSubscriberCount: number;
  /** 1-10 drawdown bin; null until daily cron has run. */
  riskScore: number | null;
  /** True if PI has <90d data; renders "limited history" affordance. */
  riskScoreLimitedHistory: boolean;
  aumCents: string | null;
  /** Rolling 30d return as decimal percentage string (e.g. "12.40"). */
  return30dPct: string | null;
  winRate30dPct: string | null;
  /** Manual category (admin-set) takes precedence over auto. */
  primaryCategory: string | null;
  secondaryCategory: string | null;
  /** 30 daily NAV cents; empty array until first daily cron run. */
  sparkline30dCents: string[];
  /** 0–359 HSL hue derived from sha256(username); null pre-cron. */
  avatarHue: number | null;
  /** Reserved for post-Phase-1 custom uploads; null today. */
  avatarUrl: string | null;
}

export interface PiLeaderboardResponse {
  rows: PiLeaderboardRow[];
}

// EPIC-005 STORY-502 — public PI profile shape.
export interface PiPublicProfile {
  user: {
    id: string;
    username: string;
    country: string | null;
    role: string;
    memberSince: string;
    piTier: PiTier | null;
  };
  approvedApplication: {
    id: string;
    motivation: string;
    trackRecord: string;
    externalLinks: string[];
    approvedAt: string;
    decisionNote: string | null;
  };
  stats: {
    lifetimeTradeCount: number;
    lifetimeVolumeCents: string;
    lifetimeFeesPaidCents: string;
    firstTradeAt: string | null;
    lastTradeAt: string | null;
    activeSubscriberCount: number;
  };
}

export interface BookmarkRow {
  marketId: string;
  slug: string;
  title: { en: string; pt?: string };
  imageUrl: string | null;
  status: string;
  kind: string;
  categorySlug: string;
  closesAt: string;
  yesPriceCents: number | null;
  bookmarkedAt: string;
}

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: Record<string, unknown>;
}

export interface AuditEventsPage {
  events: AuditEvent[];
  nextCursor: string | null;
}

export interface ValidationMetrics {
  generatedAt: string;
  totals: {
    users: number;
    trades: number;
    tradersAllTime: number;
  };
  activity: {
    dau: number;
    wau: number;
    mau: number;
    tradesLast24h: number;
    tradesLast7d: number;
    tradesLast28d: number;
    signupsLast7d: number;
    signupsLast28d: number;
  };
  validationGate: {
    usersActive4PlusWeeks: number;
    target: number;
    weeksOfHistory: number;
    tradesPerActiveTrader28d: number;
  };
  cohorts: Array<{
    signupWeek: string;
    cohortSize: number;
    retainedByWeek: number[];
  }>;
}

export interface TestBotStatus {
  active: boolean;
  session: {
    sessionId: string;
    startedAt: string;
    expiresAt: string;
    label: string;
  } | null;
}

export interface TestBotRuntimeState {
  enabled: boolean;
  lastEnabledAt: string | null;
  lastEnabledBy: string | null;
  lastDisabledAt: string | null;
  lastDisabledBy: string | null;
  updatedAt: string;
  envEnabled?: boolean; // present on the read endpoint, omitted on write responses
  productionLocked?: boolean;
}

// EPIC-017 STORY-385 — scenario library names mirror
// apps/api/src/test-bot/test-bot.scenarios.ts. Keep in sync.
export type TestBotScenarioName =
  | "partial_fill"
  | "auto_convert"
  | "cancel_flow"
  | "multi_outcome"
  | "resolution_payout";

export type ResolutionProposalStatus =
  | "pending"
  | "tiebreaker"
  | "approved"
  | "rejected";

export interface ResolutionProposalListItem {
  id: string;
  marketId: string;
  marketSlug: string;
  proposerId: string;
  proposedOutcome: string;
  status: ResolutionProposalStatus;
  createdAt: string;
  finalizedAt: string | null;
  approveCount: number;
  rejectCount: number;
}

export interface ResolutionProposalDetail {
  id: string;
  marketId: string;
  proposerId: string;
  proposedOutcome: string;
  reason: string;
  sourceUrl: string | null;
  status: ResolutionProposalStatus;
  createdAt: string;
  finalizedAt: string | null;
  votes: Array<{
    voterId: string;
    vote: "approve" | "reject";
    reason: string | null;
    createdAt: string;
  }>;
}

// ───────── STORY-309c PR-K — multi-resolver scaffold types ─────────

export interface ResolutionMultiSnapshot {
  proposalId: string;
  marketId: string;
  proposerId: string;
  proposedOutcome: string;
  status: ResolutionProposalStatus;
  requiredApprovals: number;
  approvalCount: number;
  rejectionCount: number;
  tiebreakerRequired: boolean;
  tiebreakerResolvedAt: string | null;
  finalizedAt: string | null;
  createdAt: string;
}

export interface ResolutionMultiDetail extends ResolutionMultiSnapshot {
  reason: string;
  sourceUrl: string | null;
  votes: Array<{
    voterUserId: string;
    vote: "approve" | "reject" | "abstain";
    altOutcome: string | null;
    reasoning: string;
    isTiebreaker: boolean;
    createdAt: string;
  }>;
}

export interface ResolutionMultiPendingSummary {
  proposalId: string;
  marketId: string;
  marketSlug: string;
  marketTitle: string;
  proposerId: string;
  proposedOutcome: string;
  status: ResolutionProposalStatus;
  requiredApprovals: number;
  approvalCount: number;
  rejectionCount: number;
  tiebreakerRequired: boolean;
  createdAt: string;
  currentUserHasVoted: boolean;
}

export interface InviteCodeRecord {
  code: string;
  createdAt: string;
  expiresAt: string | null;
  usedBy: string | null;
  usedByUsername: string | null;
  usedAt: string | null;
  label: string | null;
}

export interface CreateMarketInput {
  slug: string;
  category: string;
  kind?: "binary" | "multi_outcome";
  title: Record<string, string>;
  description: Record<string, string>;
  imageUrl?: string;
  resolutionSource: string;
  resolutionSpec: Record<string, unknown>;
  opensAt: string;
  closesAt: string;
  resolvesAt: string;
  tags?: string[];
  yesName?: Record<string, string>;
  noName?: Record<string, string>;
  outcomes?: Array<Record<string, string>>;
}

export interface SessionRecord {
  id: string;
  ip: string | null;
  userAgent: string | null;
  deviceLabel: string | null;
  // STORY-111c — ISO 3166-1 alpha-2 country code at session creation
  // (cf-ipcountry header). NULL on pre-111c rows or when CF tunnel
  // bypassed. Frontend renders flag + code when set; omits otherwise.
  country: string | null;
  expiresAt: string;
  lastUsedAt: string;
  createdAt: string;
  current?: boolean; // STORY-111b — set true on the row matching the JWT's sid
}

// EPIC-044 part 4 — topic discussion (forum post).
export interface TopicDiscussionRecord {
  id: string;
  topicId: string;
  authorUserId: string;
  authorUsername: string;
  authorDisplayName: string | null;
  authorAvatarUrl: string | null;
  title: string;
  body: string;
  replyCount: number;
  participantCount: number;
  lastReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TopicDiscussionReplyRecord {
  id: string;
  discussionId: string;
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  parentReplyId: string | null;
  body: string | null;
  isDeleted: boolean;
  isHiddenByAdmin: boolean;
  score: number;
  replyCount: number;
  createdAt: string;
  updatedAt: string;
}

// EPIC-045 — per-topic news article.
export type NewsArticleProvider = "curator" | "rss" | "user_submitted";

export interface NewsArticleRecord {
  id: string;
  topicId: string;
  topicSecondaryId: string | null;
  provider: NewsArticleProvider;
  url: string;
  title: string;
  sourceName: string;
  thumbnailUrl: string | null;
  pinned: boolean;
  publishedAt: string | null;
  createdAt: string;
}

// EPIC-044 — topic-pages subject hubs.
export interface TopicRecord {
  id: string;
  slug: string;
  name: string;
  description: string;
  chartSymbol: string | null;
  chartProvider: "coingecko" | "yahoo" | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  // EPIC-044 follow-up — denormalized social-proof counts.
  followerCount: number;
  marketCount: number;
}

export interface TopicMarketCard {
  marketId: string;
  marketSlug: string;
  title: string;
  yesPriceCents: number | null;
  totalVolumeCents: string;
  resolved: boolean;
  closesAt: string;
  outcomeKind: "binary" | "multi";
}

// PR U.1a / W.1 — namespace-scoped tag attached to a topic.
export interface TopicTagRecord {
  id: string;
  topicId: string;
  slug: string;
  name: string;
  description: string;
  status: "active" | "inactive" | "archived";
  sortOrder: number;
}

// EPIC-047 — row shape for /v1/topics/:slug/my-positions. The composer
// uses these to render the "Attach a position" picker grouped by
// market with the caller's net YES/NO shares pre-joined.
export interface TopicMarketWithUserPosition {
  marketId: string;
  marketSlug: string;
  title: string;
  yesPriceCents: number | null;
  outcomeKind: "binary" | "multi";
  userYesShares: string; // BIGINT cents → string
  userNoShares: string;
  // Bullish when YES shares > NO shares (binary markets); null on
  // multi-outcome or zero positions.
  sentimentHint: "bullish" | "bearish" | null;
}

// EPIC-042 — admin platform config row.
export type AdminConfigDataType = "number" | "string" | "boolean" | "array";
export type AdminConfigCategory =
  | "feed"
  | "comments_discussions"
  | "trade_copy"
  | "subscriptions"
  | "news"
  | "pi_program"
  | "admin_inbox";

export interface AdminConfigRow {
  key: string;
  value: unknown;
  dataType: AdminConfigDataType;
  minValue: unknown;
  maxValue: unknown;
  description: string;
  category: AdminConfigCategory;
  defaultValue: unknown;
  lastModifiedBy: string | null;
  lastModifiedAt: string;
}

// PR-D2 (Market Creation Hub, 2026-05-13) — image-suggester response
// shape. Photo-based images for prediction markets across free-tier
// sources only (no AI generation, no paid APIs). Source attribution
// + license are surfaced for admin attribution display + compliance.
export type ImageCandidateSource =
  | "logo_dev"
  | "wikipedia"
  | "coingecko"
  | "pexels"
  | "openverse"
  | "duckduckgo";

export interface ImageCandidate {
  url: string;
  thumbnailUrl: string;
  source: ImageCandidateSource;
  license?: string;
  attribution?: string;
  confidence: number;
}

const TOKEN_KEY = "granbay.tokens";

// localStorage is XSS-exposed; acceptable pre-beta but should move to httpOnly
// cookies before production launch (tracked under EPIC-001 follow-ups).
export function saveTokens(tokens: AuthTokens): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function loadTokens(): AuthTokens | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_KEY);
}
